// src/account.ts
import {
  WalletAccount
} from "starknet";

// src/types.ts
var ResponseCodes = /* @__PURE__ */ ((ResponseCodes2) => {
  ResponseCodes2["SUCCESS"] = "SUCCESS";
  ResponseCodes2["NOT_CONNECTED"] = "NOT_CONNECTED";
  ResponseCodes2["ERROR"] = "ERROR";
  ResponseCodes2["CANCELED"] = "CANCELED";
  ResponseCodes2["USER_INTERACTION_REQUIRED"] = "USER_INTERACTION_REQUIRED";
  return ResponseCodes2;
})(ResponseCodes || {});

// src/utils.ts
import {
  addAddressPadding,
  CallData,
  getChecksumAddress,
  hash,
  typedData,
  TypedDataRevision
} from "starknet";
var ALLOWED_PROPERTIES = /* @__PURE__ */ new Set([
  "contracts",
  "messages",
  "target",
  "method",
  "name",
  "description",
  "types",
  "domain",
  "primaryType"
]);
function validatePropertyName(prop) {
  if (!ALLOWED_PROPERTIES.has(prop)) {
    throw new Error(`Invalid property name: ${prop}`);
  }
}
function safeObjectAccess(obj, prop) {
  validatePropertyName(prop);
  return obj[prop];
}
function toSessionPolicies(policies) {
  return Array.isArray(policies) ? policies.reduce(
    (prev, p) => {
      if (safeObjectAccess(p, "target")) {
        const target = getChecksumAddress(
          safeObjectAccess(p, "target")
        );
        const entrypoint = safeObjectAccess(p, "method");
        const contracts = safeObjectAccess(
          prev,
          "contracts"
        );
        const item = {
          name: humanizeString(entrypoint),
          entrypoint,
          description: safeObjectAccess(p, "description")
        };
        if (target in contracts) {
          const methods = toArray(contracts[target].methods);
          contracts[target] = {
            methods: [...methods, item]
          };
        } else {
          contracts[target] = {
            methods: [item]
          };
        }
      } else {
        const messages = safeObjectAccess(prev, "messages");
        messages.push(p);
      }
      return prev;
    },
    { contracts: {}, messages: [] }
  ) : policies;
}
function toWasmPolicies(policies) {
  return [
    ...Object.entries(policies.contracts ?? {}).flatMap(
      ([target, { methods }]) => toArray(methods).map((m) => ({
        target,
        method: m.entrypoint
      }))
    ),
    ...(policies.messages ?? []).map((p) => {
      const domainHash = typedData.getStructHash(
        p.types,
        "StarknetDomain",
        p.domain,
        TypedDataRevision.ACTIVE
      );
      const typeHash = typedData.getTypeHash(
        p.types,
        p.primaryType,
        TypedDataRevision.ACTIVE
      );
      return {
        scope_hash: hash.computePoseidonHash(domainHash, typeHash)
      };
    })
  ];
}
function toArray(val) {
  return Array.isArray(val) ? val : [val];
}
function humanizeString(str) {
  return str.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

// src/account.ts
var ControllerAccount = class extends WalletAccount {
  constructor(provider, address, keychain, options, modal) {
    super({ nodeUrl: provider.rpc.toString() }, provider);
    this.address = address;
    this.keychain = keychain;
    this.options = options;
    this.modal = modal;
  }
  /**
   * Invoke execute function in account contract
   *
   * @param calls the invocation object or an array of them, containing:
   * - contractAddress - the address of the contract
   * - entrypoint - the entrypoint of the contract
   * - calldata - (defaults to []) the calldata
   * - signature - (defaults to []) the signature
   * @param abis (optional) the abi of the contract for better displaying
   *
   * @returns response from addTransaction
   */
  async execute(calls) {
    calls = toArray(calls);
    return new Promise(async (resolve, reject) => {
      const sessionExecute = await this.keychain.execute(
        calls,
        void 0,
        void 0,
        false
      );
      if (sessionExecute.code === "SUCCESS" /* SUCCESS */) {
        resolve(sessionExecute);
        return;
      }
      if (this.options?.propagateSessionErrors) {
        reject(sessionExecute.error);
        return;
      }
      this.modal.open();
      const manualExecute = await this.keychain.execute(
        calls,
        void 0,
        void 0,
        true,
        sessionExecute.error
      );
      if (manualExecute.code === "SUCCESS" /* SUCCESS */) {
        resolve(manualExecute);
        this.modal.close();
        return;
      }
      reject(manualExecute.error);
      return;
    });
  }
  /**
   * Sign an JSON object for off-chain usage with the starknet private key and return the signature
   * This adds a message prefix so it cant be interchanged with transactions
   *
   * @param json - JSON object to be signed
   * @returns the signature of the JSON object
   * @throws {Error} if the JSON object is not a valid JSON
   */
  async signMessage(typedData2) {
    return new Promise(async (resolve, reject) => {
      const sessionSign = await this.keychain.signMessage(typedData2, "", true);
      if (!("code" in sessionSign)) {
        resolve(sessionSign);
        return;
      }
      this.modal.open();
      const manualSign = await this.keychain.signMessage(typedData2, "", false);
      if (!("code" in manualSign)) {
        resolve(manualSign);
      } else {
        reject(manualSign.error);
      }
      this.modal.close();
    });
  }
};
var account_default = ControllerAccount;

// src/iframe/base.ts
import { connectToChild } from "@cartridge/penpal";
var IFrame = class {
  constructor({
    id,
    url,
    preset,
    theme,
    colorMode,
    onClose,
    onConnect,
    methods = {}
  }) {
    if (typeof document === "undefined") {
      return;
    }
    if (theme) {
      url.searchParams.set("theme", theme);
    }
    if (preset) {
      url.searchParams.set("preset", preset);
    }
    if (colorMode) {
      url.searchParams.set("colorMode", colorMode);
    }
    this.url = url;
    const iframe = document.createElement("iframe");
    iframe.src = url.toString();
    iframe.id = id;
    iframe.style.border = "none";
    iframe.sandbox.add("allow-forms");
    iframe.sandbox.add("allow-popups");
    iframe.sandbox.add("allow-scripts");
    iframe.sandbox.add("allow-same-origin");
    iframe.allow = "publickey-credentials-create *; publickey-credentials-get *; clipboard-write";
    if (!!document.hasStorageAccess) {
      iframe.sandbox.add("allow-storage-access-by-user-activation");
    }
    const container = document.createElement("div");
    container.id = "controller";
    container.style.position = "fixed";
    container.style.height = "100%";
    container.style.width = "100%";
    container.style.top = "0";
    container.style.left = "0";
    container.style.zIndex = "10000";
    container.style.backgroundColor = "rgba(0,0,0,0.6)";
    container.style.display = "flex";
    container.style.alignItems = "center";
    container.style.justifyContent = "center";
    container.style.visibility = "hidden";
    container.style.opacity = "0";
    container.style.transition = "opacity 0.2s ease";
    container.appendChild(iframe);
    this.iframe = iframe;
    this.container = container;
    connectToChild({
      iframe: this.iframe,
      methods: { close: () => this.close(), ...methods }
    }).promise.then(onConnect);
    this.resize();
    window.addEventListener("resize", () => this.resize());
    const observer = new MutationObserver(() => {
      const existingController2 = document.getElementById("controller");
      if (document.body) {
        if (id === "controller-keychain" && !existingController2 || id === "controller-profile") {
          document.body.appendChild(container);
          observer.disconnect();
        }
      }
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
    const existingController = document.getElementById("controller");
    if (document.body) {
      if (id === "controller-keychain" && !existingController || id === "controller-profile") {
        document.body.appendChild(container);
      }
    }
    this.onClose = onClose;
  }
  open() {
    if (!this.container) return;
    document.body.style.overflow = "hidden";
    this.container.style.visibility = "visible";
    this.container.style.opacity = "1";
  }
  close() {
    if (!this.container) return;
    this.onClose?.();
    document.body.style.overflow = "auto";
    this.container.style.visibility = "hidden";
    this.container.style.opacity = "0";
  }
  sendBackward() {
    if (!this.container) return;
    this.container.style.zIndex = "9999";
  }
  sendForward() {
    if (!this.container) return;
    this.container.style.zIndex = "10000";
  }
  resize() {
    if (!this.iframe) return;
    this.iframe.style.userSelect = "none";
    if (window.innerWidth < 768) {
      this.iframe.style.height = "100%";
      this.iframe.style.width = "100%";
      this.iframe.style.borderRadius = "0";
      return;
    }
    this.iframe.style.height = "600px";
    this.iframe.style.width = "432px";
    this.iframe.style.borderRadius = "8px";
  }
};

// src/constants.ts
var KEYCHAIN_URL = "https://x.cartridge.gg";
var PROFILE_URL = "https://profile.cartridge.gg";
var API_URL = "https://api.cartridge.gg";

// src/iframe/keychain.ts
var KeychainIFrame = class extends IFrame {
  constructor({ url, policies, ...iframeOptions }) {
    const _url = new URL(url ?? KEYCHAIN_URL);
    if (policies) {
      _url.searchParams.set(
        "policies",
        encodeURIComponent(JSON.stringify(policies))
      );
    }
    super({
      ...iframeOptions,
      id: "controller-keychain",
      url: _url
    });
  }
};

// src/iframe/profile.ts
var ProfileIFrame = class extends IFrame {
  constructor({
    profileUrl,
    rpcUrl,
    version,
    username,
    slot,
    namespace,
    tokens,
    ...iframeOptions
  }) {
    const _profileUrl = (profileUrl || PROFILE_URL).replace(/\/$/, "");
    let _url = new URL(
      slot ? namespace ? `${_profileUrl}/account/${username}/slot/${slot}?ps=${encodeURIComponent(
        slot
      )}&ns=${encodeURIComponent(namespace)}` : `${_profileUrl}/account/${username}/slot/${slot}?ps=${encodeURIComponent(
        slot
      )}` : `${_profileUrl}/account/${username}`
    );
    if (version) {
      _url.searchParams.set("v", encodeURIComponent(version));
    }
    _url.searchParams.set("rpcUrl", encodeURIComponent(rpcUrl));
    if (tokens?.erc20) {
      _url.searchParams.set(
        "erc20",
        encodeURIComponent(tokens.erc20.toString())
      );
    }
    super({
      ...iframeOptions,
      id: "controller-profile",
      url: _url
    });
  }
};

// src/errors.ts
var NotReadyToConnect = class _NotReadyToConnect extends Error {
  constructor() {
    super("Not ready to connect");
    Object.setPrototypeOf(this, _NotReadyToConnect.prototype);
  }
};

// src/provider.ts
import {
  Permission
} from "@starknet-io/types-js";

// src/icon.ts
var icon = "data:image/png;base64,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";

// src/provider.ts
var BaseProvider = class {
  constructor(options) {
    this.id = "controller";
    this.name = "Controller";
    this.version = "0.4.0";
    this.icon = icon;
    this.subscriptions = [];
    this.request = async (call) => {
      switch (call.type) {
        case "wallet_getPermissions":
          await this.probe();
          if (this.account) {
            return [Permission.ACCOUNTS];
          }
          return [];
        case "wallet_requestAccounts": {
          if (this.account) {
            return [this.account.address];
          }
          this.account = await this.probe();
          if (!this.account) {
            this.account = await this.connect();
          }
          if (this.account) {
            return [this.account.address];
          }
          return [];
        }
        case "wallet_watchAsset":
          throw {
            code: 63,
            message: "An unexpected error occurred",
            data: "wallet_watchAsset not implemented"
          };
        case "wallet_addStarknetChain":
          throw {
            code: 63,
            message: "An unexpected error occurred",
            data: "wallet_addStarknetChain not implemented"
          };
        case "wallet_switchStarknetChain":
          throw {
            code: 63,
            message: "An unexpected error occurred",
            data: "wallet_switchStarknetChain not implemented"
          };
        case "wallet_requestChainId":
          if (!this.account) {
            throw {
              code: 63,
              message: "An unexpected error occurred",
              data: "wallet_deploymentData not implemented"
            };
          }
          return await this.account.getChainId();
        case "wallet_deploymentData":
          throw {
            code: 63,
            message: "An unexpected error occurred",
            data: "wallet_deploymentData not implemented"
          };
        case "wallet_addInvokeTransaction":
          if (!this.account) {
            throw {
              code: 63,
              message: "An unexpected error occurred",
              data: "wallet_deploymentData not implemented"
            };
          }
          let params = call.params;
          return await this.account.execute(
            params.calls.map((call2) => ({
              contractAddress: call2.contract_address,
              entrypoint: call2.entry_point,
              calldata: call2.calldata
            }))
          );
        case "wallet_addDeclareTransaction":
          throw {
            code: 63,
            message: "An unexpected error occurred",
            data: "wallet_addDeclareTransaction not implemented"
          };
        case "wallet_signTypedData": {
          if (!this.account) {
            throw {
              code: 63,
              message: "An unexpected error occurred",
              data: "Account not initialized"
            };
          }
          return await this.account.signMessage(call.params);
        }
        case "wallet_supportedSpecs":
          return [];
        case "wallet_supportedWalletApi":
          return [];
        default:
          throw {
            code: 63,
            message: "An unexpected error occurred",
            data: `Unknown RPC call type: ${call.type}`
          };
      }
    };
    this.on = (event, handler) => {
      if (event !== "accountsChanged" && event !== "networkChanged") {
        throw new Error(`Unknown event: ${event}`);
      }
      this.subscriptions.push({ type: event, handler });
    };
    this.off = (event, handler) => {
      if (event !== "accountsChanged" && event !== "networkChanged") {
        throw new Error(`Unknown event: ${event}`);
      }
      const idx = this.subscriptions.findIndex(
        (sub) => sub.type === event && sub.handler === handler
      );
      if (idx >= 0) {
        this.subscriptions.splice(idx, 1);
      }
    };
    const { rpc } = options;
    this.rpc = new URL(rpc);
  }
};

// src/controller.ts
var ControllerProvider = class extends BaseProvider {
  constructor(options) {
    const { rpc } = options;
    super({ rpc });
    this.iframes = {
      keychain: new KeychainIFrame({
        ...options,
        onClose: this.keychain?.reset,
        onConnect: (keychain) => {
          this.keychain = keychain;
        }
      })
    };
    this.options = options;
    if (typeof window !== "undefined") {
      window.starknet_controller = this;
    }
  }
  async probe() {
    try {
      await this.waitForKeychain();
      if (!this.keychain) {
        console.error(new NotReadyToConnect().message);
        return;
      }
      const response = await this.keychain.probe(
        this.rpc.toString()
      );
      this.account = new account_default(
        this,
        response.address,
        this.keychain,
        this.options,
        this.iframes.keychain
      );
    } catch (e) {
      console.error(e);
      return;
    }
    if (!this.iframes.profile) {
      const username = await this.keychain.username();
      this.iframes.profile = new ProfileIFrame({
        ...this.options,
        onConnect: (profile) => {
          this.profile = profile;
        },
        methods: {
          openSettings: () => this.openSettings.bind(this),
          openPurchaseCredits: () => this.openPurchaseCredits.bind(this),
          openExecute: () => this.openExecute.bind(this)
        },
        rpcUrl: this.rpc.toString(),
        username,
        version: this.version
      });
    }
    return this.account;
  }
  async connect() {
    if (this.account) {
      return this.account;
    }
    if (!this.keychain || !this.iframes.keychain) {
      console.error(new NotReadyToConnect().message);
      return;
    }
    if (!!document.hasStorageAccess) {
      const ok = await document.hasStorageAccess();
      if (!ok) {
        await document.requestStorageAccess();
      }
    }
    this.iframes.keychain.open();
    try {
      let response = await this.keychain.connect(
        this.options.policies || [],
        this.rpc.toString()
      );
      if (response.code !== "SUCCESS" /* SUCCESS */) {
        throw new Error(response.message);
      }
      response = response;
      this.account = new account_default(
        this,
        response.address,
        this.keychain,
        this.options,
        this.iframes.keychain
      );
      return this.account;
    } catch (e) {
      console.log(e);
    } finally {
      this.iframes.keychain.close();
    }
  }
  async disconnect() {
    if (!this.keychain) {
      console.error(new NotReadyToConnect().message);
      return;
    }
    if (!!document.hasStorageAccess) {
      const ok = await document.hasStorageAccess();
      if (!ok) {
        await document.requestStorageAccess();
      }
    }
    this.account = void 0;
    return this.keychain.disconnect();
  }
  async openProfile(tab = "inventory") {
    if (!this.profile || !this.iframes.profile?.url) {
      console.error("Profile is not ready");
      return;
    }
    if (!this.account) {
      console.error("Account is not ready");
      return;
    }
    this.profile.navigate(`${this.iframes.profile.url?.pathname}/${tab}`);
    this.iframes.profile.open();
  }
  async openSettings() {
    if (!this.keychain || !this.iframes.keychain) {
      console.error(new NotReadyToConnect().message);
      return null;
    }
    if (this.iframes.profile?.sendBackward) {
      this.iframes.profile?.sendBackward();
    } else {
      this.iframes.profile?.close();
    }
    this.iframes.keychain.open();
    const res = await this.keychain.openSettings();
    this.iframes.keychain.close();
    this.iframes.profile?.sendForward?.();
    if (res && res.code === "NOT_CONNECTED" /* NOT_CONNECTED */) {
      return false;
    }
    return true;
  }
  revoke(origin, _policy) {
    if (!this.keychain) {
      console.error(new NotReadyToConnect().message);
      return null;
    }
    return this.keychain.revoke(origin);
  }
  username() {
    if (!this.keychain) {
      console.error(new NotReadyToConnect().message);
      return;
    }
    return this.keychain.username();
  }
  fetchControllers(contractAddresses) {
    if (!this.keychain) {
      throw new NotReadyToConnect().message;
    }
    return this.keychain.fetchControllers(contractAddresses);
  }
  openPurchaseCredits() {
    if (!this.keychain || !this.iframes.keychain) {
      console.error(new NotReadyToConnect().message);
      return;
    }
    if (!this.iframes.profile) {
      console.error("Profile is not ready");
      return;
    }
    this.iframes.profile.close();
    this.iframes.keychain.open();
    this.keychain.openPurchaseCredits();
  }
  async openExecute(calls) {
    if (!this.keychain || !this.iframes.keychain) {
      console.error(new NotReadyToConnect().message);
      return;
    }
    if (!this.iframes.profile) {
      console.error("Profile is not ready");
      return;
    }
    if (this.iframes.profile?.sendBackward) {
      this.iframes.profile?.sendBackward();
    } else {
      this.iframes.profile?.close();
    }
    this.iframes.keychain.open();
    const res = await this.keychain.execute(calls, void 0, void 0, true);
    this.iframes.keychain.close();
    this.iframes.profile?.sendForward?.();
    return !(res && res.code === "NOT_CONNECTED" /* NOT_CONNECTED */);
  }
  async delegateAccount() {
    if (!this.keychain) {
      console.error(new NotReadyToConnect().message);
      return null;
    }
    return await this.keychain.delegateAccount();
  }
  waitForKeychain({
    timeout = 5e3,
    interval = 100
  } = {}) {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const id = setInterval(() => {
        if (Date.now() - startTime > timeout) {
          clearInterval(id);
          reject(new Error("Timeout waiting for keychain"));
          return;
        }
        if (!this.keychain) return;
        clearInterval(id);
        resolve();
      }, interval);
    });
  }
};

// src/lookup.ts
import { num } from "starknet";
var cache = /* @__PURE__ */ new Map();
async function lookup(request) {
  if (!request.addresses?.length && !request.usernames?.length) {
    return { results: [] };
  }
  const response = await fetch(`${API_URL}/lookup`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(request)
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}
async function lookupUsernames(usernames) {
  const uncachedUsernames = usernames.filter((name) => !cache.has(name));
  if (uncachedUsernames.length > 0) {
    const response = await lookup({ usernames: uncachedUsernames });
    response.results.forEach((result) => {
      cache.set(result.username, result.addresses[0]);
    });
  }
  return new Map(
    usernames.map((name) => [name, cache.get(name)]).filter((entry) => entry[1] !== void 0)
  );
}
async function lookupAddresses(addresses) {
  addresses = addresses.map(num.toHex);
  const uncachedAddresses = addresses.filter((addr) => !cache.has(addr));
  if (uncachedAddresses.length > 0) {
    const response = await lookup({
      addresses: uncachedAddresses
    });
    response.results.forEach((result) => {
      cache.set(result.addresses[0], result.username);
    });
  }
  return new Map(
    addresses.map((addr) => [addr, cache.get(addr)]).filter((entry) => entry[1] !== void 0)
  );
}

// ../../node_modules/.pnpm/@cartridge+presets@https+++codeload.github.com+cartridge-gg+presets+tar.gz+101d241/node_modules/@cartridge/presets/dist/index.js
var configs = {
  "blob-arena": {
    origin: "https://www.blobarena.xyz",
    theme: {
      colors: {
        primary: "#980f06"
      },
      cover: "https://static.cartridge.gg/presets/blob-arena/cover.png",
      icon: "https://static.cartridge.gg/presets/blob-arena/icon.png",
      name: "Blob Arena"
    }
  },
  cartridge: {
    origin: "*",
    theme: {
      name: "Cartridge",
      icon: "https://static.cartridge.gg/presets/cartridge/icon.svg",
      cover: {
        light: "https://static.cartridge.gg/presets/cartridge/cover-light.png",
        dark: "https://static.cartridge.gg/presets/cartridge/cover-dark.png"
      }
    }
  },
  "dark-shuffle": {
    origin: "https://darkshuffle.dev",
    theme: {
      colors: {
        primary: "#F59100"
      },
      cover: "https://static.cartridge.gg/presets/dark-shuffle/cover.png",
      icon: "https://static.cartridge.gg/presets/dark-shuffle/icon.svg",
      name: "Dark Shuffle"
    }
  },
  "dope-wars": {
    origin: "dopewars.game",
    policies: {
      contracts: {
        "0x051Fea4450Da9D6aeE758BDEbA88B2f665bCbf549D2C61421AA724E9AC0Ced8F": {
          name: "VRF Provider",
          description: "Provides verifiable random functions",
          methods: [
            {
              name: "Request Random",
              description: "Request a random number",
              entrypoint: "request_random"
            }
          ]
        },
        "0x0410466536b5ae074f7fea81e5533b8134a9fa08b3dd077dd9db08f64997d113": {
          name: "Paper Token",
          description: "Manages paper approvals",
          methods: [
            {
              name: "Approve",
              description: "Approve paper usage",
              entrypoint: "approve"
            }
          ]
        },
        "0x044a23BbfE03FFe90D3C23Fb6e5A8AD0341036C039363DfA6F3513278Aa51fCA": {
          name: "Game Contract",
          description: "Core game mechanics",
          methods: [
            {
              name: "Create Game",
              description: "Start a new game",
              entrypoint: "create_game"
            },
            {
              name: "Travel",
              description: "Travel to a new location",
              entrypoint: "travel"
            },
            {
              name: "Decide",
              description: "Make a game decision",
              entrypoint: "decide"
            },
            {
              name: "End Game",
              description: "End the current game",
              entrypoint: "end_game"
            }
          ]
        },
        "0x0412445e644070C69fEa16b964cC81Cd6dEBF6A4DBf683E2E9686a45ad088de8": {
          name: "Laundromat Contract",
          description: "Manages game scoring and laundering",
          methods: [
            {
              name: "Register Score",
              description: "Register a game score",
              entrypoint: "register_score"
            },
            {
              name: "Claim",
              description: "Claim rewards",
              entrypoint: "claim"
            },
            {
              name: "Launder",
              description: "Launder resources",
              entrypoint: "launder"
            }
          ]
        }
      }
    },
    theme: {
      colors: {
        primary: "#11ED83"
      },
      cover: "https://static.cartridge.gg/presets/dope-wars/cover.png",
      icon: "https://static.cartridge.gg/presets/dope-wars/icon.png",
      name: "Dope Wars"
    }
  },
  dragark: {
    origin: "https://dragark.net",
    theme: {
      colors: {
        primary: "#ED1138"
      },
      cover: "https://static.cartridge.gg/presets/dragark/cover.png",
      icon: "https://static.cartridge.gg/presets/dragark/icon.png",
      name: "Dragark"
    }
  },
  eternum: {
    origin: "eternum.realms.world",
    policies: {
      contracts: {
        "0x4ed3a7c5f53c6e96186eaf1b670bd2e2a3699c08e070aedf4e5fc6ac246ddc1": {
          name: "Pillage",
          description: "Allows you to raid a structure and pillage resources",
          methods: [
            {
              name: "Battle Pillage",
              description: "Pillage a structure",
              entrypoint: "battle_pillage"
            }
          ]
        },
        "0x2620f65aa2fd72d705306ada1ee7410023a3df03da9291f1ccb744fabfebc0": {
          name: "Battle contract",
          description: "Required to engage in battles",
          methods: [
            {
              name: "Battle Start",
              description: "Start a battle",
              entrypoint: "battle_start"
            },
            {
              name: "Battle Force Start",
              description: "Force start a battle",
              entrypoint: "battle_force_start"
            },
            {
              name: "Battle Join",
              description: "Join a battle",
              entrypoint: "battle_join"
            },
            {
              name: "Battle Leave",
              description: "Leave a battle",
              entrypoint: "battle_leave"
            },
            {
              name: "Battle Claim",
              description: "Claim a structure after a battle",
              entrypoint: "battle_claim"
            },
            {
              name: "Battle Resolve",
              description: "Reopens the bank after battle",
              entrypoint: "battle_resolve"
            }
          ]
        },
        "0x6bf57710571fd159e71b1ed155bb0759027e416c88a06556f321c94c214e768": {
          name: "Leave battle contract",
          description: "Allows armies to leave a battle",
          methods: [
            {
              name: "Leave Battle",
              description: "Leave a battle",
              entrypoint: "leave_battle"
            },
            {
              name: "Leave Battle If Ended",
              description: "Leave a battle if its ended",
              entrypoint: "leave_battle_if_ended"
            }
          ]
        },
        "0x4b6a35c0c541467674ebb9640113a6d79c6f5a468796e9299b8e484a770032a": {
          name: "Building contract",
          description: "Allows to manage buildings",
          methods: [
            {
              name: "Create",
              description: "Create a building",
              entrypoint: "create"
            },
            {
              name: "Pause Production",
              description: "Pause the production of a building",
              entrypoint: "pause_production"
            },
            {
              name: "Resume Production",
              description: "Resume production of a building",
              entrypoint: "resume_production"
            },
            {
              name: "Destroy a building",
              description: "Destroy a building",
              entrypoint: "destroy"
            }
          ]
        },
        "0x57d514154bb4dc319539d4e338312a41c248fb6a5122f82b2f2e11ddd3e67e4": {
          name: "Guild contract",
          description: "Allows guild utilities",
          methods: [
            {
              name: "Create Guild",
              description: "Creates a new guild",
              entrypoint: "create_guild"
            },
            {
              name: "Join Guild",
              description: "Join an existing guild",
              entrypoint: "join_guild"
            },
            {
              name: "Whitelist Player",
              description: "Add a player to the guild's whitelist",
              entrypoint: "whitelist_player"
            },
            {
              name: "Transfer Guild Ownership",
              description: "Transfer ownership of the guild to another player",
              entrypoint: "transfer_guild_ownership"
            },
            {
              name: "Remove Guild Member",
              description: "Remove a member from the guild",
              entrypoint: "remove_guild_member"
            },
            {
              name: "Remove Player From Whitelist",
              description: "Remove a player from the guild's whitelist",
              entrypoint: "remove_player_from_whitelist"
            }
          ]
        },
        "0x2fcc3c1691387321c2f4d6310eda7a14365bd274c1a37ed3948d2e93a56f821": {
          name: "Hyperstructure contract",
          description: "Handles the creation and management of hyperstructures",
          methods: [
            {
              name: "Get Points",
              description: "Gets your total number of points",
              entrypoint: "get_points"
            },
            {
              name: "Create",
              description: "Create a new hyperstructure",
              entrypoint: "create"
            },
            {
              name: "Contribute To Construction",
              description: "Contribute resources to hyperstructure construction",
              entrypoint: "contribute_to_construction"
            },
            {
              name: "Set Co Owners",
              description: "Set additional owners for the hyperstructure",
              entrypoint: "set_co_owners"
            },
            {
              name: "End Game",
              description: "Terminates the current game season once you've reached enough points",
              entrypoint: "end_game"
            },
            {
              name: "Set Access",
              description: "Configure access permissions for contributions to the hyperstructure",
              entrypoint: "set_access"
            }
          ]
        },
        "0xa052c5ca082356bcc8457f0f805eaf18f97d0fdddde3f90f5b238923859ed4": {
          name: "AMM liquidity contract",
          description: "Manages liquidity for the Automated Market Maker",
          methods: [
            {
              name: "Add",
              description: "Add liquidity to the pool",
              entrypoint: "add"
            },
            {
              name: "Remove",
              description: "Remove liquidity from the pool",
              entrypoint: "remove"
            }
          ]
        },
        "0x4a212c52c4035bc9bd170125216604f406dcd75b41be11d3b4d89047366d84d": {
          name: "Exploration contract",
          description: "Allows you to move to unexplored hexes on the map",
          methods: [
            {
              name: "Explore",
              description: "Explore an uncharted hex on the game map",
              entrypoint: "explore"
            }
          ]
        },
        "0x7e3bae0e253a0131063b63ee4d7b27b50329c617ae88b82d529a70f1a11c63": {
          name: "Naming contract",
          description: "Manages entity naming in the game",
          methods: [
            {
              name: "Set Entity Name",
              description: "Assign a custom name to a game entity",
              entrypoint: "set_entity_name"
            },
            {
              name: "Set Address Name",
              description: "Assign a custom name to a user's address",
              entrypoint: "set_address_name"
            }
          ]
        },
        "0x1b480f620ea35431ab43dba634795b14f547ef3e77370db6f0a31f2fdc21d86": {
          name: "Realms contract",
          description: "Manages realm-related actions",
          methods: [
            {
              name: "Create a realm",
              description: "Create a new realm",
              entrypoint: "create"
            },
            {
              name: "Upgrade Level",
              description: "Upgrade the level of a realm",
              entrypoint: "upgrade_level"
            },
            {
              name: "Quest Claim",
              description: "Claim rewards from completed quests",
              entrypoint: "quest_claim"
            }
          ]
        },
        "0x691a60b709ca5c1c5ff86824831f84deb26f0f5d07d70c0f807eab48110d2f6": {
          name: "Resource bridge contract",
          description: "Manages bridge transfers between L2 and Eternum",
          methods: [
            {
              name: "Deposit Initial",
              description: "Initial deposit of resources for bridge transfer",
              entrypoint: "deposit_initial"
            },
            {
              name: "Deposit",
              description: "Deposit additional resources for bridge transfer",
              entrypoint: "deposit"
            },
            {
              name: "Start Withdraw",
              description: "Initiate a withdrawal process",
              entrypoint: "start_withdraw"
            },
            {
              name: "Finish Withdraw",
              description: "Finalize a withdrawal process",
              entrypoint: "finish_withdraw"
            }
          ]
        },
        "0x42c0303a2119a9e20daa60e83c48221cdf1bb2a4c009bab031d1cd3555a127e": {
          name: "Resource contract",
          description: "In-game resource management",
          methods: [
            {
              name: "Approve",
              description: "Approve resource transfer",
              entrypoint: "approve"
            },
            {
              name: "Send",
              description: "Send resources to another entity",
              entrypoint: "send"
            },
            {
              name: "Pickup",
              description: "Collect available resources after approval",
              entrypoint: "pickup"
            }
          ]
        },
        "0x4f92a1d00d3aec8cece60fc2d0fc236fe1d95c54ff0ceb2f393fbc7e0863d8e": {
          name: "AMM swap contract",
          description: "Handles token swaps in the Automated Market Maker",
          methods: [
            {
              name: "Buy",
              description: "Purchase tokens from the liquidity pool",
              entrypoint: "buy"
            },
            {
              name: "Sell",
              description: "Sell tokens to the liquidity pool",
              entrypoint: "sell"
            }
          ]
        },
        "0x7e2b1334398fafbe640f34bacae99b649d633417960ee397b6a8fb117fec819": {
          name: "Market contract",
          description: "Manages trading orders in the in-game market",
          methods: [
            {
              name: "Create Order",
              description: "Create a new trading order",
              entrypoint: "create_order"
            },
            {
              name: "Accept Order",
              description: "Accept a trading order",
              entrypoint: "accept_order"
            },
            {
              name: "Accept Partial Order",
              description: "Accept a partial trading order",
              entrypoint: "accept_partial_order"
            },
            {
              name: "Cancel Order",
              description: "Cancel a trading order",
              entrypoint: "cancel_order"
            }
          ]
        },
        "0x4069c2be57f08fef9f31afc85a5b4c03c208ebdb278b9d853606caa7a9cbee6": {
          name: "Map travel contract",
          description: "Manages player movement across the game map",
          methods: [
            {
              name: "Travel Hex",
              description: "Move to a specific hex on the map",
              entrypoint: "travel_hex"
            }
          ]
        },
        "0x26be0ed574aa9ee6f73b53b12f0a199ddbf4ac697470316cdb3d9d1f5680cab": {
          name: "Army contract",
          description: "Manages army-related actions",
          methods: [
            {
              name: "Army Create",
              description: "Create a new army",
              entrypoint: "army_create"
            },
            {
              name: "Army Delete",
              description: "Delete an existing army",
              entrypoint: "army_delete"
            },
            {
              name: "Army Buy Troops",
              description: "Buy troops for an army",
              entrypoint: "army_buy_troops"
            },
            {
              name: "Army Merge Troops",
              description: "Merge troops from multiple armies",
              entrypoint: "army_merge_troops"
            }
          ]
        },
        "0x051fea4450da9d6aee758bdeba88b2f665bcbf549d2c61421aa724e9ac0ced8f": {
          name: "VRF Provider",
          description: "Verifiable Random Function contract, allows randomness in the game",
          methods: [
            {
              name: "Request Random",
              description: "Allows requesting random numbers from the VRF provider",
              entrypoint: "request_random"
            }
          ]
        },
        "0x057675b9c0bd62b096a2e15502a37b290fa766ead21c33eda42993e48a714b80": {
          name: "Season pass ERC20 contract",
          description: "Manages the season passes",
          methods: [
            {
              name: "Approve for all",
              description: "Approves transfer of season pass",
              entrypoint: "set_approval_for_all"
            }
          ]
        }
      },
      messages: [
        {
          types: {
            StarknetDomain: [
              {
                name: "name",
                type: "shortstring"
              },
              {
                name: "version",
                type: "shortstring"
              },
              {
                name: "chainId",
                type: "shortstring"
              },
              {
                name: "revision",
                type: "shortstring"
              }
            ],
            "s0_eternum-Message": [
              {
                name: "identity",
                type: "ContractAddress"
              },
              {
                name: "channel",
                type: "shortstring"
              },
              {
                name: "content",
                type: "string"
              },
              {
                name: "timestamp",
                type: "felt"
              },
              {
                name: "salt",
                type: "felt"
              }
            ]
          },
          primaryType: "s0_eternum-Message",
          domain: {
            name: "Eternum",
            version: "1",
            chainId: "SN_MAIN",
            revision: "1"
          }
        }
      ]
    },
    theme: {
      name: "Eternum",
      icon: "https://static.cartridge.gg/presets/eternum/icon.svg",
      cover: "https://static.cartridge.gg/presets/eternum/cover.png",
      colors: {
        primary: "#dc8b07"
      }
    }
  },
  flippyflop: {
    origin: "https://flippyflop.gg",
    theme: {
      colors: {
        primary: "#F38332"
      },
      cover: "https://static.cartridge.gg/presets/flippyflop/cover.png",
      icon: "https://static.cartridge.gg/presets/flippyflop/icon.png",
      name: "FlippyFlop"
    }
  },
  "force-prime": {
    origin: "https://forceprime.io",
    theme: {
      colors: {
        primary: "#E1CC89"
      },
      cover: "https://static.cartridge.gg/presets/force-prime/cover.png",
      icon: "https://static.cartridge.gg/presets/force-prime/icon.png",
      name: "Force Prime"
    }
  },
  "jokers-of-neon": {
    origin: "https://jokersofneon.com",
    theme: {
      colors: {
        primary: "#A144B2"
      },
      cover: "https://static.cartridge.gg/presets/jokers-of-neon/cover.png",
      icon: "https://static.cartridge.gg/presets/jokers-of-neon/icon.png",
      name: "Jokers of Neon"
    }
  },
  "loot-survivor": {
    origin: "https://lootsurvivor.io",
    policies: {
      contracts: {
        "0x0124aeb495b947201f5fac96fd1138e326ad86195b98df6dec9009158a533b49": {
          methods: [
            {
              entrypoint: "approve"
            },
            {
              entrypoint: "mint_lords"
            }
          ]
        },
        "0x0305f26ad19e0a10715d9f3137573d3a543de7b707967cd85d11234d6ec0fb7e": {
          methods: [
            {
              entrypoint: "attack"
            },
            {
              entrypoint: "drop"
            },
            {
              entrypoint: "equip"
            },
            {
              entrypoint: "explore"
            },
            {
              entrypoint: "flee"
            },
            {
              entrypoint: "new_game"
            },
            {
              entrypoint: "transfer_from"
            },
            {
              entrypoint: "upgrade"
            }
          ]
        },
        "0x049d36570d4e46f48e99674bd3fcc84644ddd6b96f7c741b1562b82f9e004dc7": {
          methods: [
            {
              entrypoint: "approve"
            }
          ]
        }
      }
    },
    theme: {
      colors: {
        primary: "#33FF33"
      },
      cover: "https://static.cartridge.gg/presets/loot-survivor/cover.png",
      icon: "https://static.cartridge.gg/presets/loot-survivor/icon.png",
      name: "Loot Survivor"
    }
  },
  paved: {
    origin: "https://paved.gg",
    theme: {
      colors: {
        primary: "#B0CAF8"
      },
      cover: "https://static.cartridge.gg/presets/paved/cover.png",
      icon: "https://static.cartridge.gg/presets/paved/icon.svg",
      name: "Paved"
    }
  },
  pistols: {
    origin: "https://pistols.underware.gg",
    theme: {
      colors: {
        primary: "#EF9758"
      },
      cover: "https://static.cartridge.gg/presets/pistols/cover.png",
      icon: "https://static.cartridge.gg/presets/pistols/icon.png",
      name: "Pistols at Ten Blocks"
    }
  },
  pixelaw: {
    origin: "https://dojo.pixelaw.xyz",
    theme: {
      colors: {
        primary: "#7C00B1",
        primaryForeground: "white"
      },
      cover: "https://static.cartridge.gg/presets/pixelaw/cover.png",
      icon: "https://static.cartridge.gg/presets/pixelaw/icon.svg",
      name: "Pixelaw"
    }
  },
  "realm-of-ra": {
    origin: "https://mancala.realmofra.com",
    theme: {
      colors: {
        primary: "#de9534"
      },
      cover: "https://static.cartridge.gg/presets/realm-of-ra/cover.png",
      icon: "https://static.cartridge.gg/presets/realm-of-ra/icon.png",
      name: "Realm of Ra"
    }
  },
  "savage-summit": {
    origin: "",
    theme: {
      colors: {
        primary: "#fbf7da"
      },
      cover: "https://static.cartridge.gg/presets/savage-summit/cover.png",
      icon: "https://static.cartridge.gg/presets/savage-summit/icon.png",
      name: "Savage Summit"
    }
  },
  "tale-weaver": {
    origin: "",
    theme: {
      colors: {
        primary: "#fce377"
      },
      cover: "https://static.cartridge.gg/presets/tale-weaver/cover.png",
      icon: "https://static.cartridge.gg/presets/tale-weaver/icon.png",
      name: "Tale Weaver"
    }
  },
  zkastle: {
    origin: "https://zkastle.vercel.app",
    theme: {
      colors: {
        primary: "#E50D2C"
      },
      cover: "https://static.cartridge.gg/presets/zkastle/cover.png",
      icon: "https://static.cartridge.gg/presets/zkastle/icon.svg",
      name: "zKastle"
    }
  },
  zktt: {
    origin: "https://zktable.top",
    theme: {
      colors: {
        primary: "#FFFFFF"
      },
      cover: "https://static.cartridge.gg/presets/zktt/cover.png",
      icon: "https://static.cartridge.gg/presets/zktt/icon.png",
      name: "zKTT"
    }
  },
  zkube: {
    origin: "https://zkube.vercel.app",
    theme: {
      colors: {
        primary: "#5bc3e6"
      },
      cover: "https://static.cartridge.gg/presets/zkube/cover.png",
      icon: "https://static.cartridge.gg/presets/zkube/icon.png",
      name: "zKube"
    }
  }
};
var metadata = [
  {
    name: "Wrapped BTC",
    symbol: "WBTC",
    decimals: 8,
    l2_token_address: "0x03fe2b97c1fd336e750087d68b9b867997fd64a2661ff3ca5a7c771641e8e7ac",
    sort_order: 0,
    total_supply: null,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/7dcb2db2-a7a7-44af-660b-8262e057a100/logo"
  },
  {
    name: "USD Coin",
    symbol: "USDC",
    decimals: 6,
    l2_token_address: "0x053c91253bc9682c04929ca02ed00b3e423f6710d2ee7e0d5ebb06f3ecf368a8",
    sort_order: 5,
    total_supply: null,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/e5aaa970-a998-47e8-bd43-4a3b56b87200/logo"
  },
  {
    name: "LUSD Stablecoin",
    symbol: "LUSD",
    decimals: 18,
    l2_token_address: "0x070a76fd48ca0ef910631754d77dd822147fe98a569b826ec85e3c33fde586ac",
    sort_order: 3,
    total_supply: null,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/dc0ae733-5498-4afa-f475-48dba677aa00/logo"
  },
  {
    name: "Tether USD",
    symbol: "USDT",
    decimals: 6,
    l2_token_address: "0x068f5c6a61780768455de69077e07e89787839bf8166decfbf92b645209c0fb8",
    sort_order: 4,
    total_supply: null,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/c8a721d1-07c3-46e4-ab4e-523977c30b00/logo"
  },
  {
    name: "Ether",
    symbol: "ETH",
    decimals: 18,
    l2_token_address: "0x049d36570d4e46f48e99674bd3fcc84644ddd6b96f7c741b1562b82f9e004dc7",
    sort_order: 3,
    total_supply: null,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/e07829b7-0382-4e03-7ecd-a478c5aa9f00/logo"
  },
  {
    name: "Dai Stablecoin",
    symbol: "DAIv0",
    decimals: 18,
    l2_token_address: "0x00da114221cb83fa859dbdb4c44beeaa0bb37c7537ad5ae66fe5e0efd20e6eb3",
    sort_order: 4,
    total_supply: null,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/919e761b-56f7-4f53-32aa-5e066f7f6200/logo"
  },
  {
    name: "Dai Stablecoin",
    symbol: "DAI",
    decimals: 18,
    l2_token_address: "0x05574eb6b8789a91466f902c380d978e472db68170ff82a5b650b95a58ddf4ad",
    sort_order: 4,
    total_supply: null,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/919e761b-56f7-4f53-32aa-5e066f7f6200/logo"
  },
  {
    name: "Wrapped liquid staked Ether 2.0",
    symbol: "wstETH",
    decimals: 18,
    l2_token_address: "0x042b8f0484674ca266ac5d08e4ac6a3fe65bd3129795def2dca5c34ecc5f96d2",
    sort_order: 1,
    total_supply: null,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/dbbcbdea-1a92-437d-3701-4a5ee129d000/logo"
  },
  {
    name: "Rocket Pool ETH",
    symbol: "rETH",
    decimals: 18,
    l2_token_address: "0x0319111a5037cbec2b3e638cc34a3474e2d2608299f3e62866e9cc683208c610",
    sort_order: 1,
    total_supply: null,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/c9f2d6fe-fbc6-4384-0990-923dfcb7a200/logo"
  },
  {
    name: "LORDS",
    symbol: "LORDS",
    decimals: 18,
    l2_token_address: "0x0124aeb495b947201f5fac96fd1138e326ad86195b98df6dec9009158a533b49",
    sort_order: 1,
    total_supply: 509e5,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/a3bfe959-50c4-4f89-0aef-b19207d82a00/logo"
  },
  {
    name: "R Stablecoin",
    symbol: "R",
    decimals: 18,
    l2_token_address: "0x01fa2fb85f624600112040e1f3a848f53a37ed5a7385810063d5fe6887280333",
    sort_order: 3,
    total_supply: null,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/77612e4e-f7ee-4dba-2066-af321843ef00/logo"
  },
  {
    name: "Frax",
    symbol: "FRAX",
    decimals: 18,
    l2_token_address: "0x009c6b4fb13dfaa025c1383ed6190af8ed8cbb09d9588a3bb020feb152442406",
    sort_order: 1,
    total_supply: 649462235,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/eeaf0779-e492-474c-ef19-b27843525600/logo"
  },
  {
    name: "Frax Share",
    symbol: "FXS",
    decimals: 18,
    l2_token_address: "0x0058efd0e73c33a848ffaa88738d128ebf0af98ea78cf3c14dc757bb02d39ffb",
    sort_order: 1,
    total_supply: null,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/98bea621-1e4f-4d63-9689-bdaef0d56500/logo"
  },
  {
    name: "Staked Frax Ether",
    symbol: "sfrxETH",
    decimals: 18,
    l2_token_address: "0x04578fffc279e61b5cb0267a5f8e24b6089d40f93158fbbad2cb23b8622c9233",
    sort_order: 1,
    total_supply: null,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/cd6fe18a-25db-4de9-758a-daf3b364ea00/logo"
  },
  {
    name: "Anubis",
    symbol: "ANB",
    decimals: 18,
    l2_token_address: "0x070dd1f88cc973de315c10b2da4e196554c20e925d630474e257a0a4d9d363dc",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/ef8a0a50-7c12-4b9b-e937-1bea5bdd3500/logo"
  },
  {
    name: "Uniswap",
    symbol: "UNI",
    decimals: 18,
    l2_token_address: "0x049210ffc442172463f3177147c1aeaa36c51d152c1b0630f2364c300d4f48ee",
    sort_order: 1,
    total_supply: 1e9,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/361b018e-bd53-4019-27c8-7cf8d9031b00/logo"
  },
  {
    name: "OWL",
    symbol: "OWL",
    decimals: 18,
    l2_token_address: "0x039877a272619050ab8b0e3e0a19b58d076fc2ce84da1dc73b699590e629f2b8",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/30622111-3d69-4d47-37fd-2beac1c53400/logo"
  },
  {
    name: "Paper",
    symbol: "PAPER",
    decimals: 18,
    l2_token_address: "0x0410466536b5ae074f7fea81e5533b8134a9fa08b3dd077dd9db08f64997d113",
    sort_order: 1,
    total_supply: null,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/811f019a-0461-4cff-6c1e-442102863f00/logo"
  },
  {
    name: "STRAK-meme",
    symbol: "STRAK-meme",
    decimals: 18,
    l2_token_address: "0x055c3868c743e92c470701799388cb3fb1d922dcec271150d10462c6948e3cf4",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "StarkPepe",
    symbol: "xSPEPE",
    decimals: 18,
    l2_token_address: "0x06f15ec4b6ff0b7f7a216c4b2ccdefc96cbf114d6242292ca82971592f62273b",
    sort_order: 1,
    total_supply: null,
    hidden: true,
    disabled: true
  },
  {
    name: "Gary Gensler",
    symbol: "xGARY",
    decimals: 18,
    l2_token_address: "0xd3d60f69d76b6639fc51ae1447480ecdf0ba1ea3f6162e3230be0a40ef2a6b",
    sort_order: 1,
    total_supply: null,
    hidden: true,
    disabled: true
  },
  {
    name: "StarkBanana",
    symbol: "xSBANANA",
    decimals: 18,
    l2_token_address: "0x03650d77855a4f05b9dd0acff3936cfffd693624754a96d90e4d5876e06c231d",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true,
    disabled: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/516c435e-f89a-4a88-aff0-fdcc6e218900/logo"
  },
  {
    name: "Starknet",
    symbol: "STRK",
    decimals: 18,
    l2_token_address: "0x04718f5a0fc34cc1af16a1cdee98ffb20c31f5cd61d6ab07201858f4287c938d",
    sort_order: 2,
    total_supply: 1e10,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/1b126320-367c-48ed-cf5a-ba7580e49600/logo"
  },
  {
    name: "zkLend",
    symbol: "ZEND",
    decimals: 18,
    l2_token_address: "0x00585c32b625999e6e5e78645ff8df7a9001cf5cf3eb6b80ccdd16cb64bd3a34",
    sort_order: 1,
    total_supply: null,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/95515b0e-1230-4158-10f1-56888f613c00/logo"
  },
  {
    name: "Also Ekubo Protocol",
    symbol: "MOCHI",
    decimals: 18,
    l2_token_address: "0x075afe6402ad5a5c20dd25e10ec3b3986acaa647b77e4ae24b0cbc9a54a27a87",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/e623fa00-5b58-4b38-1d08-67983128b600/logo"
  },
  {
    name: "Ekubo Protocol",
    symbol: "EKUBO",
    decimals: 18,
    l2_token_address: "0x075afe6402ad5a5c20dd25e10ec3b3986acaa647b77e4ae24b0cbc9a54a27a87",
    sort_order: 1,
    total_supply: 1e7,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/634d9c36-2f0b-4781-93e6-72d701b5af00/logo"
  },
  {
    name: "SOCKS",
    symbol: "SOCKS",
    decimals: 18,
    l2_token_address: "0x023ed2ba4fb5709302c5dfd739fa7613359042f143286c115b6c7f7dc2601015",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/2db5a2a6-c98e-4b80-35e0-31b489132100/logo"
  },
  {
    name: "Nostra",
    symbol: "NSTR",
    decimals: 18,
    l2_token_address: "0x00c530f2c0aa4c16a0806365b0898499fba372e5df7a7172dc6fe9ba777e8007",
    sort_order: 1,
    total_supply: 1e8,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/a45c2224-17a7-4269-ea7e-3924e9755800/logo"
  },
  {
    name: "Standard Weighted Adalian Yield",
    symbol: "SWAY",
    decimals: 6,
    l2_token_address: "0x004878d1148318a31829523ee9c6a5ee563af6cd87f90a30809e5b0d27db8a9b",
    sort_order: 1,
    total_supply: null,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/dd43c15d-80bf-4298-7620-abc12d474f00/logo"
  },
  {
    name: "ZKX",
    symbol: "ZKX",
    decimals: 18,
    l2_token_address: "0x256c74b33f5734c68402cd9433dfa77856f62c6940f54591874527cf898a924",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/bfc2ef55-3dd4-43ef-66a7-9a0e228b5900/logo"
  },
  {
    name: "Carmine",
    symbol: "CRM",
    decimals: 18,
    l2_token_address: "0x51c4b1fe3bf6774b87ad0b15ef5d1472759076e42944fff9b9f641ff13e5bbe",
    sort_order: 1,
    total_supply: 1e8,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/6ab817f1-8075-4a94-6e14-f112f1f89d00/logo"
  },
  {
    name: "Nimbora sDAI",
    symbol: "nsDAI",
    decimals: 18,
    l2_token_address: "0x004380de5819e2e989b5e8b978ea2811fd36fdbc5c12fcfb3a2b444098888665",
    sort_order: 2,
    total_supply: null,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/a016d9ab-a7b5-4f31-3656-f41fa27cc300/logo"
  },
  {
    name: "Nimbora Pendle EtherFi ETH",
    symbol: "npeETH",
    decimals: 18,
    l2_token_address: "0x316ec509f7ad89b7e6e03d15a436df634454f95e815536d616af03edc850fa3",
    sort_order: 1,
    total_supply: null,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/f1f900d0-da29-49f6-271b-d1a3754aa200/logo"
  },
  {
    name: "Nimbora Pendle Puffer ETH",
    symbol: "nppETH",
    decimals: 18,
    l2_token_address: "0x00357cba05d61beb5fe378429d25013dc5fe0f67878b541747b0675c5ebecee1",
    sort_order: 1,
    total_supply: null,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/d37fbc14-daa9-45e8-d523-c656704e7d00/logo"
  },
  {
    name: "Nimbora Pendle Flux USDC",
    symbol: "npfUSDC",
    decimals: 6,
    l2_token_address: "0x52bdb85297e6b0c87d8ec98c5195a4324ff731676d64d9bee2e9e8710e8ea52",
    sort_order: 3,
    total_supply: null,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/af942cb7-aae8-41dc-5cc9-4aedc5c06000/logo"
  },
  {
    name: "Nimbora Pendle Aave USDT",
    symbol: "npaUSDT",
    decimals: 6,
    l2_token_address: "0x040daf98c49c6104002428958456208455a03825198fa7646bd793a321f4fc82",
    sort_order: 3,
    total_supply: null,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/f376f6d9-bcf3-41b0-c0d3-6ef2f606d200/logo"
  },
  {
    name: "Cash",
    symbol: "CASH",
    decimals: 18,
    l2_token_address: "0x498edfaf50ca5855666a700c25dd629d577eb9afccdf3b5977aec79aee55ada",
    sort_order: 3,
    total_supply: null,
    hidden: false,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/6bd6d156-f509-4b51-5dfc-3ee566143600/logo"
  },
  {
    name: "Flip",
    symbol: "FLIP",
    decimals: 18,
    l2_token_address: "0x01bfe97d729138fc7c2d93c77d6d1d8a24708d5060608017d9b384adf38f04c7",
    sort_order: 1,
    total_supply: null,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/275f0fa8-a691-471c-ace6-0eb0315dde00/logo"
  },
  {
    name: "Alf the Alpha",
    symbol: "ALF",
    decimals: 18,
    l2_token_address: "0x078d7dfcddb44667153513bd837ff534ef395484f4b6cf28faa25b2c46e063d9",
    sort_order: 1,
    total_supply: 10065076070,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/5864e60d-8432-42d3-1ad4-fced31d43a00/logo"
  },
  {
    name: "Endur xSTRK",
    symbol: "xSTRK",
    decimals: 18,
    l2_token_address: "0x028d709c875c0ceac3dce7065bec5328186dc89fe254527084d1689910954b0a",
    sort_order: 1,
    total_supply: null,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/c1f44170-c1b0-4531-3d3b-5f0bacfe1300/logo"
  },
  {
    name: "Staked Starknet",
    symbol: "sSTRK",
    decimals: 18,
    l2_token_address: "0x0356f304b154d29d2a8fe22f1cb9107a9b564a733cf6b4cc47fd121ac1af90c9",
    sort_order: 1,
    total_supply: null,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/e28697ac-a8ba-462f-0d40-ad33b285c200/logo"
  },
  {
    name: "zkLend Staked STRK",
    symbol: "kSTRK",
    decimals: 18,
    l2_token_address: "0x45cd05ee2caaac3459b87e5e2480099d201be2f62243f839f00e10dde7f500c",
    sort_order: 1,
    total_supply: null,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/585854e8-3656-4bfc-412b-9cd8b8ae2900/logo"
  },
  {
    name: "JOHNNY",
    symbol: "JOHNNY",
    decimals: 18,
    l2_token_address: "0x46bad8dc5d58a853905fb7b0c2231917a1ae77cb14a8219e480808a81dafa86",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "pipa",
    symbol: "pipa",
    decimals: 18,
    l2_token_address: "0x47d9865ffbabc16c05f5bfe5dfcd7a28eaf7be9343e7500dfab00c81493e62",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "MyMeme",
    symbol: "MM",
    decimals: 18,
    l2_token_address: "0x1444606f60841573a010e820f9dd9ea203fc47f4f37fe7796d39b18caa93498",
    sort_order: 1,
    total_supply: 5e6,
    hidden: true
  },
  {
    name: "Justin hammer",
    symbol: "Hammer",
    decimals: 18,
    l2_token_address: "0x6fec9b7831f5becf31328dac95bf0e8aee6b25adb5390e62e96f0175843a087",
    sort_order: 1,
    total_supply: 21e9,
    hidden: true
  },
  {
    name: "Habiba",
    symbol: "HBB",
    decimals: 18,
    l2_token_address: "0x5938d6a20fc9ff91f33fde7beb38bf53794a4416c6b4314a6a879a489da1df3",
    sort_order: 1,
    total_supply: 5555551111,
    hidden: true
  },
  {
    name: "Starkspliff",
    symbol: "SPF",
    decimals: 18,
    l2_token_address: "0x72e1c6ea9a22669331add104d37eeaf97670a9c7a8727e967f290bdbd35ae43",
    sort_order: 1,
    total_supply: 420,
    hidden: true
  },
  {
    name: "the 8",
    symbol: "eight",
    decimals: 18,
    l2_token_address: "0x394071d2dbd65437cfbb5dab8fd72efe579c0daa5cfde60993a80965edc7d",
    sort_order: 1,
    total_supply: 8,
    hidden: true
  },
  {
    name: "Duckwifhat",
    symbol: "DWIF",
    decimals: 18,
    l2_token_address: "0x78f27c61243b81af94a108a9e3fe3124e2175b6a5721be8d8b7b2720b3cc215",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "YuvalMebulbal",
    symbol: "YMBLBL",
    decimals: 18,
    l2_token_address: "0x3432a5b0137cb59c33fc198f841658fa585b826afd45390d2116f2d805699b1",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "StarkCat",
    symbol: "SCAT",
    decimals: 18,
    l2_token_address: "0x6c2fd1bcc03e54e4e23b3ffd5f6a4c65a9397996b718e633283d6d4e77a5257",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "ZG",
    symbol: "ZGZEG",
    decimals: 18,
    l2_token_address: "0x400cab3b1c5c296e125b71fedd8002a87c1971c277bcafad227b99413b26c6c",
    sort_order: 1,
    total_supply: 8888888888888,
    hidden: true
  },
  {
    name: "Stark Killer",
    symbol: "SKER",
    decimals: 18,
    l2_token_address: "0x4e86d441c1156f86a51375b4b8189ef8893274a131f0ba6d2a2635d38b5c65",
    sort_order: 1,
    total_supply: 6666699999,
    hidden: true
  },
  {
    name: "SNAIL STARK",
    symbol: "SNAIL",
    decimals: 18,
    l2_token_address: "0x3f889d6e008c5790b744177ab1a48d4735862c1c29235390cb06307bea0d193",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "URBANIST",
    symbol: "URB",
    decimals: 18,
    l2_token_address: "0x10bba2c3a69bf5c5ba456fcdfd4e06387a6d22aaeeb5d15424110c7fa6d4154",
    sort_order: 1,
    total_supply: 5e7,
    hidden: true
  },
  {
    name: "RUGGED",
    symbol: "RUG",
    decimals: 18,
    l2_token_address: "0x5d19ed3bb1ba640a5448fae3d5caa8d857f0834ba8c75e9278151496cc1fbdb",
    sort_order: 1,
    total_supply: 1e5,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/33a39ba6-670f-47c1-4f49-7ac1db409400/logo"
  },
  {
    name: "Niggolas Cage",
    symbol: "Nigg",
    decimals: 18,
    l2_token_address: "0x550664762709d3d34684f6d985e8f9b2c3366e8ce3e712461f996f8ed816c13",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "more tps daddy",
    symbol: "MORE",
    decimals: 18,
    l2_token_address: "0x113cf9c90c2e545bbfb270e4713c390a56eb87902f8d1cc2c83660ff9ed8bd",
    sort_order: 1,
    total_supply: 287777e5,
    hidden: true
  },
  {
    name: "Dog Wif Hat",
    symbol: "WIF",
    decimals: 18,
    l2_token_address: "0xb862c8de208aa6ae7cb2005dd48b3e3052aa290dcab28907c8db461f83fdbd",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Powni",
    symbol: "POWNI",
    decimals: 18,
    l2_token_address: "0x79337c58d486aec3f2c5648271345d7c54657a158d88a1e3bd3521129c7cc94",
    sort_order: 1,
    total_supply: 42e9,
    hidden: true
  },
  {
    name: "hello",
    symbol: "hello",
    decimals: 18,
    l2_token_address: "0x10a96500c674697c1d94d1264d56c7faffee5bec1d8657619e54e55014c7461",
    sort_order: 1,
    total_supply: 1329227995784916e3,
    hidden: true
  },
  {
    name: "Baby Starknet",
    symbol: "BSTRK",
    decimals: 18,
    l2_token_address: "0x704bdb2a36bb773b39ea41873d30dbfee7111fbd9cd6336a40629fafca742f3",
    sort_order: 1,
    total_supply: 5e7,
    hidden: true
  },
  {
    name: "moshon-is-the-best",
    symbol: "gggg",
    decimals: 18,
    l2_token_address: "0xb519d76299e617361be6d221a5ce3cb7b2ef66b40292c65b3e0bcfaa3ebcd1",
    sort_order: 1,
    total_supply: 2e9,
    hidden: true
  },
  {
    name: "Gameplays y Bugs",
    symbol: "GYB",
    decimals: 18,
    l2_token_address: "0x78d8aabd655edc2ea405774a5114d647b998967df14aaa0ed7f23fe770f4b8c",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "BackDoor",
    symbol: "BD",
    decimals: 18,
    l2_token_address: "0x7c5782c9c96e142d1222f0838aaa9c3e10226bb9e6949554499ff429858f394",
    sort_order: 1,
    total_supply: 806,
    hidden: true
  },
  {
    name: "toby",
    symbol: "toby",
    decimals: 18,
    l2_token_address: "0x286933fe21dd7a9251dd3d6aa1da8d36741c30104aeec2f8bda9f8fcceb48fb",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "TIME",
    symbol: "TIME",
    decimals: 18,
    l2_token_address: "0xda2a16582da2e4da0fb303a0ad3b4197c10e222378a24531ab519182868033",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Sink It",
    symbol: "Sink",
    decimals: 18,
    l2_token_address: "0x29b44b2349dc6973b2b570b6215575695602927fd4ecd32409071c930ce69e6",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Slinky",
    symbol: "SLINK",
    decimals: 18,
    l2_token_address: "0x13ff4e86fa3e7286cc5c64b62f4099cf41e7918d727d22a5109ecfd00274d19",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/12b8ebd8-f4bf-4642-92e5-5ad8e7dafc00/logo"
  },
  {
    name: "TEST",
    symbol: "TST",
    decimals: 18,
    l2_token_address: "0x1a0d2af97dc31725bce7729533283b6f3e88b86d3bbcfe2d127df021f709326",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "Mulcion",
    symbol: "MCO",
    decimals: 18,
    l2_token_address: "0x712439d74c9a005d57c8313f6601c41f2c5b5782967c7fdb7aaa784802ab36",
    sort_order: 1,
    total_supply: 88888,
    hidden: true
  },
  {
    name: "PAL Is The First",
    symbol: "1 = PAL",
    decimals: 18,
    l2_token_address: "0x73f9943e9f96c89e71669a09ee882c828db222fe2e3d862c808916dd6f06aa4",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "RoniCarrington",
    symbol: "RCG",
    decimals: 18,
    l2_token_address: "0x167946fe551396c38bce8f8c468b9c4fcdb0e6ebfc6d083227c77fc67ff4cd4",
    sort_order: 1,
    total_supply: 4433322222,
    hidden: true
  },
  {
    name: "Nimbora Staked Usda",
    symbol: "nstUSD",
    decimals: 6,
    l2_token_address: "0x405b7b5fb7353ec745d9ef7cf1634e54fd25c5e24d62241c177114a18c45910",
    sort_order: 1,
    total_supply: 66,
    hidden: true
  },
  {
    name: "Pedro Pedro Pedro",
    symbol: "PEDRO",
    decimals: 18,
    l2_token_address: "0x202212de0c23798ac24b3d10b3d310c16783f15629ab6ae03c2706e61412005",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Never Trust a Dijonnais",
    symbol: "DIJON",
    decimals: 18,
    l2_token_address: "0x2f6f9b17426365725c0a9fe92b843d0cb6d7f9830dc60593d87f2601ab4c1ce",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "StarkBubble",
    symbol: "SBB",
    decimals: 18,
    l2_token_address: "0xd7dad148b7b66dd2e3710f11b0916d56283560a650559df0962e3c6974dc00",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "WE ONLY LIVE ONCE",
    symbol: "WOLO",
    decimals: 18,
    l2_token_address: "0x3ffd8fdc56155b1b80c69852a19d017ef202df1e728d606b16f415451a4c29f",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "Star",
    symbol: "star",
    decimals: 18,
    l2_token_address: "0x7fc8207f6ed789b46fe3ea7159064027b41e8a874df82b63a9a9148e733abb1",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "StarkCROC",
    symbol: "SCROC",
    decimals: 18,
    l2_token_address: "0x510e91b8afba2b17870b9d441cffdb64f36bfbb3c2bfad1a61e873045d95627",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "ONLY 10 TRANSACTIONS",
    symbol: "10TX",
    decimals: 18,
    l2_token_address: "0x3dc4c899ab90ef679d4006972a4155b8b4b5090c27ca1a0b94412736fed3c2c",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Kuzhakou",
    symbol: "KZQ",
    decimals: 18,
    l2_token_address: "0x240233d0d186f64a59c8704b11f638bff3499110ba7312b6f21e19d470a41dd",
    sort_order: 1,
    total_supply: 58564672902400,
    hidden: true
  },
  {
    name: "e-beggar",
    symbol: "EBGR",
    decimals: 18,
    l2_token_address: "0x13db4966643bf4182d49475ec905f354e386e048b375d2cd6e3822bd228d8f1",
    sort_order: 1,
    total_supply: 2e10,
    hidden: true
  },
  {
    name: "zero point four",
    symbol: "ZPF",
    decimals: 18,
    l2_token_address: "0x12442a0c5b35b8f3d72385c8dbfe832701cd2db7bc547a5429d1c03b5530774",
    sort_order: 1,
    total_supply: 5e5,
    hidden: true
  },
  {
    name: "Tremp",
    symbol: "TREMP",
    decimals: 18,
    l2_token_address: "0x15335f3f46b83b7cc0f6c8485807642fc4b6c8c098401105f482780afbb8422",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "BitStark",
    symbol: "BIT",
    decimals: 18,
    l2_token_address: "0x67c86a9f49b91fcf1450972dfcdee4f8ff7c2b975ff0886a1bafc862d73ae46",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "King Tobby Kitty",
    symbol: "TOBBY",
    decimals: 18,
    l2_token_address: "0x64626a935ee96e532673e0c638a6f3da937d0f57a74d8d2ec2150f09639b757",
    sort_order: 1,
    total_supply: 64e6,
    hidden: true
  },
  {
    name: "kesonwin",
    symbol: "KWN",
    decimals: 18,
    l2_token_address: "0x1f6df140969dd82b4c1141d1bf532198fd6fb1495cd20a3f527fd456c8e98ba",
    sort_order: 1,
    total_supply: 7329077678798,
    hidden: true
  },
  {
    name: "Hey Jude official",
    symbol: "JUDE",
    decimals: 18,
    l2_token_address: "0x24847297fd75569be279bf1d44405272b7da6588528c5b7d5736b93d8862806",
    sort_order: 1,
    total_supply: 1e4,
    hidden: true
  },
  {
    name: "Brioche Pur Beurre",
    symbol: "BPB",
    decimals: 18,
    l2_token_address: "0x32c5c3511cea179e4d5885cc174a90fe0f8f9f4f65b5457312ce55842b7e9e5",
    sort_order: 1,
    total_supply: 1e5,
    hidden: true
  },
  {
    name: "nomorealons",
    symbol: "nalon",
    decimals: 18,
    l2_token_address: "0x2faf7745cfa74ac80f84d0933b304fd0f3ae66ed1eb637db3f5db6301272521",
    sort_order: 1,
    total_supply: 7,
    hidden: true
  },
  {
    name: "Unruggable",
    symbol: "UNRUG",
    decimals: 18,
    l2_token_address: "0x613db0f731d6c8ab55ea3a9ea796a72734c89d11555c734dfb3afd5b0722da2",
    sort_order: 1,
    total_supply: 5,
    hidden: true
  },
  {
    name: "GaiaPapaya",
    symbol: "GAPA",
    decimals: 18,
    l2_token_address: "0x12ab6afe9fed41ff8f722927a8b2a4f6a3689c8ede8111eb8e0091ec85fdf61",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Debiet Herry",
    symbol: "DHY",
    decimals: 18,
    l2_token_address: "0x5f7158c3baa4188d7c9ad325c35a0cb928710b2b7357375f1a5668e9a073453",
    sort_order: 1,
    total_supply: 572662852187,
    hidden: true
  },
  {
    name: "jiutongjun",
    symbol: "JTJ",
    decimals: 18,
    l2_token_address: "0x21921f3de40112fddd339beb985fa8f652d795747eedcd6a990619777a0800c",
    sort_order: 1,
    total_supply: 444444444444,
    hidden: true
  },
  {
    name: "Eli",
    symbol: "ELI",
    decimals: 18,
    l2_token_address: "0x22efee3a59b1d25cd6e2a6c5609cc8d7810ecdfff0be41796c1841125a6bd86",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Baby Laika",
    symbol: "BABYLAIKA",
    decimals: 18,
    l2_token_address: "0x6ea04cd7e40bfef07c3625e2b3472839093d966bf601568128436466f962fcf",
    sort_order: 1,
    total_supply: 1957e12,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/82e0e163-6ad9-4f3c-1629-c66fcc4f0500/logo"
  },
  {
    name: "Putin",
    symbol: "PUTIN",
    decimals: 18,
    l2_token_address: "0x3cf12143058752c132f272c153826cd5737202628df099564b96358767f807c",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "Dubai Xander",
    symbol: "DXD",
    decimals: 18,
    l2_token_address: "0x2e7cebfb9f9d1977a8efc83f4cc5bd82837e75291838cea5617f7205daaf304",
    sort_order: 1,
    total_supply: 499875336,
    hidden: true
  },
  {
    name: "Unrug",
    symbol: "UNG",
    decimals: 18,
    l2_token_address: "0x5d5f59113d87ee0834455fde4dde6bb58172e9f4acd390a607520cb3d7dfac",
    sort_order: 1,
    total_supply: 7106454744,
    hidden: true
  },
  {
    name: 'SATOSHI "NAKAMOTO',
    symbol: "SATOSHI",
    decimals: 18,
    l2_token_address: "0x639e101d00db15bdcaa84b51d74547b5b2696673b8143952647f803e984a6e0",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "JULIAN ASSANGE",
    symbol: "WIKILEAKS",
    decimals: 18,
    l2_token_address: "0x5929094be3d7baf1cd3fc7752d9859294ad15ca9cee1442d12eda20d27f5e4a",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Reduction du Temps de Travail",
    symbol: "RTT",
    decimals: 18,
    l2_token_address: "0x400dfa348d7f4d6944abd83dba474df7b2e2bf80b63f312ada25ef20f2b253",
    sort_order: 1,
    total_supply: 35e6,
    hidden: true
  },
  {
    name: "StarkDog",
    symbol: "sDOG",
    decimals: 18,
    l2_token_address: "0x905bb70b5326fb2a575ae24e7f05b0c01f61e18cd5ebcb00ef573060c877e7",
    sort_order: 1,
    total_supply: 420000000069,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/cad88778-78ec-4bf3-18c3-13eef2196100/logo"
  },
  {
    name: "nemek",
    symbol: "NMK",
    decimals: 18,
    l2_token_address: "0x3798fe4fde2ce18bfe04a57e0d96bc579cbb6114667cb274a9a6df2f9ebebea",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Oxana@STARK",
    symbol: "Ox",
    decimals: 18,
    l2_token_address: "0x43f3dc6be8637ff89c9c517961f2b7d341ca72f83194e1ea63463da8ee90430",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "ARTPEACE",
    symbol: "ART",
    decimals: 18,
    l2_token_address: "0x787fcd81c60916886275a9c2ae54202db1f3c0dfd94a6d2eca9a9517f1269fc",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "Odin fam",
    symbol: "Odin",
    decimals: 18,
    l2_token_address: "0x269e1c082de181565a4571899791bb69fc5792d7c362f0aa0cbea25d8525627",
    sort_order: 1,
    total_supply: 123456789,
    hidden: true
  },
  {
    name: "Uncarpetable",
    symbol: "UCPT",
    decimals: 18,
    l2_token_address: "0x1ab6c43fb203a39f8bbd1aa4aab17c44a3909548891c75203ba7c5c467f673d",
    sort_order: 1,
    total_supply: 25e4,
    hidden: true
  },
  {
    name: "Onbodem",
    symbol: "OBD",
    decimals: 18,
    l2_token_address: "0x24611abd0e7203158d1ca2b7a04c2333341bb15c0f8b25a2582582228908a3e",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/57267f37-8030-45bb-78df-55fee8bb0a00/logo"
  },
  {
    name: "Bonzo",
    symbol: "BonZo",
    decimals: 18,
    l2_token_address: "0xd2afb52d33ae8e8690bc6f52c590fdfe8f1ae51c5be78519dc4f25dffcffb9",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "Stark Inu",
    symbol: "STARKINU",
    decimals: 18,
    l2_token_address: "0x6cead2351c6fc93ccf3a43d4ddb645d0c851c1827b0332e3ac0c5c89d6560db",
    sort_order: 1,
    total_supply: 8888888888888,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/83fe0fc0-0c64-4823-8647-53c87f03a900/logo"
  },
  {
    name: "molly",
    symbol: "molly",
    decimals: 18,
    l2_token_address: "0x2097a5dd1f45c6efa913d05f6cb4e0870cb890aaac02e83c4b0f16e91650703",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "1 of the 8 users",
    symbol: "1N8",
    decimals: 18,
    l2_token_address: "0x5e602506935bf389d98a0cd72e3a0ec2a8c28075854d12bec8c0f14f7cde399",
    sort_order: 1,
    total_supply: 3e6,
    hidden: true
  },
  {
    name: "TT",
    symbol: "TT",
    decimals: 18,
    l2_token_address: "0x11e1744158e25c07c278e584527f6ee3f551f3a8042351ae7a83dc5c4ff675a",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "HellyManSeason",
    symbol: "FAKELI",
    decimals: 18,
    l2_token_address: "0x1769a3046e379ab74ef21ecc858eb80ac784265eaa89a54593c7b341123025c",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "gidi",
    symbol: "gidi",
    decimals: 18,
    l2_token_address: "0x70ed1af5fc1196b510a8480c766d6b697daf802bd32a59234e79c04a86f894e",
    sort_order: 1,
    total_supply: 1e5,
    hidden: true
  },
  {
    name: "UNO",
    symbol: "UNO",
    decimals: 18,
    l2_token_address: "0x719b5092403233201aa822ce928bd4b551d0cdb071a724edd7dc5e5f57b7f34",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/32899821-e4aa-4131-00f9-7bd3a5728500/logo"
  },
  {
    name: "Drowzy",
    symbol: "ZZZ",
    decimals: 18,
    l2_token_address: "0x1597c6265320a24c8adb2054a117f8be35c5e3e12aab80bbeccf3cfbcdeeb5a",
    sort_order: 1,
    total_supply: 15e8,
    hidden: true
  },
  {
    name: "Shiba Inu",
    symbol: "SHIB",
    decimals: 18,
    l2_token_address: "0x24550d2debf464e95eb22639db0a9a46bcf2dda78a39a6bfbc7311a8297718",
    sort_order: 1,
    total_supply: 999982362230839,
    hidden: true
  },
  {
    name: "Lamiya",
    symbol: "LAM",
    decimals: 18,
    l2_token_address: "0x5a1f532547c374e513e9c78d4030b11e07fdb3b0fc19c9c8ae83952bade5ff3",
    sort_order: 1,
    total_supply: 444444444,
    hidden: true
  },
  {
    name: "deneme",
    symbol: "dnm",
    decimals: 18,
    l2_token_address: "0x596bc6ee3c8185bf5023fe6ffb1ec2373bcc7de2b5b8bc5bc1003ac43ff4730",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Perro de Presa Canario",
    symbol: "PRESA",
    decimals: 18,
    l2_token_address: "0x498217d66d866ee436014b6b04f969c59f3aea9f1e3a14d3899aae3337759dc",
    sort_order: 1,
    total_supply: 210000012,
    hidden: true
  },
  {
    name: "Brussels",
    symbol: "BXL",
    decimals: 18,
    l2_token_address: "0x70f6a4cdc26f44426784e41550a3989e9e6afeb7a028c9861f354fa8e7a0436",
    sort_order: 1,
    total_supply: 11e6,
    hidden: true
  },
  {
    name: "Zariha Choah",
    symbol: "zcv",
    decimals: 18,
    l2_token_address: "0x15dec8882ab81b286fb942cff78b6b418dcead46691549e2e3578c9814626c0",
    sort_order: 1,
    total_supply: 2777193195480,
    hidden: true
  },
  {
    name: "BBL Drizzy",
    symbol: "$BBL",
    decimals: 18,
    l2_token_address: "0xb85c4ff45167c89033105b797bd75ff1d605f13ecb6ee95b52fcbe596f354d",
    sort_order: 1,
    total_supply: 6666666666,
    hidden: true
  },
  {
    name: "OmniDog",
    symbol: "OMNI",
    decimals: 18,
    l2_token_address: "0x12137d3fc8a67d3d9ba6ab3c7ffa1780866d5670c6f39bb366c54ae006bff5b",
    sort_order: 1,
    total_supply: 42e13,
    hidden: true
  },
  {
    name: "Book of Memes on Stark",
    symbol: "BOMES",
    decimals: 18,
    l2_token_address: "0x5b75079c424974f8350337d1fda4614e954a3e82e58352a328644e9a744e93c",
    sort_order: 1,
    total_supply: 69696969696,
    hidden: true
  },
  {
    name: "CAIHONGM",
    symbol: "CHM",
    decimals: 18,
    l2_token_address: "0x3c2f1e4d662e127c351097bc9c7e8af572d5bfed9ec71b826d6a405fc39ca6c",
    sort_order: 1,
    total_supply: 2222222333,
    hidden: true
  },
  {
    name: "London Ssidon",
    symbol: "LSD",
    decimals: 18,
    l2_token_address: "0x20db47afdf7ffabf54f17b40998725d00a81ee3a94db3350ca14c5112435061",
    sort_order: 1,
    total_supply: 496992509,
    hidden: true
  },
  {
    name: "Dos Kolpas",
    symbol: "DKS",
    decimals: 18,
    l2_token_address: "0x3cf761dbeefa30e922164d3e52d586af79e4e6dda356bec268f11ba2751e9fc",
    sort_order: 1,
    total_supply: 46379764527,
    hidden: true
  },
  {
    name: "Bolt Cat",
    symbol: "BOLTCAT",
    decimals: 18,
    l2_token_address: "0x16c56204f8943c372375febfdcedc92088fc7b87a6280a91e3ce02211877f2e",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "GOGOAD",
    symbol: "GOD",
    decimals: 18,
    l2_token_address: "0x733b20baffaee151fce761f69a88eede360986df5222ff79b1ceee5d702504e",
    sort_order: 1,
    total_supply: 5685949653,
    hidden: true
  },
  {
    name: "This Place Sucks",
    symbol: "TPS",
    decimals: 18,
    l2_token_address: "0x72ad4195b783b56a41b428f864d96f727cce59127874fbb4b2bce1d70b28aad",
    sort_order: 1,
    total_supply: 5,
    hidden: true
  },
  {
    name: "UnruggableSDK",
    symbol: "SDK",
    decimals: 18,
    l2_token_address: "0x5f7995ea458142135c6d8731a73aa75f46ebba9fc3af4160adc41a0422f8958",
    sort_order: 1,
    total_supply: 1e4,
    hidden: true
  },
  {
    name: "Shimi",
    symbol: "SHIMI",
    decimals: 18,
    l2_token_address: "0x4dc9aaa0064c3a562d8e7eabcde97bfbce77b518b709d46015b22161f49d17",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "MariyaNiha1",
    symbol: "MN1",
    decimals: 18,
    l2_token_address: "0xd71403844aac40e44fcb077010a3d17973290e33d83e3f230d50f61f630ea8",
    sort_order: 1,
    total_supply: 133422837,
    hidden: true
  },
  {
    name: "ZKS",
    symbol: "ZKS",
    decimals: 18,
    l2_token_address: "0x7f2b6b2d22232cb6fdd4cf137b660150e514534d80707e19bcf0d28ca65ee59",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "TestSpeed",
    symbol: "Speed",
    decimals: 18,
    l2_token_address: "0x488d3ddac913ec42f3e32a43bed2e0b51e7629fd5f2bc3b36c62184161bff00",
    sort_order: 1,
    total_supply: 1e3,
    hidden: true
  },
  {
    name: "teamshimi",
    symbol: "$wednsday",
    decimals: 18,
    l2_token_address: "0x546b5d69c8a7f45d18e2220e90c9850be0dfc89111586cc269cc3c1aec4a302",
    sort_order: 1,
    total_supply: 10,
    hidden: true
  },
  {
    name: "Shrink",
    symbol: "SHR",
    decimals: 18,
    l2_token_address: "0x43689cfc66944909f249f0ea1cf9ff683dbd5ef40918085e8c88a9a31e3b0ff",
    sort_order: 1,
    total_supply: 24,
    hidden: true
  },
  {
    name: "BabyCario",
    symbol: "cro",
    decimals: 18,
    l2_token_address: "0x4ee423b1f89f1ac011d6f2f05c1eb35d61594b801122771cce7747718aa416f",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/0c87afdd-fbae-4eb4-43ce-83857ef0c500/logo"
  },
  {
    name: "STARKNET DAUNI",
    symbol: "DDDD",
    decimals: 18,
    l2_token_address: "0x571e167d97d8870865d16f8b41f8603d60027ab2bc3c5ab4b09ee1d2c7ba65c",
    sort_order: 1,
    total_supply: 1e5,
    hidden: true
  },
  {
    name: "DegenMaxi",
    symbol: "DMAX",
    decimals: 18,
    l2_token_address: "0x373947e49332c0414928d75246383ec13fbe4b085cf8e1d34c40092d644d931",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "STONED TOE TONY",
    symbol: "STOENY",
    decimals: 18,
    l2_token_address: "0x6e2c03e31f6cda18484ed035c02a25d6075393e11da24c898337f736716acae",
    sort_order: 1,
    total_supply: 42e10,
    hidden: true
  },
  {
    name: "I didn't meme it",
    symbol: "IDMI",
    decimals: 18,
    l2_token_address: "0x51cdfae7c5bfe0a70d8af0972b76d4f5e93a38ffc16e670dc1a7697d21362eb",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Nostra Staked STRK",
    symbol: "nstSTRK",
    decimals: 18,
    l2_token_address: "0x4619e9ce4109590219c5263787050726be63382148538f3f936c22aa87d2fc2",
    sort_order: 1,
    total_supply: 8847115,
    hidden: true
  },
  {
    name: "LayerzeroSwap",
    symbol: "ZROSWAP",
    decimals: 18,
    l2_token_address: "0x1762cbb83f4f152b70f1c6da2d045b6b8e92472101e132cda8d161bc0228897",
    sort_order: 1,
    total_supply: 1e5,
    hidden: true
  },
  {
    name: "Hossain",
    symbol: "HSS",
    decimals: 18,
    l2_token_address: "0x739c3f9a20ab2b412ae174048eff38a5ab50a497c35a14330c639e4c183dd72",
    sort_order: 1,
    total_supply: 7777777771,
    hidden: true
  },
  {
    name: "Baby Slinky",
    symbol: "BABYSLINK",
    decimals: 18,
    l2_token_address: "0x231ca6f1f570fb5ee8356faa4545e6cd16096a8c88f7aa77e38defe11f21540",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "XIEDIDISS",
    symbol: "DISS",
    decimals: 18,
    l2_token_address: "0x55daa2cd64401710f214c197d965eb006f7036a5df3512143032a4bf5a4e2d4",
    sort_order: 1,
    total_supply: 21298986823,
    hidden: true
  },
  {
    name: "Sasson's",
    symbol: "KRAR",
    decimals: 18,
    l2_token_address: "0x6494762974ac0dd62ba76d582c546f25e9b2eb9220cbce9d68fb6b21f9e131e",
    sort_order: 1,
    total_supply: 1e3,
    hidden: true
  },
  {
    name: "Inconceivable",
    symbol: "INCON",
    decimals: 18,
    l2_token_address: "0x1bed2f279616a500dccab1ae7a2f7455851b531924ef393aa0d033b88bd722",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Obuke Protomeme",
    symbol: "OBUKE",
    decimals: 18,
    l2_token_address: "0x2f273aa4c28a417ca4119b1f8c71286a5bb764962964d7df3f9e665d78c37d1",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "Caklvo Naya",
    symbol: "CNA",
    decimals: 18,
    l2_token_address: "0x18883ff16964cc9da17905165aaa07f5e72b8a58487e6bec8ace0ac9de2d728",
    sort_order: 1,
    total_supply: 321992943444,
    hidden: true
  },
  {
    name: "WARS",
    symbol: "WARS",
    decimals: 18,
    l2_token_address: "0x15905d4ce047ed501b498964e74d82750c22826db5a38449080e9bf4e4796ca",
    sort_order: 1,
    total_supply: 2999999999,
    hidden: true
  },
  {
    name: "avail",
    symbol: "avail",
    decimals: 18,
    l2_token_address: "0x2ca70e2afa39bbd71c803313cd3b82f108cef1de491c2050f01cc5a1dfa7836",
    sort_order: 1,
    total_supply: 1e14,
    hidden: true
  },
  {
    name: "Nasren Jan",
    symbol: "NAJ",
    decimals: 18,
    l2_token_address: "0x6ac3faa57fc37b2d23e66211ae1642acc1117fa8c34259d89c80b1b27205c37",
    sort_order: 1,
    total_supply: 999999999,
    hidden: true
  },
  {
    name: "Zombie Kangaroo",
    symbol: "ZK",
    decimals: 18,
    l2_token_address: "0x10fe5732595c2ada53d8da8dca4593bfcc76f0cfe7460471d04edf952f7b05c",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Hawk Tuah",
    symbol: "SPIT",
    decimals: 18,
    l2_token_address: "0x6fac96191781af71c51fef6f3486c3f7bb2ab8bbc53cf7411b476f16697bf18",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "SababaShekels",
    symbol: "SABABA",
    decimals: 18,
    l2_token_address: "0x1bb3930086685eea9268054a23afc2657ffcb59ddc57820e3a591dad02f8a81",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "Dogo Argentino",
    symbol: "DOGO",
    decimals: 18,
    l2_token_address: "0xab2b39c0a2e21a7998241f3042cb27aec189556fdc54459de3cb5fa2a7184e",
    sort_order: 1,
    total_supply: 19201956,
    hidden: true
  },
  {
    name: "Starky",
    symbol: "STARKY",
    decimals: 18,
    l2_token_address: "0x1505c5f80c4123b2a02e27cf4afae9463d4c3235170048bf89179b61152ae7",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "STRUMP",
    symbol: "ST",
    decimals: 18,
    l2_token_address: "0x401fe1faa5f79a2b8491577adbae81656398efb488f0fb464c2d827e91ce07a",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Nasam",
    symbol: "NAM",
    decimals: 18,
    l2_token_address: "0x3e9af2c6bdd701b43649796fea3a1f0b93f0999c1a81566b76ee36d5e28fb3f",
    sort_order: 1,
    total_supply: 6e6,
    hidden: true
  },
  {
    name: "secret society",
    symbol: "SCC",
    decimals: 18,
    l2_token_address: "0x440f07bd76b39f2ff2820e80c0927e7b6dd6393cbcbd1747c4f43870b666b67",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Gold(berg)Coin",
    symbol: "Gold",
    decimals: 18,
    l2_token_address: "0x65681ca1adc06cad4f0f45fee28a037c628ccc773c46f41628d47f999c0a911",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Unruggable3",
    symbol: "MEME3",
    decimals: 18,
    l2_token_address: "0x6633f31e9f82e876b207d398fff3e24946609195dfa06b1f5afebfff9f84153",
    sort_order: 1,
    total_supply: 1e4,
    hidden: true
  },
  {
    name: "Nooice",
    symbol: "NOCE",
    decimals: 18,
    l2_token_address: "0xe3d0960041bab289a3d4982d7b0e23b25a19c8db8f721b0e69c079206829c9",
    sort_order: 1,
    total_supply: 3e7,
    hidden: true
  },
  {
    name: "Ali Bin-Salman",
    symbol: "ALI",
    decimals: 18,
    l2_token_address: "0x404db4439034e1da8b2856133cee71ae98f71a2fb849f9171656175b5b61061",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "NATCOIN",
    symbol: "NAT",
    decimals: 18,
    l2_token_address: "0x6a35bf4456d4f302d81266faf0b794d3292a6d2dec4841c3c134b9341f974e0",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Friend",
    symbol: "FED",
    decimals: 18,
    l2_token_address: "0x1ad0835fcd4f6e3ae4e4825b21038647bb00c406c9f0b3df8e7f27ed02a0ec6",
    sort_order: 1,
    total_supply: 683048124553,
    hidden: true
  },
  {
    name: "Capybara",
    symbol: "Capy",
    decimals: 18,
    l2_token_address: "0x782ed91f33041f35df55feca2f09b23ff9eafd41f05706ffb4901af37476828",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "BITCOIN TO THA MOOON",
    symbol: "BTTM",
    decimals: 18,
    l2_token_address: "0x8d3c594752646a4e0c0256a52960b371dde5c32928b2c8a1eb76335d05de27",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "GO FISH",
    symbol: "FISH",
    decimals: 18,
    l2_token_address: "0x270b2be1022e901827cee0d2213cf34ae23b7724f8b41aebd1cb8cb315fbd8c",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "ShibAnubis",
    symbol: "SHIBANU",
    decimals: 18,
    l2_token_address: "0x74d22f7c0cfe8180a27ec7db6aab9cbac658313f739a8bf29896597f6b7daed",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Working_From_Office",
    symbol: "WFO",
    decimals: 18,
    l2_token_address: "0x29009b14dc1cb10ead13e5a733727946e3be0782ef8dfc15f4987343030541f",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Putero de Epoca",
    symbol: "PE",
    decimals: 18,
    l2_token_address: "0x3728f11c5ae0ef08bf5460c78c810582a4bcf07c15f885410ff51446c0c7be5",
    sort_order: 1,
    total_supply: 1e5,
    hidden: true
  },
  {
    name: "Unruggable",
    symbol: "UR",
    decimals: 18,
    l2_token_address: "0x28fb5103c03172c4d45d83412d512641e34561bc7ba6b29e819d0a673860a10",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "RICHMAN",
    symbol: "RICH",
    decimals: 18,
    l2_token_address: "0x9b54c41ee003edcbc5507c98316ec2e5430c6fcb3139529e92683862ddbd40",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/51a18975-4c02-4137-626e-34b355e0ff00/logo"
  },
  {
    name: "Momo",
    symbol: "MOMO",
    decimals: 18,
    l2_token_address: "0x2f9877ac5eef6798194f585749dc09939b7db5b4bfc8c98d0584fefbe391b01",
    sort_order: 1,
    total_supply: 1e13,
    hidden: true
  },
  {
    name: "PabloCoin",
    symbol: "PAB",
    decimals: 18,
    l2_token_address: "0x4a86a4fce448fba44616e05b45ffe0c0e3ba08969b1eeb2eec40ca890e18da8",
    sort_order: 1,
    total_supply: 3e10,
    hidden: true
  },
  {
    name: "DumpCoin",
    symbol: "DUMP",
    decimals: 18,
    l2_token_address: "0x4807b28cab0f43f6c81f1b0500c016a4a98830401728133b590fdf95205b7cc",
    sort_order: 1,
    total_supply: 1e15,
    hidden: true
  },
  {
    name: "Struknet",
    symbol: "STRUK",
    decimals: 18,
    l2_token_address: "0x2ad9abb73d998d92405b6c3fef6097cfff2667d22b8c8111d0037de88d72af1",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Wojak",
    symbol: "Wojak",
    decimals: 18,
    l2_token_address: "0x22c873c3cc5bd288c0006929fe27ebd04085a1b90cd7e188b2cb0ca069781f7",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "UwU",
    symbol: "UWU",
    decimals: 18,
    l2_token_address: "0x64e16e284c3be3496468f55a83a20ba5a5c85024da3d823370c2d1b18031fb6",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "wope",
    symbol: "WOPE",
    decimals: 18,
    l2_token_address: "0x4eb7c6f24af9500de02536d1c15b80d839f36ac72eda95697b98be35d987384",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Nerd",
    symbol: "Nerd",
    decimals: 18,
    l2_token_address: "0x6ffd08d8eb0c52deb4ecf484c06120c99fd2de8e240c99762708360f8c929d7",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "scamnet",
    symbol: "scamnet",
    decimals: 18,
    l2_token_address: "0x3a9cbc27296ee7750bdb3fd315fef271cab01ef8bcf332268f84bf557d96ec",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "PEPE",
    symbol: "PEPE",
    decimals: 18,
    l2_token_address: "0x9015e5f8410d049095c93ac215038966bc7b1960166be684c63ed6a062a81a",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "SCRUFFY",
    symbol: "SCRF",
    decimals: 18,
    l2_token_address: "0x4d7e6dbf1a6c08538cb31a938ba3c94155ce940f23d6c9a04f8dce75342f417",
    sort_order: 1,
    total_supply: 1e13,
    hidden: true
  },
  {
    name: "CorinaHayner",
    symbol: "CHR",
    decimals: 18,
    l2_token_address: "0x7dfad59e2ce897482fa2913f196e9af94bb75c3cc62559f6d47dd87711b2895",
    sort_order: 1,
    total_supply: 438613347312,
    hidden: true
  },
  {
    name: "Jasamn",
    symbol: "JMN",
    decimals: 18,
    l2_token_address: "0x66ee08cf04ced395e6fa324a8109ab8f4e79010e8a5a56a379bcb4e949c1140",
    sort_order: 1,
    total_supply: 48265937259,
    hidden: true
  },
  {
    name: "gm",
    symbol: "gm",
    decimals: 18,
    l2_token_address: "0x5957aea62cd302cd736d2decd578cdf5095bcac294353b0ab3dc521fced4f32",
    sort_order: 1,
    total_supply: 1e16,
    hidden: true
  },
  {
    name: "Punk",
    symbol: "PUNK",
    decimals: 18,
    l2_token_address: "0x26e0852e1de834db3858b644270c52c4e0cab5be1da710751711c11b74eefed",
    sort_order: 1,
    total_supply: 139999999999999,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/5e8bfd68-f84a-450a-cced-771171bff800/logo"
  },
  {
    name: "DRAGON BALL",
    symbol: "DRAB",
    decimals: 18,
    l2_token_address: "0x2a3c4db12911f4acc6480f45928c2858e6517ec77d15a9709bc727af5f26e1b",
    sort_order: 1,
    total_supply: 1e15,
    hidden: true
  },
  {
    name: "rhger",
    symbol: "trhzeg",
    decimals: 18,
    l2_token_address: "0x12959acb518ec8ed045f4277d687828ca6e1b2bac310bec19b2a42e351bfdeb",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "SON GOKU",
    symbol: "GOKU",
    decimals: 18,
    l2_token_address: "0x5908a959cb0de5f2bce33e60e998742a17ebe663fd39ddf7377cb37d8925fa",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "The Roman War Dog",
    symbol: "CANE",
    decimals: 18,
    l2_token_address: "0xf5f982426849acf919d257b535635330b2d6b8dac9f822656fcab47a9671fa",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "UNRUGGBLESTARKDOGGYINU",
    symbol: "USDI",
    decimals: 18,
    l2_token_address: "0x5848244e6ef91a9599d21bdb9d60a6e8b8fd71289794f48965b376e8f3acb6a",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Tony",
    symbol: "Tony",
    decimals: 18,
    l2_token_address: "0x6e93bee3b8fe29713292a0d137af8d1ba0f0751573a6663f84c4c620ee5c66c",
    sort_order: 1,
    total_supply: 1e13,
    hidden: true
  },
  {
    name: "ONLY EIGHT USERS ON SCROLL",
    symbol: "8SCROLL",
    decimals: 18,
    l2_token_address: "0x43a7d8dd51051f9e9db84543fd529db6feab974713b41338da9326b663ee57c",
    sort_order: 1,
    total_supply: 88888888,
    hidden: true
  },
  {
    name: "krats",
    symbol: "krats",
    decimals: 18,
    l2_token_address: "0x6d593420479471d5b8853e527af756be258eaf88bd52b53b35351c2b519b8b5",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Unruggable",
    symbol: "MEME",
    decimals: 18,
    l2_token_address: "0x70fc9a142adea087abe3761ffceb4885c36223c12d80bc2235e01a60fd1285b",
    sort_order: 1,
    total_supply: 1e4,
    hidden: true
  },
  {
    name: "DOGINUAI",
    symbol: "BITCOIN",
    decimals: 18,
    l2_token_address: "0x53b198a26392efb8ee0c4b75a90739478beec0b84ca9bfde090e506efb12935",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "STARKBULL",
    symbol: "STBULL",
    decimals: 18,
    l2_token_address: "0x6a541d142645c5b3c092377deff902bb35f1c6217164cdd2eb71ed7c75889a8",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/3c64e210-206e-4a05-fae4-519f0800fb00/logo"
  },
  {
    name: "Stark Bitcoin",
    symbol: "SBTC",
    decimals: 18,
    l2_token_address: "0xa0f5e30bd4749dbbf51bf6d7bcfddd28c9c770defedd7612cfb3fa849e050e",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "TRUMP",
    symbol: "TRUMP",
    decimals: 18,
    l2_token_address: "0x3c5ad50167f21a51e07d26db7353cfbd8eae77754542e7c8a306b010fe82ffa",
    sort_order: 1,
    total_supply: 5e9,
    hidden: true
  },
  {
    name: "ZETA",
    symbol: "ZETA",
    decimals: 18,
    l2_token_address: "0x718917d4e79c50f886fea3738ae5c48b1f16c36a03b2c683e314c22e5418fb7",
    sort_order: 1,
    total_supply: 1e4,
    hidden: true
  },
  {
    name: "STA[V]RK",
    symbol: "AAA",
    decimals: 18,
    l2_token_address: "0x4a11affba2c65a201b63049e208629c31ec0320bf1b5f6657efc228fadf361c",
    sort_order: 1,
    total_supply: 1e3,
    hidden: true
  },
  {
    name: "Banana",
    symbol: "BANANA",
    decimals: 18,
    l2_token_address: "0x67bd8f64ddf28f55f42c568c415c1e137d9da8c60e007e417487cf005cdf411",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/c76e1128-0938-420d-baaa-2fb9da454000/logo"
  },
  {
    name: "doge",
    symbol: "doge",
    decimals: 18,
    l2_token_address: "0x1d566afa6e05e41368f6befcdda6cf03c6be95b3a79439ae834ab1c740729aa",
    sort_order: 1,
    total_supply: 1e20,
    hidden: true
  },
  {
    name: "Starkshib",
    symbol: "Sshib",
    decimals: 18,
    l2_token_address: "0x4818c7552614091b41eef33c909d6cf38a0d31368c90bc60ebbc401682d21ea",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "RugMe",
    symbol: "RugMe",
    decimals: 18,
    l2_token_address: "0x2e16de4e546c6a5db7752d4d37333397e878af1b991ebcac18c12f645b1faa8",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "ANGRY",
    symbol: "ANGRY",
    decimals: 18,
    l2_token_address: "0x7eca25abb06cabebb1cb1ca3d7bd89d10903d3bee2e1290cbfc9e4177e50b50",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "StarkDoge",
    symbol: "SDOGE",
    decimals: 18,
    l2_token_address: "0x4f812d4c2284675fdb8fbe95c1b8c3aca2c4b8254597f7eba642c18ca845f0e",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Starknet Bolt",
    symbol: "BOLT",
    decimals: 18,
    l2_token_address: "0x4a6859b26d63e169531558e278cc8ebdf3ca3939c5a096094713b024f658e57",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/22f8f2cf-aea8-4fe7-ee9e-0d802a05af00/logo"
  },
  {
    name: "Frieza",
    symbol: "FRZ",
    decimals: 18,
    l2_token_address: "0x7afaade86227ae5cae4fe4ee906f8c2f31aa22bfec7adda23990a4a567fa706",
    sort_order: 1,
    total_supply: 1e3,
    hidden: true
  },
  {
    name: "Dafna",
    symbol: "*-:",
    decimals: 18,
    l2_token_address: "0x27a85047f5ce29f16cf99543a028ec0b88b0e9c9924bf3238e8a496792634c7",
    sort_order: 1,
    total_supply: 1,
    hidden: true
  },
  {
    name: "Only 8 Coin",
    symbol: "8COIN",
    decimals: 18,
    l2_token_address: "0xf2bf1759106be30cf33b350bac9a4026ef7d327bb72f64c9264021c96d7823",
    sort_order: 1,
    total_supply: 8,
    hidden: true
  },
  {
    name: "Masuma",
    symbol: "MMA",
    decimals: 18,
    l2_token_address: "0x46d670ae98dcb6371a490addf3b4cecb4e3fcbaaef02f1be4e034a4918125a5",
    sort_order: 1,
    total_supply: 66666678,
    hidden: true
  },
  {
    name: "DAILY TEN TX",
    symbol: "TEN",
    decimals: 18,
    l2_token_address: "0x7a79bb5ed85e9b1534610adf427a1e98e89e95a25fefb6c18f0ecd2bcf1b13b",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "UEA",
    symbol: "UEA",
    decimals: 18,
    l2_token_address: "0x1ec829a0492c18f1ca371ce14bbc903c98e46d8123802e229f7daf736847d59",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "AnimCoin",
    symbol: "ANiM",
    decimals: 18,
    l2_token_address: "0x7da402120e527fa3080872f5d3808bd2414e0b7d6eb151174f18b1a3b6e7a09",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "dwpxkcfveqy",
    symbol: "DWP",
    decimals: 18,
    l2_token_address: "0x152f52f701d7042045d8fccb45aaafddc8338e6fdcea62be8fc3c466596a310",
    sort_order: 1,
    total_supply: 1376974542,
    hidden: true
  },
  {
    name: "gemstrak",
    symbol: "gems",
    decimals: 18,
    l2_token_address: "0x4c221e49d608102708af232fbb9645cc9228fa47b3ee2fb043fb21205ae84db",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "STROK",
    symbol: "STROKNET",
    decimals: 18,
    l2_token_address: "0x2b7e3e439f09a08b37ac8f444e53d7d2a777ea80be520ae4a798e9ec8ecd180",
    sort_order: 1,
    total_supply: 69e6,
    hidden: true
  },
  {
    name: "2nd try",
    symbol: "Woooooo",
    decimals: 18,
    l2_token_address: "0x20035820d1cc59cb937803f7b59780d4c9e1dac9354db3437abb4a804b32d0",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "4 cats",
    symbol: "4cats",
    decimals: 18,
    l2_token_address: "0x393e53d93f598d4cd78a8180a724b4546c531209aec354b965b83e06a80d0bb",
    sort_order: 1,
    total_supply: 1e4,
    hidden: true
  },
  {
    name: "Wolf Dog",
    symbol: "WOLFDOG",
    decimals: 18,
    l2_token_address: "0xafe31370a1e8c3a1e63d044a37637940c1514734419f9d8b3cbda3e9173bf6",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "Wengou",
    symbol: "Weng",
    decimals: 18,
    l2_token_address: "0x1d14bc74d00fa8e807be2c5066b04c9eaedacedc4063bc7f3a9208e68ab11e5",
    sort_order: 1,
    total_supply: 6666666,
    hidden: true
  },
  {
    name: "wubnet",
    symbol: "wubnet",
    decimals: 18,
    l2_token_address: "0x60f167513dd4288f53fac18ec344bafbd11d4bb5ca77eb31f00b9ba77951581",
    sort_order: 1,
    total_supply: 1e13,
    hidden: true
  },
  {
    name: "StrkDropV2",
    symbol: "V2",
    decimals: 18,
    l2_token_address: "0x3acb2889b69e0f43bd17405c867f0659e412af147bbb401b4c747005c3fa129",
    sort_order: 1,
    total_supply: 4e8,
    hidden: true
  },
  {
    name: "Wolf Wif Cat",
    symbol: "WWC",
    decimals: 18,
    l2_token_address: "0x52f835dd0bf81651b51fc7e31bd672dbf8c5e78fc72dc11c43f1061c1445fca",
    sort_order: 1,
    total_supply: 10,
    hidden: true
  },
  {
    name: "0.005 Etherum",
    symbol: "0.005",
    decimals: 18,
    l2_token_address: "0x6283b46bd32dbe3397ae55381b86c65e405fbeb12e7f3b4ea2433b8a0f7589f",
    sort_order: 1,
    total_supply: 42e7,
    hidden: true
  },
  {
    name: "SithSwap",
    symbol: "SITH",
    decimals: 18,
    l2_token_address: "0x6fedc8e5246e38e06c08c2e784e6a46eb2ae69433be04fe911f5726cb96b67e",
    sort_order: 1,
    total_supply: 36611101,
    hidden: true
  },
  {
    name: "Flexium",
    symbol: "FLX",
    decimals: 18,
    l2_token_address: "0x13bcdb9dc28e32acaa2461d44210952aa2693fc7c323ac4d543f865bac6a3bc",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Ington",
    symbol: "IGN",
    decimals: 18,
    l2_token_address: "0x187553145dbbdad0832ecb1d461660a80edfb77730b4fe322bc689b29af5881",
    sort_order: 1,
    total_supply: 539368443,
    hidden: true
  },
  {
    name: "Xerantia Forest",
    symbol: "TREES",
    decimals: 18,
    l2_token_address: "0x39101e5e1ceea589d7bad59730fada70ba5be980dc35178a91bcde31337b9c9",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Stark Neiro",
    symbol: "NEIRO",
    decimals: 18,
    l2_token_address: "0x4b2a69d4ab1f71eaa11f0efcaf6c4c6ac35f7348ad6698b6390e76adf691167",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "NasamHabiba",
    symbol: "NHB",
    decimals: 18,
    l2_token_address: "0x48cb30c7b51912bd1b8560cfc580fef6620e3fd9685a8c5b563e4b2ca6481",
    sort_order: 1,
    total_supply: 2e6,
    hidden: true
  },
  {
    name: "Ramsey Abed Ramsey",
    symbol: "BOSGA",
    decimals: 18,
    l2_token_address: "0x39b5fb2d6e622fed00c007038a171a0614af4cf654d1e967e0e2792f2d4518",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Beggar",
    symbol: "Begg",
    decimals: 18,
    l2_token_address: "0x50ed99be7815234c8e00ef67bf32040dbd9f95b100b85a6ed4e7505c611d6c9",
    sort_order: 1,
    total_supply: 420699669969,
    hidden: true
  },
  {
    name: "toomanyalons",
    symbol: "toomany",
    decimals: 18,
    l2_token_address: "0x54ed1c70164eb130f23e293f953ba76cc60323efa9611e0eec40e28202d74ae",
    sort_order: 1,
    total_supply: 1e5,
    hidden: true
  },
  {
    name: "E Z R A",
    symbol: "EZRA",
    decimals: 18,
    l2_token_address: "0x1ae0573be817a18f137cde12bf141b2c4393bff8c69fdaca9f5e1566369cd64",
    sort_order: 1,
    total_supply: 211211211211,
    hidden: true
  },
  {
    name: "Nipa Akter",
    symbol: "NPA",
    decimals: 18,
    l2_token_address: "0x2172e7855b0ef1c63e51fca915ad1691a259c0cd47e381893d0ca7cbb42a1fe",
    sort_order: 1,
    total_supply: 33333333332,
    hidden: true
  },
  {
    name: "Unrugme",
    symbol: "URUG",
    decimals: 18,
    l2_token_address: "0x61af9cad67a30745083f18c92432f3fbf21a676925b39310b4d34b6f6053639",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "ehrth",
    symbol: "rtjrtj",
    decimals: 18,
    l2_token_address: "0x54815ca58000e9a931197ab325a0aa8970e73efcf9accd5282ee36b7e624fec",
    sort_order: 1,
    total_supply: 6999999999999,
    hidden: true
  },
  {
    name: "shitnow",
    symbol: "SHIT",
    decimals: 18,
    l2_token_address: "0x7a526c605428dc48f12b8d47e4ceb203f9f858338c15206cb64bab00ab56a8a",
    sort_order: 1,
    total_supply: 192591560855157500,
    hidden: true
  },
  {
    name: "starkcat",
    symbol: "stcat",
    decimals: 18,
    l2_token_address: "0x2f6ff8d280da4176d4acbf994da0a53e4f125f7494ac9a8dc78cdba7277f191",
    sort_order: 1,
    total_supply: 1e5,
    hidden: true
  },
  {
    name: "Dollie",
    symbol: "DLL",
    decimals: 18,
    l2_token_address: "0x7cd39417f0a23f22478529bf1be103fd2ccb80a213161e9aefa6b6174687170",
    sort_order: 1,
    total_supply: 5555555555,
    hidden: true
  },
  {
    name: "Black",
    symbol: "BLACK",
    decimals: 18,
    l2_token_address: "0x3a6ec0b0ea7a1903329d5dec4bb574ecb4d6fdc206664e1c61eeded8158ab40",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/02e6783d-52b2-48e5-3774-3fe64f72db00/logo"
  },
  {
    name: "PACCAN Cash",
    symbol: "PACCAN",
    decimals: 18,
    l2_token_address: "0x6e141a7f86d9ce6cb5f4dd05cd05cae712ee0354425b84bbe6523b9f1b53093",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Kodrat",
    symbol: "KDT",
    decimals: 18,
    l2_token_address: "0x41ef37cf4ee113e835c0265384af2813525d55d7fcb5c60e0c8c6fce49eca15",
    sort_order: 1,
    total_supply: 1862486662,
    hidden: true
  },
  {
    name: "Best Currency Ever",
    symbol: "BCE",
    decimals: 18,
    l2_token_address: "0x2852491f4e347ef05b393d2238423e9dc10d70ae30410e3358ccf7addaf164",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Margarete",
    symbol: "MAG",
    decimals: 18,
    l2_token_address: "0x2cd9c6cda32408b7647057a0001ceba78c6636cc8c9f541b54b3edee9b20f30",
    sort_order: 1,
    total_supply: 4916053394,
    hidden: true
  },
  {
    name: "Unruggable IBS",
    symbol: "UIBS",
    decimals: 18,
    l2_token_address: "0x16ee5f7c7ee9deb5705fdea318381f1cdde8e86c1d5782b9c5520cbc2f034ed",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "AllCopsAreBastardsCoin",
    symbol: "ACAB",
    decimals: 18,
    l2_token_address: "0x4f8f4b0f92b63ba5ebf12467c52460f167c8aa1e202dcbe4c34dd7bde08ae57",
    sort_order: 1,
    total_supply: 1312,
    hidden: true
  },
  {
    name: "TEST2",
    symbol: "TEST2",
    decimals: 18,
    l2_token_address: "0x6105a515cd9b51b1477040217a001e5acc130261e990a13f70c386d1bd51d39",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "HUA",
    symbol: "HUA",
    decimals: 18,
    l2_token_address: "0x798f5f0bc123fe148f6da97d5083562834bdd92ed1824357dcfed140746915",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Chakal",
    symbol: "CHKL",
    decimals: 18,
    l2_token_address: "0x4febfe208ca7ee6a821ff2c1be63e3628ba194a56b2f6c0ef2805908ef83239",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "SCOKS",
    symbol: "SCOKS",
    decimals: 18,
    l2_token_address: "0x53819ba7623a2bd419ad06b54068d53f0ca2878442b59824b36ccba28dc1f5a",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "Pilpel",
    symbol: "PL",
    decimals: 18,
    l2_token_address: "0x4ae68704c1d62342b9a737cc94262724326052bfeff6dcff0a71c03921f582",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "Starknet Cat",
    symbol: "$CAT",
    decimals: 18,
    l2_token_address: "0x7483422af9154c1ce509a205ccfe49f4054f915712338242e56793d76ceb414",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "UNALIGNED",
    symbol: "UNALIGNED",
    decimals: 18,
    l2_token_address: "0x365bdac54c4ce3a7f4a74822e3bd41991e90a2702967293ef49616448c8db55",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Come Back",
    symbol: "CB",
    decimals: 18,
    l2_token_address: "0x3f9ad91ea09e63b4d8f2464fc503e4a9df4d7e12aeeaa23090d139de696efdd",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "There is no meme",
    symbol: "NOMEME",
    decimals: 18,
    l2_token_address: "0x26576be92c8fd5d9e3de71c3e0212c9fea35efb8c1c77486593b6ca03c62e6e",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "StarkBucks",
    symbol: "STB",
    decimals: 18,
    l2_token_address: "0x7b0505e4998ed8467acc24543076426efaa35e5e490672b5d4bfb2e31f1a91e",
    sort_order: 1,
    total_supply: 2e6,
    hidden: true
  },
  {
    name: "vega finance",
    symbol: "VEGA",
    decimals: 18,
    l2_token_address: "0x180e5372efc0f6dbafc014a58b087a3e637443b4a41cfb8d29668610e7968c0",
    sort_order: 1,
    total_supply: 100,
    hidden: true
  },
  {
    name: "Oompa Loompa",
    symbol: "OOMP",
    decimals: 18,
    l2_token_address: "0x7a13b1dc646f44e3759a91373873fd232410ac66308c49970c7f82f7f3b1ee3",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Molosser",
    symbol: "MOL",
    decimals: 18,
    l2_token_address: "0x6a717c94ad61e4cc41df5a5cc0d2492e48d8911a8268dda17a183097894b3bc",
    sort_order: 1,
    total_supply: 8e5,
    hidden: true
  },
  {
    name: "TaroCoin",
    symbol: "TARO",
    decimals: 18,
    l2_token_address: "0x46e35c4d7bdd27c9be8250d98886591aa0a2ed4f683629407ef56c27ae4be55",
    sort_order: 1,
    total_supply: 1e3,
    hidden: true
  },
  {
    name: "The Answer To Life",
    symbol: "FORTYTWO",
    decimals: 18,
    l2_token_address: "0x54d12fcf5d1a7b8afcbf06cf6b75e6c3d5aadd2cd19b706507d73e884b26c8b",
    sort_order: 1,
    total_supply: 42e3,
    hidden: true
  },
  {
    name: "TESTNEW",
    symbol: "TESTNEW",
    decimals: 18,
    l2_token_address: "0x695967645cafcd68a22b720ad3a3fd4da39c94d988fbdc3c7de81824c543e4d",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Caitlyn Jenner",
    symbol: "$JENNER",
    decimals: 18,
    l2_token_address: "0x481eb4d535ef8c4066cfaab40201296798146cfde0c93eff89c5e06c677b313",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "UPTOBER",
    symbol: "UPTOBER",
    decimals: 18,
    l2_token_address: "0x29d5775a713e6480fab0650859bd0cc529557503eab0639c799c8b6fd98a83e",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Bonk",
    symbol: "BONK",
    decimals: 18,
    l2_token_address: "0x781bddf077f25a53411d567093f63f9a839ec9e6bc4d99d9240cf742b98b0e8",
    sort_order: 1,
    total_supply: 93526183276778,
    hidden: true
  },
  {
    name: "DEEZNUTS",
    symbol: "DN",
    decimals: 18,
    l2_token_address: "0x57e350c93a51b7bfb3b80670ecbff2dc1f673eb1ecc82771f44470b4616123",
    sort_order: 1,
    total_supply: 15e8,
    hidden: true
  },
  {
    name: "Starkouille",
    symbol: "STK",
    decimals: 18,
    l2_token_address: "0x3c8896cd73370d5e7121f449047761f093e93125b50d6b86fb171d65b319a5",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "MEDOEDONELOVE",
    symbol: "BBT",
    decimals: 18,
    l2_token_address: "0x36e0f53b460706f7b68adff273bc4c0e307520dd7bbfd64c029749f0e8701c3",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Starknet Voting",
    symbol: "vSTRK",
    decimals: 18,
    l2_token_address: "0x782f0ddca11d9950bc3220e35ac82cf868778edb67a5e58b39838544bc4cd0f",
    sort_order: 1,
    total_supply: 1680532,
    hidden: true
  },
  {
    name: "Nimbora Pendle Staked Eth",
    symbol: "npstETH",
    decimals: 18,
    l2_token_address: "0x2f66b5ae3425bec510c8a29b4f6af92923d916acf1e1aebd82cbc4ead6c057f",
    sort_order: 1,
    total_supply: 1,
    hidden: true
  },
  {
    name: "Book of Memes",
    symbol: "BOME",
    decimals: 18,
    l2_token_address: "0x23c57ed8dde5d9ec502365284dfa785643e6a619aaec296cdfa2327cb50177d",
    sort_order: 1,
    total_supply: 69696969696,
    hidden: true
  },
  {
    name: "Swiss Cheese",
    symbol: "SWIC",
    decimals: 18,
    l2_token_address: "0x71a3b7cfe9c587ec486c4a5283af43ea7c8e9bad0cd0e350dee6843236294d5",
    sort_order: 1,
    total_supply: 1e5,
    hidden: true
  },
  {
    name: "CAT-019",
    symbol: "01999J",
    decimals: 18,
    l2_token_address: "0x310880eb405b53c67a25596fe337f7e33f6d55a18b3c38d01f4f380bf5a0198",
    sort_order: 1,
    total_supply: 229260690964994,
    hidden: true
  },
  {
    name: "Stark Corgi",
    symbol: "CORGI",
    decimals: 18,
    l2_token_address: "0x844d2cf792180590334b4ca0ce8bfc3df6fcc780f8b803aef5df37548d2b2",
    sort_order: 1,
    total_supply: 2e9,
    hidden: true
  },
  {
    name: "Runestone",
    symbol: "RUNE",
    decimals: 18,
    l2_token_address: "0x2d52edb66e7ac27c5a8a659b2f575649cbac6c2c9b81b1c395149af6749f732",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "its time",
    symbol: "IT",
    decimals: 18,
    l2_token_address: "0x45e4243185c1cf6a252a3056e8376f2f968b8e3bab0155ea40f5777dac68368",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Aakhi Ash",
    symbol: "AAH",
    decimals: 18,
    l2_token_address: "0x734ef73b9726409224ea2c7bb5aaf639bde19770ff34077a16e9a85ed2d02e2",
    sort_order: 1,
    total_supply: 888888777,
    hidden: true
  },
  {
    name: "SkyrimForNords",
    symbol: "SFN",
    decimals: 18,
    l2_token_address: "0x37c8e7d63fe9e1d08c62b33f86d49df320cf7c666d77403a93f64839216a4e9",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Lorik Endri",
    symbol: "LEI",
    decimals: 18,
    l2_token_address: "0xd65449dc25fe38d9f57f9a853cc6f28f487146d12ee0c2373506c058be388",
    sort_order: 1,
    total_supply: 123456789e3,
    hidden: true
  },
  {
    name: "PonziSchemeCoin",
    symbol: "PONZI",
    decimals: 18,
    l2_token_address: "0x25363f24530607bcf94f81d5512c67070fc2b3f21b6490468a093c5cb596aa0",
    sort_order: 1,
    total_supply: 1e4,
    hidden: true
  },
  {
    name: "Shoko",
    symbol: "\\_/",
    decimals: 18,
    l2_token_address: "0x7ad9d6af4932c1f75f2a275cbd58db98a6541a132b422d430f8302412ea96b4",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Stark Toes",
    symbol: "STOE",
    decimals: 18,
    l2_token_address: "0x1a6d63e9fc51019c2820cc7bdc4e3c66cf9adaf37898eb8d797b180dee78ed5",
    sort_order: 1,
    total_supply: 8e10,
    hidden: true
  },
  {
    name: "Frogi",
    symbol: "Frogi",
    decimals: 18,
    l2_token_address: "0x76c0d4e6e267a3556416310015cda468b724baa447bcab18d0b72df970eca31",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "Gamatatsu",
    symbol: "GAMA",
    decimals: 18,
    l2_token_address: "0x3b347281792501989f255fd5a6bf21b69da173ea5d7dfea171b13dd4ef1df50",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "worthless",
    symbol: "ESHR",
    decimals: 18,
    l2_token_address: "0x6346e2d3f57209c88b7eaec764cc6de1a96220835b25a4dc569249c7ef8a731",
    sort_order: 1,
    total_supply: 1e4,
    hidden: true
  },
  {
    name: "Bitcoin Halving",
    symbol: "HALVING",
    decimals: 18,
    l2_token_address: "0x521037960b6b2e0cf11f2aa04d47e5bb7872cb4deefdd00f46611d29f18ff9c",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "el-retardos",
    symbol: "RETARD",
    decimals: 18,
    l2_token_address: "0x4d57c83ddbbd82e86852d9c1ab35b3142e889e1c08804f2a546c674648cba6c",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "American Pitbull Terrier",
    symbol: "APBT",
    decimals: 18,
    l2_token_address: "0x1983526deed43c6da8a5da25d42da3752874e31b311bd78efecbceef72d01f1",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Lane",
    symbol: "LEE",
    decimals: 18,
    l2_token_address: "0x3bb54ad86d8b668b363398fab1ec06f51fd5286a1799d0cc0baea5d30a5ae0a",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "Avu",
    symbol: "AVU",
    decimals: 18,
    l2_token_address: "0x2aaa5e8bf3577d8bee2802c2d81309cef88b45d6181b3cd9b106dc3e1267a1f",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "GalS",
    symbol: "GLS",
    decimals: 18,
    l2_token_address: "0x7e7b834589f714c42e2098a6ff2d679e2cc413d5e5d01ed81d0f9dc20c6c394",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "Runes",
    symbol: "RUNES",
    decimals: 18,
    l2_token_address: "0x6f8b528a3e95978be34d508d5340c07169a010a0e7b162d44f350fa4a02f555",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "lets fucking test",
    symbol: "LFT",
    decimals: 18,
    l2_token_address: "0x1e70cfaf6b569d647d6f6d274d198eba4d29f2105f43ebc7688bab1957f6888",
    sort_order: 1,
    total_supply: 10,
    hidden: true
  },
  {
    name: "Mother Slerf",
    symbol: "FCK",
    decimals: 18,
    l2_token_address: "0x65eca8b72f9ceca3252d52c353adb1576fa23506066f7fd023e23a50938eb65",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "iykyk",
    symbol: "iykyk",
    decimals: 18,
    l2_token_address: "0x7b9cce609a2399ccc55881fc81d0290be4cde41a8726e96acc1b05a6daff2e7",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Stark Rocket",
    symbol: "STRKR",
    decimals: 18,
    l2_token_address: "0x30c42f4c0a094ea1eda7e3086056a225a464c43dd7da48bd2083fc3114a4db",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "soso",
    symbol: "soso",
    decimals: 18,
    l2_token_address: "0x34ceb4231e644bd38b1039bb7b6605210a92e8a8da8a18f6ba46cb4d1aa377c",
    sort_order: 1,
    total_supply: 1e17,
    hidden: true
  },
  {
    name: "Gotta Go Fast",
    symbol: "GGF",
    decimals: 18,
    l2_token_address: "0x4b15629904a0fa1efaa83ee0035436b53675faef96a1bbb0a951209b6868363",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "zerghre",
    symbol: "zrhet",
    decimals: 18,
    l2_token_address: "0x6558f62f610be5bb7bd0ef195607e4a745b0feea0730dd4fdd0e2f6830f72cf",
    sort_order: 1,
    total_supply: 267890456781,
    hidden: true
  },
  {
    name: "TrumpWifCat",
    symbol: "TWC",
    decimals: 18,
    l2_token_address: "0x768aef7aea42030cb701e1a97984b8f91b8f306a34bd0d461c466012d720b4b",
    sort_order: 1,
    total_supply: 78e6,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/9458c907-f15f-4d92-29a1-c54296d5e300/logo"
  },
  {
    name: "BULLTOBER",
    symbol: "BULLTOBER",
    decimals: 18,
    l2_token_address: "0xeb0eb2b60b38041c6ffc913e7f56974eb616bd2e244db99d296c3faedb1304",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "STARKi",
    symbol: "STARKi",
    decimals: 18,
    l2_token_address: "0x60c31afc25bbacaa607c6b420c5667011cb529beb89c4d2b1a0a545ffc2c54c",
    sort_order: 1,
    total_supply: 123456789,
    hidden: true
  },
  {
    name: "Polaam Kasam",
    symbol: "PKM",
    decimals: 18,
    l2_token_address: "0x288e33230c9e169220a029e34e1495880b96bb5655450a7d25472c5e8a17d60",
    sort_order: 1,
    total_supply: 622682297,
    hidden: true
  },
  {
    name: "Test 2",
    symbol: "TTTEST",
    decimals: 18,
    l2_token_address: "0x3a862d5d6fb8df1ee983840fd44007277c8c068d43563306c6d2a46493ee6d6",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "Escargot",
    symbol: "Escargot",
    decimals: 18,
    l2_token_address: "0x77779fbdcae5c2b4c321d336f2ccf80c2dd0ada63215901508f63e0b261df60",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "bra",
    symbol: "bra",
    decimals: 18,
    l2_token_address: "0x4461e6fe97d7f7c25c9a98feb2f1ef7f506334958d6a92857034e768fe8dfb",
    sort_order: 1,
    total_supply: 134343423424,
    hidden: true
  },
  {
    name: "xiangjiao",
    symbol: "xjj",
    decimals: 18,
    l2_token_address: "0x5416712ee8c3cb4f501495fa932fb61ae0d658723fd0b4db933398d103a6375",
    sort_order: 1,
    total_supply: 222222222222,
    hidden: true
  },
  {
    name: ".oO",
    symbol: ".oO",
    decimals: 18,
    l2_token_address: "0x6217cc4bdcc6487b6a6dff19a92f740c14694ec8429a224835dbb1d785032fc",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "ZANJI",
    symbol: "ZNJ",
    decimals: 18,
    l2_token_address: "0x5b9ae1b4bef0f8456cb101fdf6a283e3ff25f748c3f59aa68df1dc11f7b2e4c",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "KERO",
    symbol: "KERO",
    decimals: 18,
    l2_token_address: "0x2eaf48d850f05be53c74e90ae9e8c35e76e961a5eec7e07b5cc8b52900db276",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Dogfood",
    symbol: "DGFD",
    decimals: 18,
    l2_token_address: "0xf1c28eea181dbce878a4029de09f493b8ea3b74ff8f58e0264fb835d159346",
    sort_order: 1,
    total_supply: 1,
    hidden: true
  },
  {
    name: "MageName",
    symbol: "MGNM",
    decimals: 18,
    l2_token_address: "0x5a459e01603c9872a848db2009d019109e91552d8378bdcdbd32cf4b489ac24",
    sort_order: 1,
    total_supply: 5e8,
    hidden: true
  },
  {
    name: "Jy Bangladesh",
    symbol: "JBGAY",
    decimals: 18,
    l2_token_address: "0x65c94c7d0fd338dd90f66a23a034b547a98e21df6bb02cd6043ad97f0eb35bb",
    sort_order: 1,
    total_supply: 66203412084295010,
    hidden: true
  },
  {
    name: "Spirit Stone",
    symbol: "SPIST",
    decimals: 18,
    l2_token_address: "0x6182278e63816ff4080ed07d668f991df6773fd13db0ea10971096033411b11",
    sort_order: 1,
    total_supply: 334115e4,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/dc78ea62-c15a-4d90-1464-c5077fca3e00/logo"
  },
  {
    name: "Poor",
    symbol: "POOR",
    decimals: 18,
    l2_token_address: "0x69823f8677ebcc3cc4eb14ff93ccadc4873ec83fbf6afa643ee813f929638bc",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "RatLife",
    symbol: "RAT",
    decimals: 18,
    l2_token_address: "0x2d8ac4be2e1b0456c813669ddec6bb890130bdab958d032635b8524c7842762",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "Superman Amedi",
    symbol: "YMT",
    decimals: 18,
    l2_token_address: "0x1d2bd3ca536696760b17fdab42aeb04e8e481f5dd00bc5de21f96ccda0b723f",
    sort_order: 1,
    total_supply: 1e4,
    hidden: true
  },
  {
    name: "Masuma Habiba",
    symbol: "MSM",
    decimals: 18,
    l2_token_address: "0x64c36febc60bb5b68155e7236517ea92e369374c8c084ac6a0bd84ff272afc6",
    sort_order: 1,
    total_supply: 185989492656635,
    hidden: true
  },
  {
    name: "HAHA",
    symbol: "HAHA",
    decimals: 18,
    l2_token_address: "0x18fdd0a21f76332b4bb63d8d69ca1b2a2b7aa226e50c65867e9cc9ec5b5dbb4",
    sort_order: 1,
    total_supply: 696969696969,
    hidden: true
  },
  {
    name: "Paris",
    symbol: "LuvR",
    decimals: 18,
    l2_token_address: "0x5c6cc8fdd226bbd505b5b1ea99ed00ce8bb8aea15dc752e59c7af0e0815a07d",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Klea Kodrat",
    symbol: "KKA",
    decimals: 18,
    l2_token_address: "0x21d0fdf1e4b9f70c59d7dc8b5546c28fd1840b556588f4ac5bbbaadcb8ab38",
    sort_order: 1,
    total_supply: 999999999999999,
    hidden: true
  },
  {
    name: "GAME STOP",
    symbol: "GAMESTOP",
    decimals: 18,
    l2_token_address: "0x1a14666d319aa89138dfe25b815bc16c537e00b3b85eb670ec1c4dd753963df",
    sort_order: 1,
    total_supply: 1234567890,
    hidden: true
  },
  {
    name: "zkLend Interest-Bearing ZEND",
    symbol: "zZEND",
    decimals: 18,
    l2_token_address: "0x2a28036ec5007c05c5611281a7d740c71a26d0305f7e9a4fa2f751d252a9f0d",
    sort_order: 1,
    total_supply: 305445,
    hidden: true
  },
  {
    name: "TEST3",
    symbol: "TEST3",
    decimals: 18,
    l2_token_address: "0x6f977313b95b14c194193a5f979149122cc3d0882a155b73caef99edc83aa1b",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "BaraCUDA",
    symbol: "CUDA",
    decimals: 18,
    l2_token_address: "0x63db091bccda857a94a801a6bb36f79f541a4420447f5ba83e1a85ed25e83ec",
    sort_order: 1,
    total_supply: 75e9,
    hidden: true
  },
  {
    name: "QQ",
    symbol: "QQ",
    decimals: 18,
    l2_token_address: "0x3e1d5552ef8c4ed018b20f492d3d22c8397413c4e3b21d9a525e9e3d0be45ee",
    sort_order: 1,
    total_supply: 1e3,
    hidden: true
  },
  {
    name: "KENI",
    symbol: "KENI",
    decimals: 18,
    l2_token_address: "0xc6453f8473098a095922faa88699a70dcfc7ca7515f3c551c98aeeea171d54",
    sort_order: 1,
    total_supply: 4206969696969697e3,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/c47a23be-37a4-4479-5577-3ffdcfdfa300/logo"
  },
  {
    name: "Gloves",
    symbol: "GLOVES",
    decimals: 18,
    l2_token_address: "0x17787cbeb746a0a13a2d5fa9e4acbd72a9f724ffd0840a2ff7c0d429d9d5d38",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "s",
    symbol: "JOf",
    decimals: 18,
    l2_token_address: "0x5e0f09be11eef003691d147982a92e5b41186488bf1c902bee5728f835b106c",
    sort_order: 1,
    total_supply: 111111111111,
    hidden: true
  },
  {
    name: "Empire",
    symbol: "EMPR",
    decimals: 18,
    l2_token_address: "0x18b75009c5471c3e942da6bc163af7647fc0c67566814a13d62db077b0b89f2",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Assump' is mom of F ups",
    symbol: "AMFU",
    decimals: 18,
    l2_token_address: "0x5dd7a2d7452c3d0f838ef219cb06c446279bcc6b2300357b7e0865b856e7e98",
    sort_order: 1,
    total_supply: 666,
    hidden: true
  },
  {
    name: "SkippyCoin",
    symbol: "SKPY",
    decimals: 18,
    l2_token_address: "0x52e6b9e785ba04d63d4c82128bd37e6b8569403ccafd4ccbb0ce3503d3b1ca9",
    sort_order: 1,
    total_supply: 1e4,
    hidden: true
  },
  {
    name: "WAFFY",
    symbol: "WFY",
    decimals: 18,
    l2_token_address: "0x4ebc30d86b242eda5f6a834b61d6432cee475c047196736ef86505d5fccbfc8",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "JARVIS",
    symbol: "JAR",
    decimals: 18,
    l2_token_address: "0x130ad3f440b9110b256b4149595540805b2e583799f421e48a470d4146c4fa2",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Rektober",
    symbol: "REKT",
    decimals: 18,
    l2_token_address: "0x19c969c5e8a0d8d590ac2a1ce6274c4545fc3ba8f20e664e659fae1acf93d95",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Cibus",
    symbol: "Cbs",
    decimals: 18,
    l2_token_address: "0x1ca21e68c60bc63c9ec0726f1384d1bbe0e85d829f25e5bdd63825b5ab8c88a",
    sort_order: 1,
    total_supply: 1e3,
    hidden: true
  },
  {
    name: "Dencun",
    symbol: "DCN",
    decimals: 18,
    l2_token_address: "0xe311bc4877f1c1ed98ffd21da2d721154ce5cd2740f001f13a437395784f3c",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "ZENDYED",
    symbol: "ZDZ",
    decimals: 18,
    l2_token_address: "0x79f6e2bdbabd983a094a37176ebe071cfa654276e9889ad0698c2d473eaadae",
    sort_order: 1,
    total_supply: 666666666666,
    hidden: true
  },
  {
    name: "Test au raisins",
    symbol: "TAR",
    decimals: 18,
    l2_token_address: "0x3921b9008871829928ac6f4974a9ece1d311e42b0cfa37fd8e2dc309ba013e4",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Tasnea",
    symbol: "TAA",
    decimals: 18,
    l2_token_address: "0x707d0e3bf5fb7273106704dbc07074df8e950a17603d58e73808d225e50c967",
    sort_order: 1,
    total_supply: 2222111111,
    hidden: true
  },
  {
    name: "autonomousworlds",
    symbol: "AW",
    decimals: 18,
    l2_token_address: "0x15e84de2ff05e7c1bee396cf6b8c5744a525b1904b2d00e237380b9bed0c3a2",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Starknet Village",
    symbol: "Village",
    decimals: 18,
    l2_token_address: "0x44b195a7f7819bbd9f7d77708be33825c3e7894fa42945a388caeeb1213d241",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "carol ebeling",
    symbol: "CEG",
    decimals: 18,
    l2_token_address: "0x4b46f0d3899f8403eb16c7b5e304d7de817f00fbca84260455c117e0eda1020",
    sort_order: 1,
    total_supply: 3321111112,
    hidden: true
  },
  {
    name: "SonicBoomTigerUppercut9000Inu",
    symbol: "STARKNET",
    decimals: 18,
    l2_token_address: "0x783f872e2cfe24b50b2c46f4f3a28bb7135696760953315fc372d1b2eb885a1",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "ChatGPTCoin",
    symbol: "GPT",
    decimals: 18,
    l2_token_address: "0x14cbafac1b8cdc0ddaef182012bc71eef78247f22f726efe763cd2b8b788b1d",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Test au lait",
    symbol: "TAL",
    decimals: 18,
    l2_token_address: "0x21c83bfb451c6acf058a872a924f71139dfd3d25af0979528af3810019c8452",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "ArtPeaceCoin",
    symbol: "APC",
    decimals: 18,
    l2_token_address: "0x2e1a92e32f81a47cbb3f252135f1bb8e7c73159cd94d3e1c00318b15d6b4935",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Hicuc",
    symbol: "HCC",
    decimals: 18,
    l2_token_address: "0x633890b0c91ae8838d95e042e81705b258d813f2ff98d45750a29c9ae57bbe8",
    sort_order: 1,
    total_supply: 98217455682,
    hidden: true
  },
  {
    name: "Cool Meme",
    symbol: "CMT",
    decimals: 18,
    l2_token_address: "0x48b961eb771f7dbbc7479ad72e2b05648c1abf37a0724a6cca5fce14dc933c4",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "STRONK",
    symbol: "STRONK",
    decimals: 18,
    l2_token_address: "0x2b2ba5da3a25d7e0d45487649a9c4535f29711b57b9054099737e7c9f7dc10a",
    sort_order: 1,
    total_supply: 1690120636399962,
    hidden: true
  },
  {
    name: "Akamaru",
    symbol: "AKU",
    decimals: 18,
    l2_token_address: "0x137dfca7d96cdd526d13a63176454f35c691f55837497448fad352643cfe4d4",
    sort_order: 1,
    total_supply: 1e13,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/3a9e7657-41fa-41b4-bb5c-6cb9ce092a00/logo"
  },
  {
    name: "Blye",
    symbol: "BLU",
    decimals: 18,
    l2_token_address: "0x28ff87b5ff61780af76a5f8a1ae0abddbdc4fa79d5a7eb1f754ae1069275daa",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "defispring",
    symbol: "DEFI",
    decimals: 18,
    l2_token_address: "0xab741710120ddc7e2553dc528a30b4d9e59c0b6e728a6be39212449d633411",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Unruggable5",
    symbol: "MEME5",
    decimals: 18,
    l2_token_address: "0x4b215d8d0d444657ac93a446b5d6b38c98d69a994a65579624b0449fa89c41b",
    sort_order: 1,
    total_supply: 1e4,
    hidden: true
  },
  {
    name: "Mahakala",
    symbol: "MHKL",
    decimals: 18,
    l2_token_address: "0x511c07f3a99ca1d6c6055c2a9beeaceebd4b2d6914659080077678e2d53d63",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "PUTEEN",
    symbol: "PUT",
    decimals: 18,
    l2_token_address: "0x3181975c218f3f511c208bb210f79eafc7eb42371d273c95c4f6599210e0625",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "test en vif",
    symbol: "TEV",
    decimals: 18,
    l2_token_address: "0x3c8ca7fe3835b9ccab47ad7fde8a087a12360be5ef839b04522c9db9357fce4",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "CAIRO",
    symbol: "CAIRO",
    decimals: 18,
    l2_token_address: "0x1c5f64a91da5426e7b84e2fbb4ab724781c71cf8312a87d57f2c07c2f0ea021",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "4thepixels",
    symbol: "pixel",
    decimals: 18,
    l2_token_address: "0x52cc6f4c32665aa7071cef8dc198b1d1d796549e0ad2ca28e63cddc814f195e",
    sort_order: 1,
    total_supply: 100,
    hidden: true
  },
  {
    name: "TEST",
    symbol: "TESTT",
    decimals: 18,
    l2_token_address: "0x480674213c15a2c65d52d37390f2c3c26e1c94395f984ba532d28eca8dac3ed",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Elecoin",
    symbol: "ELE",
    decimals: 18,
    l2_token_address: "0x51b6c50edbcb046c7177d17930b16a699182297eeb8be26b4ef17f8106def39",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "people",
    symbol: "people",
    decimals: 18,
    l2_token_address: "0x536bf4c9616a8c7102ff7bdd73d26c15893b6ef78ebcafffd2dc353943422a9",
    sort_order: 1,
    total_supply: 1e17,
    hidden: true
  },
  {
    name: "Mithila Mim",
    symbol: "MMM",
    decimals: 18,
    l2_token_address: "0x7ab663c1bd42f6920e7184d061706339a1a96019a76e11d804caa14669edc62",
    sort_order: 1,
    total_supply: 7777777777,
    hidden: true
  },
  {
    name: "Money Printer Go Brrr",
    symbol: "$BRRR",
    decimals: 18,
    l2_token_address: "0x67737cbee9be3e3042ca86ef41598af7cc36fa6e109193c165b854de07d1df7",
    sort_order: 1,
    total_supply: 4269e8,
    hidden: true
  },
  {
    name: "pompom",
    symbol: "POM",
    decimals: 18,
    l2_token_address: "0x7403c26f5d5072ef89f31e3c942a9a8de50bb11a704af0d7ddb41d88960e96a",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "Feet Enjoyer",
    symbol: "FEET",
    decimals: 18,
    l2_token_address: "0x6e409732749fd6e621039675e3126e66dd2552bc62103e229d75b6d9efca5e7",
    sort_order: 1,
    total_supply: 15e6,
    hidden: true
  },
  {
    name: "WOLF",
    symbol: "WOLF",
    decimals: 18,
    l2_token_address: "0x6906a5273e81b470c3c1b9e4792c32136a210600d5b3a477bf5596cd1c09c30",
    sort_order: 1,
    total_supply: 1e14,
    hidden: true
  },
  {
    name: "LOL",
    symbol: "LOL",
    decimals: 18,
    l2_token_address: "0x7906ea3706a4a29197ccb8bec1ad593f46eeb5dd86560804b82b342d2001998",
    sort_order: 1,
    total_supply: 1e5,
    hidden: true
  },
  {
    name: "BONZO",
    symbol: "BNZ",
    decimals: 18,
    l2_token_address: "0x1336ad09b46d1cbf3a63c2115428f9812cb06642bdecca0d2c6c938be29d1c2",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "YOLO",
    symbol: "YOLO",
    decimals: 18,
    l2_token_address: "0x62b5b2de4624a3633182ea9b9f1a3acf7116554316e9e671789db9e8280bf71",
    sort_order: 1,
    total_supply: 999999999,
    hidden: true
  },
  {
    name: "Teeny the Dog on Starknet",
    symbol: "TEENY",
    decimals: 18,
    l2_token_address: "0x47d356c975b1bce1320a25045470c5ad603d48289132f73e923b54d055b98ca",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "Jon Daslor",
    symbol: "JDR",
    decimals: 18,
    l2_token_address: "0x2f724caf173f48897018579562924d2ce1f2f15ddf49f5595020155f0be5142",
    sort_order: 1,
    total_supply: 70244863952,
    hidden: true
  },
  {
    name: "Rim Tim Tagi Dim",
    symbol: "RTTD",
    decimals: 18,
    l2_token_address: "0x4593e1e96dea0502827ee66a7611221ad7da3b1eee65bc06efa1007675db442",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "CorinaHayner l",
    symbol: "LLL",
    decimals: 18,
    l2_token_address: "0xcc19a3a0c49b7f9c81fd118c175f92f2fc39a13ce4fc8cd9664c711187768c",
    sort_order: 1,
    total_supply: 455389545198,
    hidden: true
  },
  {
    name: "BU gaga",
    symbol: "BGG",
    decimals: 18,
    l2_token_address: "0x7de796f47e37cfdeecbbdfa3e73c36bff701e0f155b11bfafc80c8e0e198284",
    sort_order: 1,
    total_supply: 83374683208,
    hidden: true
  },
  {
    name: "Froggy coin",
    symbol: "FROGGY",
    decimals: 18,
    l2_token_address: "0x7cd909e49d89a39410ce92ecc74a866c4ec57e2d59d1b98a93e02d9293807c4",
    sort_order: 1,
    total_supply: 3e10,
    hidden: true
  },
  {
    name: "Lonely user",
    symbol: "USRS",
    decimals: 18,
    l2_token_address: "0x40cb300a7e5f7dc37aceee3fd58fc4b7db7816a85ff79e0c2e1c4a8095b3da",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Starknet Bunny",
    symbol: "BUNNY",
    decimals: 18,
    l2_token_address: "0x21a9fbe8bc117c608f36a95573948002cad3dc5017355c54803f7278ad445e8",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Pxls Papyrus",
    symbol: "DRAW",
    decimals: 18,
    l2_token_address: "0x72c1ed39bffe5b1585a24b73190b1de195ae3f3decbf5470d93e06bbb816017",
    sort_order: 1,
    total_supply: 4e11,
    hidden: true
  },
  {
    name: "Random stupid name",
    symbol: "RSN",
    decimals: 18,
    l2_token_address: "0x263a5bf0686b730a4cbfd009f4d0a9c016c06ae936068dbd8bdd7ee528f2e89",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "Sekandl",
    symbol: "SKD",
    decimals: 18,
    l2_token_address: "0x484e1cfd772e892c616433c6ebceed7e06e283c94e70a588ddadf33cd05364f",
    sort_order: 1,
    total_supply: 675464771,
    hidden: true
  },
  {
    name: "Pain au lait",
    symbol: "PAL",
    decimals: 18,
    l2_token_address: "0x49201f03a0f0a9e70e28dcd74cbf44931174dbe3cc4b2ff488898339959e559",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/480c644e-de70-44ea-6c07-bc370512dd00/logo"
  },
  {
    name: "Talstark",
    symbol: "talstark",
    decimals: 18,
    l2_token_address: "0x290e06c5b4ee30491c246b15e0169afceda36fb8d19789922f7cbf738379455",
    sort_order: 1,
    total_supply: 1,
    hidden: true
  },
  {
    name: "Starkned",
    symbol: "STARKNED",
    decimals: 18,
    l2_token_address: "0x523fae386f3319957bbf94f66e981b58c5cb6fcdc9d1750ecb97d11c5081dfb",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/db38871d-88e5-48f7-2c38-4253dea03800/logo"
  },
  {
    name: "Kasam Kauxmnk",
    symbol: "KKK",
    decimals: 18,
    l2_token_address: "0x1049137d5691ce8de1119e5eaef8035c93ccf1f1d65dfb5ff410d548dae86bb",
    sort_order: 1,
    total_supply: 7587214960430581e4,
    hidden: true
  },
  {
    name: "Bullish",
    symbol: "BULLISH",
    decimals: 18,
    l2_token_address: "0x3f68f890cec8e77dc0d34e7e69ae86301cf9b0e11db232690005e2a93d052c3",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "STARK CAT",
    symbol: "CATS",
    decimals: 18,
    l2_token_address: "0x2ba82d441a2804e3963eb05cdb81337d1b0086fec16dd751e41a175e7d6652c",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Operation Cat",
    symbol: "OPCAT",
    decimals: 18,
    l2_token_address: "0x793ac946d0bdd79d47af5ecf50c528bf53c642d3912a6baf433b5cc09ec6171",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Japanese Tosa Inu",
    symbol: "TOSA",
    decimals: 18,
    l2_token_address: "0x1d2b5fe0be222026165a12e5b81a77b395c10a4a54af482d0430cb24c0a3163",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "Donkey",
    symbol: "DONKEY",
    decimals: 18,
    l2_token_address: "0x3db6034c76bf1898ebac71a00651f7243d7c147578f157ad96717add61edaf9",
    sort_order: 1,
    total_supply: 1e13,
    hidden: true
  },
  {
    name: "Chucky",
    symbol: "CHKY",
    decimals: 18,
    l2_token_address: "0x2884aa94c04939430e192181b0878250db66c6798f5d435476d2394e95303e4",
    sort_order: 1,
    total_supply: 21e9,
    hidden: true
  },
  {
    name: "Man Hicutt",
    symbol: "MTT",
    decimals: 18,
    l2_token_address: "0x1bb46490ef56add1fb17c9c5a5fe8e407fbf839ff42eb4a1f269b1afea2418",
    sort_order: 1,
    total_supply: 99555555,
    hidden: true
  },
  {
    name: "UnruggableMEME",
    symbol: "UMEME",
    decimals: 18,
    l2_token_address: "0x2128ae664f0e86d37d8a099c0433bde8110fd33cacbad2d3afc88ee86d776e",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "HEIGOUGE",
    symbol: "HGG",
    decimals: 18,
    l2_token_address: "0x63abca8b87278cf0315ac0dcc3ca44e3c6788b9790d9f923b536b04352ecf69",
    sort_order: 1,
    total_supply: 1111111111111,
    hidden: true
  },
  {
    name: "Subiha Has",
    symbol: "SBH",
    decimals: 18,
    l2_token_address: "0x26a987da9e4263d49839db5a6ee098f76b48e1c334c29e1108da56fad43e455",
    sort_order: 1,
    total_supply: 2539331060,
    hidden: true
  },
  {
    name: "TEST",
    symbol: "TEST",
    decimals: 18,
    l2_token_address: "0x38481266e8f5f86e3d073e50f8a7267a65a3e02bdbaf656936c7587c933dd44",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Krts",
    symbol: "KRTS",
    decimals: 18,
    l2_token_address: "0x39f4c929e4b92a901db8b99f137dc11e49a095b4fef60e1c21b1609db4b158e",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "LMFAO",
    symbol: "LMFAO",
    decimals: 18,
    l2_token_address: "0x51b0704740af57987ea0852ebb4e743ceee0fb93b4b78bdfaa0749cce729e",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "STD",
    symbol: "STD",
    decimals: 18,
    l2_token_address: "0x3f1ffb9ee93ba788be4acae4dbfe1618c1bd917e7c05aea2763ff3e01ce2960",
    sort_order: 1,
    total_supply: 1e3,
    hidden: true
  },
  {
    name: "CumCoin",
    symbol: "CUM",
    decimals: 18,
    l2_token_address: "0x89f2401c598f69e67971373fd74d09900a33c2b592aca665c4dcc6dee5da39",
    sort_order: 1,
    total_supply: 69,
    hidden: true
  },
  {
    name: "DEMEME",
    symbol: "DEM",
    decimals: 18,
    l2_token_address: "0xd3592ed95b75c63b7d9e7def05432096e1a0ee169d88c448a5a6e663ed78d0",
    sort_order: 1,
    total_supply: 1e3,
    hidden: true
  },
  {
    name: "Baby Donkey",
    symbol: "BDONKEY",
    decimals: 18,
    l2_token_address: "0x5094b5715bc7556d5ce63f2c5d5721d8ad61e9f6038b6a56272c41cb77d2255",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "SOOOON",
    symbol: "LTIME",
    decimals: 18,
    l2_token_address: "0x421deb2c7dbb27f0fd93297b190816070023706c1f8ddc6cafa0ecb040d8ab5",
    sort_order: 1,
    total_supply: 430882874,
    hidden: true
  },
  {
    name: "Roaring Kitty",
    symbol: "KITTY",
    decimals: 18,
    l2_token_address: "0x8ea9d8cc0a3eef2cabc401021f344e339ff59f922d3e9ba078dec07c19f846",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Don't Bully My Breed",
    symbol: "BULLY",
    decimals: 18,
    l2_token_address: "0x3253d71bcbd6959bfe3c36017bc30bd0ca5c2b8914ed0d128eab2e9de2cee42",
    sort_order: 1,
    total_supply: 31122023,
    hidden: true
  },
  {
    name: "COPAlink",
    symbol: "CLINK",
    decimals: 18,
    l2_token_address: "0x615d69d72b596aabb24efb19ba3adcb3b1dc5c80b913a3abbb422006caf5a5e",
    sort_order: 1,
    total_supply: 6855222222,
    hidden: true
  },
  {
    name: "Blob",
    symbol: "Blob",
    decimals: 18,
    l2_token_address: "0x5c8b33bb7a95edd35dc5214771bc542eee39895d83e04231f1d52270ac1798e",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Unruggable2",
    symbol: "MEME2",
    decimals: 18,
    l2_token_address: "0x3c067916ac3c273dda6af7b0f2b861d9df7292cbeef1dc5df4a5a81e51df5fa",
    sort_order: 1,
    total_supply: 1e4,
    hidden: true
  },
  {
    name: "LANNISTER",
    symbol: "LAN",
    decimals: 18,
    l2_token_address: "0x694dabaced5685f17b65ee1ac9d24849e1a25702e6706ef8691d4e9cd478cd3",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "CHOCOLATE",
    symbol: "CHOCO",
    decimals: 18,
    l2_token_address: "0x462d03e562198b651765adba81f1a0ff27eff2d6c274f59aa1273f3b6373a98",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Starky Shark",
    symbol: "shrk",
    decimals: 18,
    l2_token_address: "0x56c2e5dca6d2f70b0b24493efda75f3d99059efcb14dcb2194e7055739823a6",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "SBF coin",
    symbol: "SBF",
    decimals: 18,
    l2_token_address: "0x134cab6affabfd0bac082179adaeee9a7b5c7c385746502ccd5aad1450c0bb9",
    sort_order: 1,
    total_supply: 9001,
    hidden: true
  },
  {
    name: "AVNimni",
    symbol: "AVNimni",
    decimals: 18,
    l2_token_address: "0x56a902659567004e0d20d47ef8bd9ab51f8dcbf233089350355d34f8aaac67e",
    sort_order: 1,
    total_supply: 1e3,
    hidden: true
  },
  {
    name: "Weed",
    symbol: "WEED",
    decimals: 18,
    l2_token_address: "0x5c39376ceafe96423792e928426b7df2db7e2d2bf6217cc1ad33e0737c01ed3",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "ZeroLend",
    symbol: "ZEN",
    decimals: 18,
    l2_token_address: "0x1cc634159f30a5cc590c12869d0ad3d4e0f5992626a106113b08948ed45a28a",
    sort_order: 1,
    total_supply: 2774483165,
    hidden: true
  },
  {
    name: "FLOKI",
    symbol: "FLOKI",
    decimals: 18,
    l2_token_address: "0x67ad287d8158f5a1117ed389f742c6eaae34e9d364dc323766ce80122e39e14",
    sort_order: 1,
    total_supply: 1e13,
    hidden: true
  },
  {
    name: "Ruggable",
    symbol: "RT",
    decimals: 18,
    l2_token_address: "0x7836e92b4dc27baeecb9ec36c3e995be866bbfa714da18fd020d4e4a59249b2",
    sort_order: 1,
    total_supply: 1e3,
    hidden: true
  },
  {
    name: "GOMU GOMU NO MI",
    symbol: "GOM",
    decimals: 18,
    l2_token_address: "0x69e5d8f696a5b6d13c7378687c5ccebeb66f4a9e600f16c724ad05c030f31e3",
    sort_order: 1,
    total_supply: 1e15,
    hidden: true
  },
  {
    name: "Monopoly Money",
    symbol: "MNP",
    decimals: 18,
    l2_token_address: "0x32d999e8dae0b38dfa15bcf9c2887933321643f7d6843c2b0d319422195a417",
    sort_order: 1,
    total_supply: 20580,
    hidden: true
  },
  {
    name: "Grails ERC404",
    symbol: "GRAILS",
    decimals: 18,
    l2_token_address: "0x2a819b93cc69b45ee5d1a1bfc16954c16f6d35c3873a06c97b95c009bfe502b",
    sort_order: 1,
    total_supply: 1e4,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/da59396d-943f-4525-6ed2-43eceb692f00/logo"
  },
  {
    name: "StarkPepe",
    symbol: "SPEPE",
    decimals: 18,
    l2_token_address: "0x1e0eee22c684fdf32babdd65e6bcca62a8ce2c23c8d5e68f3989595d26e1b4a",
    sort_order: 1,
    total_supply: 41999999999,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/0df4c5ec-66cf-4067-cc92-5a679630f800/logo"
  },
  {
    name: "JEET",
    symbol: "JEET",
    decimals: 18,
    l2_token_address: "0x7003b7e0a1bffd81d602bf10cd7ca2a9ed8bb5b0837110f9a70b8fc5f181ccf",
    sort_order: 1,
    total_supply: 42e8,
    hidden: true
  },
  {
    name: "STONK",
    symbol: "STONK",
    decimals: 18,
    l2_token_address: "0x4da6fb0db214c815d5937a71c8c962a5369e3b243d8f80211bd5ffe76a75ff6",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Ninja",
    symbol: "NJA",
    decimals: 18,
    l2_token_address: "0x1247685634c2db5c0a3ebfc0152200af8f5987a49fe2b28a18cd95e23afe88c",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Riya Moni Akter",
    symbol: "RMA",
    decimals: 18,
    l2_token_address: "0x161857ba865b5f9b9f049f7aba0e63160132fba499d38dc693bf7c8fa98b035",
    sort_order: 1,
    total_supply: 62684740458,
    hidden: true
  },
  {
    name: "KAMALA",
    symbol: "KAMALA",
    decimals: 18,
    l2_token_address: "0x58a00a3253fce72d661fb9d08ab587c783250d0cea78333699a1ba026809fdf",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "FastAndFurious",
    symbol: "FAP",
    decimals: 18,
    l2_token_address: "0x2b797b77a7ba426e73720dcf00101b07de913277ea41c407cdd14e7ca51b78c",
    sort_order: 1,
    total_supply: 696969696969,
    hidden: true
  },
  {
    name: "wow",
    symbol: "%7",
    decimals: 18,
    l2_token_address: "0x4af9d52064027fd8caf3a4a91e710becfe723abe1d7b10e087640a7f5cfe0a2",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "AAPKF",
    symbol: "XDGEE",
    decimals: 18,
    l2_token_address: "0x7cde5e662b122773d49f383362ff8fccb365480375982577258774fbc3ca67b",
    sort_order: 1,
    total_supply: 7777777777777,
    hidden: true
  },
  {
    name: "shrryna",
    symbol: "szy",
    decimals: 18,
    l2_token_address: "0x5ac89ca2fff78735f8fc5d7128109d5f4e981716e8b39589766c87f0bebdca0",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Meme from TG Bot",
    symbol: "TGBot",
    decimals: 18,
    l2_token_address: "0xb44ca0a070565dc42dd555f6be3c6404b2e9f0a18ba5a166bba5d1cf5659a",
    sort_order: 1,
    total_supply: 1e4,
    hidden: true
  },
  {
    name: "Yohoo",
    symbol: "YOO",
    decimals: 18,
    l2_token_address: "0x4ea9e7f239ed3fe08441325501e8bc5e70daf45a6d42cb459bffc8ab237f906",
    sort_order: 1,
    total_supply: 381826511905568,
    hidden: true
  },
  {
    name: "Bahhhhhhhh",
    symbol: "SHEEP",
    decimals: 18,
    l2_token_address: "0x77204ab303a20a08ed0c6d9dd86b6fce47949055e80cb06c87f84bbc702c435",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "Jesthis",
    symbol: "JEST",
    decimals: 18,
    l2_token_address: "0x73e7f70052de305bf7a36ac4931ea0232656087b0fadab5252b6ecf31fc622e",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Jarvis",
    symbol: "JARVIS",
    decimals: 18,
    l2_token_address: "0x383273eb0897414b0e4f87cb322ac4fe0d86fb77a108af28e1d1dcfecb10ce9",
    sort_order: 1,
    total_supply: 42e9,
    hidden: true
  },
  {
    name: "NeverChain",
    symbol: "NeCa",
    decimals: 18,
    l2_token_address: "0x2f6f268c1c26d8bed325cffed479c0522174074562eefa9adc44db6081bbb09",
    sort_order: 1,
    total_supply: 100,
    hidden: true
  },
  {
    name: "Make Starknet Great Again",
    symbol: "MSGA",
    decimals: 18,
    l2_token_address: "0xe234451e15427f8fdf966588e04087cbda5b04caf53d807889d46854dd036",
    sort_order: 1,
    total_supply: 137,
    hidden: true
  },
  {
    name: "ROZ",
    symbol: "ROZ",
    decimals: 18,
    l2_token_address: "0x788929eb80e2209a5fe5f06c02cb8d3e3f19ca7b13fc2337663eecd060f2831",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "WOLF",
    symbol: "WLF",
    decimals: 18,
    l2_token_address: "0x5a3f62edb1a09555859b85f70b46afc8694ad2e0da4feb0a6cf18f71870196",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Baby Slinky",
    symbol: "BSLINK",
    decimals: 18,
    l2_token_address: "0x450092907f11b0a5d366213c4130f810eef6006ce18a982cfd849a1be74f739",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "BATPEPE",
    symbol: "BATPEPE",
    decimals: 18,
    l2_token_address: "0x285fead30d25e4f74cff307ca1caa8c5eb6af0730b12842e3910b13e32ba8f3",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "pepe",
    symbol: "pep",
    decimals: 18,
    l2_token_address: "0x567608eb3eca8a692619a9e402447c955479d003cc8cef18b27041813e69435",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "MAYONNAISE",
    symbol: "MAYO",
    decimals: 18,
    l2_token_address: "0x542fa3910dbf009b1c6178357f4c62f92307d15aeff5cd82168cb293604fea8",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "pip install Starknet_py",
    symbol: "PIP",
    decimals: 18,
    l2_token_address: "0x43562d74d9432a0d01c0e38076be3eec0db0fa0db13fafeea676995fc74501f",
    sort_order: 1,
    total_supply: 1e5,
    hidden: true
  },
  {
    name: "ligma ballz",
    symbol: "ligma",
    decimals: 18,
    l2_token_address: "0x78a255990afa97068d64330bb97a682f6adc97031ae5d3544f95683318ab39a",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Truth Terminal On Stark",
    symbol: "Truth",
    decimals: 18,
    l2_token_address: "0x278a5d28996c3f084f8fd47b72188cf818a1ea3c50425fc4de15124d9c0fcad",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Memes AI",
    symbol: "MemesAI",
    decimals: 18,
    l2_token_address: "0x10a9c629b2444267bfd69fb8b042dd3b5dfb16714337775689dcc07b268062e",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Project69",
    symbol: "Project69",
    decimals: 18,
    l2_token_address: "0x3b61d1f6f95e5c3db013a9e01d2d7f5466806045fcd71530e531850ec5b866c",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "fdfgd",
    symbol: "dffg",
    decimals: 18,
    l2_token_address: "0x684ae7052e64157bfe082d99a41bed487c4e778c26147b5e954ecaa8f618e2d",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Act I : The AI Prophecy",
    symbol: "ACT",
    decimals: 18,
    l2_token_address: "0x73e803a1617a738941a1748de2dd3f4ec2c211687e30399c0e8dcb576bcdb87",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Aiwithdaddyissues",
    symbol: "SHEGEN",
    decimals: 18,
    l2_token_address: "0x5f574e59902348f644d5f2ba68ff6cf4941ff027b7a9b0da7e9d8335a487dfd",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "TACO",
    symbol: "TACO",
    decimals: 18,
    l2_token_address: "0x6b9fa1b769794393c7b8387592471b23958c318f5dd7462e233fb7f004439b2",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "BOOK OF APES",
    symbol: "BAPES",
    decimals: 18,
    l2_token_address: "0x7575788a0464c4ac0c7255ef8d83e3365ddc83ecea82d06674e62fd34d11804",
    sort_order: 1,
    total_supply: 1e13,
    hidden: true
  },
  {
    name: "flippyflop",
    symbol: "FLIPPY",
    decimals: 18,
    l2_token_address: "0x6fa03681cc8851a4493fac65f75fa988c4bb9a7b20cec81d2b393b1ab8039e6",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "ekbt",
    symbol: "ekbs",
    decimals: 18,
    l2_token_address: "0x2c3263958b6c91a1a06e678ae8816c66badd87a3b67e19d8b0b3d6abb440421",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "DUMMY TEST",
    symbol: "DUMMY",
    decimals: 18,
    l2_token_address: "0x158c1f2d4e3b9c56ca512a9bebf34fc92b6098ca88fc93d29b1d52a5750322",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "TESTING",
    symbol: "TESTING",
    decimals: 18,
    l2_token_address: "0xb1a738afab71dc957c4b9728c134e374cd17c1a156a88964f38dcccba7f961",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "IS THIS WORKING?",
    symbol: "ITW",
    decimals: 18,
    l2_token_address: "0x55aa5be4788105b8d53770bebe099bd0f27114bd3cec68ab5e3a2082c575f96",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Leo",
    symbol: "Leo",
    decimals: 18,
    l2_token_address: "0x191b244f129e62370cf16c8bf33d05029f2c1ee653261c3bc98bb937df652f4",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "ELONG",
    symbol: "ELONG",
    decimals: 18,
    l2_token_address: "0x364459e29cb0b7ac5e55b0321d89110c346cda5a3a6beabf5a9f21e32330dc6",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "MUSK",
    symbol: "MUSK",
    decimals: 18,
    l2_token_address: "0x1c231e25e7a97d1d942aaf1dedfa0c9de208508e9eebbc0edd4c09daeac1b5c",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "ECatStark",
    symbol: "CAT",
    decimals: 18,
    l2_token_address: "0x3bc357d45a1fc19bb517b3032c932ad975e4cdb2c047eea25b474354725ff50",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "StarkMoon",
    symbol: "StarkMoon",
    decimals: 18,
    l2_token_address: "0x5e2fc8e13ded3d080e70ed21495c11689aa2b401d85b4c82e7dff196abdb0e7",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "PACO",
    symbol: "PACO",
    decimals: 18,
    l2_token_address: "0x599a83c8b885157a0526a22ba3924a25f42d1880f1e0900c786371bf33d04dd",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "SKWOLF",
    symbol: "SKWOF",
    decimals: 18,
    l2_token_address: "0x5d91dc546550c65a96b6ccc1f5dea9b304e588b2725a66beababf333e823e52",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "test",
    symbol: "ttttt",
    decimals: 18,
    l2_token_address: "0x741b7bd82c63705e6a6f22bb49a1ff9926c30af24e9f97983a294eff8be3bf1",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "STARKNET BROTHER",
    symbol: "BROTHER",
    decimals: 18,
    l2_token_address: "0x3b405a98c9e795d427fe82cdeeeed803f221b52471e3a757574a2b4180793ee",
    sort_order: 1,
    total_supply: 1e15,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/282efd02-eecd-467c-545a-c53a895eff00/logo"
  },
  {
    name: "Starknet Sister",
    symbol: "SISTER",
    decimals: 18,
    l2_token_address: "0x4c96dff94fb68c0c44810f0ab7190edd01f5c2c61110b4f5c8684bd172bbe6f",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Starknet > Solanan",
    symbol: "STARK>SOL",
    decimals: 18,
    l2_token_address: "0x6d4f1fe51a5a10875b13c80e915781569b43058324202718052ac2ad3df88ac",
    sort_order: 1,
    total_supply: 33e4,
    hidden: true
  },
  {
    name: "The Peak of Starknet",
    symbol: "TPS857",
    decimals: 18,
    l2_token_address: "0x19f3cc6d57399c8845666db5f373aff5ee83e127e7820ae7c122aa87838bcdb",
    sort_order: 1,
    total_supply: 857857857857857,
    hidden: true
  },
  {
    name: "SISTER",
    symbol: "SSTR",
    decimals: 18,
    l2_token_address: "0x102d5e124c51b936ee87302e0f938165aec96fb6c2027ae7f3a5ed46c77573b",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/83900dfc-207d-447a-c003-a304fa249000/logo"
  },
  {
    name: "Starknet Wolves",
    symbol: "WOLVES",
    decimals: 18,
    l2_token_address: "0x192e0f538049ea7280931e15c1ccf565ef87dd247fee76980e7c89f4002bece",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "STARKNET FATHER",
    symbol: "FATHER",
    decimals: 18,
    l2_token_address: "0x44d0842c479da95c81cb762b0bb8866cc7a8027902d5480ba840653f88424da",
    sort_order: 1,
    total_supply: 1e13,
    hidden: true
  },
  {
    name: "Mother",
    symbol: "MTHR",
    decimals: 18,
    l2_token_address: "0x38952841eddb0fcf66abeabf4127c442e62e20e42ee860f423395884aa58912",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "UNCLE",
    symbol: "UNCLE",
    decimals: 18,
    l2_token_address: "0x53374f1ca7e820735d48376d36cfaaabf713134fa6f123fb5c1ed84e4987a10",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "DAUGHTER",
    symbol: "DGHTR",
    decimals: 18,
    l2_token_address: "0x3d4427b3b9de163cf011b57affc9ddfc44d4889b862a131eed45168566fd819",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "SABAN SON OF SABAN",
    symbol: "SSOS",
    decimals: 18,
    l2_token_address: "0x2830336f5086075be054e61e50b2b410d940ed404f80c9237366bc2365a3c5b",
    sort_order: 1,
    total_supply: 69696969696,
    hidden: true
  },
  {
    name: "MAIA",
    symbol: "MAIA",
    decimals: 18,
    l2_token_address: "0x50226e1be395d070dca16de2ae366bd69aee4d68a2ec399c425af2b38c50752",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "stepsisters",
    symbol: "ssstr",
    decimals: 18,
    l2_token_address: "0xe60fe8f137a48760c8e6c31f2509f9ee95fe8106b56902a46ea850ccc5ea91",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "AKKAAKKA",
    symbol: "AKA",
    decimals: 18,
    l2_token_address: "0x6ba5c85b2db709cb77a0936720daed116499c447ae551baed2cd8d7fd1d860f",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "COSMO",
    symbol: "COSMO",
    decimals: 18,
    l2_token_address: "0x7a4cd0a39cd2b907492a217029abfff381bc0811d9a62db437c505f4d079ef3",
    sort_order: 1,
    total_supply: 69e9,
    hidden: true
  },
  {
    name: "Baby Brother",
    symbol: "BABYBRO",
    decimals: 18,
    l2_token_address: "0x36e9c455310f5a84695baadedfe4e71722544ae9cb65228267efc261071a837",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "STARKNET SOLDIER",
    symbol: "SOLDIER",
    decimals: 18,
    l2_token_address: "0x7ad5d7176d7f30ddd34507b487c7213afcd3713089c2407a2c5dccdbfc2eac7",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "STARKNET STEPBRO",
    symbol: "STEPBRO",
    decimals: 18,
    l2_token_address: "0x708c0375dc93e3a0612d7693b03e7967cef42ab84ef2353cff1e2e823c3f2a1",
    sort_order: 1,
    total_supply: 6942e10,
    hidden: true
  },
  {
    name: "STARKNET MOTHER",
    symbol: "MOTHER",
    decimals: 18,
    l2_token_address: "0x20ab647d3893009ac333a4b80c6b5f0a7cc713e38df0b2494fd8028eb75e558",
    sort_order: 1,
    total_supply: 1e15,
    hidden: true
  },
  {
    name: "tnt",
    symbol: "tnt",
    decimals: 18,
    l2_token_address: "0x3d47624c7d102929a4cd655085363ab4b634063ae9644a19a31348c2fe6948e",
    sort_order: 1,
    total_supply: 99999999999,
    hidden: true
  },
  {
    name: "blobert",
    symbol: "BLOBERT",
    decimals: 18,
    l2_token_address: "0x642febacfaf539bc691779959b9341458f789c4b209af3ba00a0862c0186f44",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "DEGENZ",
    symbol: "DEG",
    decimals: 18,
    l2_token_address: "0x4aac09c8f9c395b31979129408656d3d8dcc1f3daa174b5a63a0afd419a6bb0",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "STAYLUS",
    symbol: "STYL",
    decimals: 18,
    l2_token_address: "0x8b0f7d90819ccfbf3235dcc1b7bf60972c7ca04ddfd84a1b7a8bf4d5495606",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "CASINO",
    symbol: "CASINO",
    decimals: 18,
    l2_token_address: "0x10c6f6c6f960242877f8a5d4d0ff5966023791060074d475b0560ec8481987f",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "STARK PUMP",
    symbol: "PUMP",
    decimals: 18,
    l2_token_address: "0x32ee0aa960934b5e694520bfbf8487e6e9890c855da2201c3dcdf8e7935c17f",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Big Pump",
    symbol: "BIGPUMP",
    decimals: 18,
    l2_token_address: "0x3d398c7301fa483f4ea8476f0acc6c88310543b9f942bd1afa735e2461f0d92",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Starknet Pnut",
    symbol: "PNUT",
    decimals: 18,
    l2_token_address: "0x2c3f8939cbebd7fe02c4cf2148128afa9ce04a600edb6aca42e33c3da2f31f2",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "OASIS MINECRAFT",
    symbol: "OASIS",
    decimals: 18,
    l2_token_address: "0x1cd7a44dde5dcd78304bd7c030921de03d12b55d4e6cf74e1a1df8e36a50e3e",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "LITTLE BROTHER",
    symbol: "LILBRO",
    decimals: 18,
    l2_token_address: "0x41c12401ea0be1629c87968499a10f5b33eee106efdaa67702eedfa79415daa",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "ALF THE ALPHA OFFICIAL",
    symbol: "ALP",
    decimals: 18,
    l2_token_address: "0x4c8022a4f63260e0de1db8b50325bc2c9c0dd310bfb8f4c220ca0aa5a5f036b",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Baby Alf",
    symbol: "BABYALF",
    decimals: 18,
    l2_token_address: "0x3f3ba193f40d85b72c218a7510514120e83dacc431b743b40affee5cc7b65a7",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Starknet Terminal",
    symbol: "TERMINAL",
    decimals: 18,
    l2_token_address: "0xe0599722cc4559fa82f0b1c74d62f57db772cf3173617bf9fb52e18c547d1e",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "Donald Trump",
    symbol: "PRESIDENT",
    decimals: 18,
    l2_token_address: "0x24f4718ffcfc96abe8ed51818620d577fcb3480044db11a20800e101c40c4f0",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "DICK",
    symbol: "DICK",
    decimals: 18,
    l2_token_address: "0x388588584bd8c651151f6baf241a85827e7ff0574101f2a8194a3df68a7e2fe",
    sort_order: 1,
    total_supply: 69420,
    hidden: true
  },
  {
    name: "ElonMuskIsraelTrump69BROTHER",
    symbol: "SOLANA",
    decimals: 18,
    l2_token_address: "0x69f52dab53b63baa7abee0ac5d2baeeafd15026db2c59ed5c603c0e5703e16c",
    sort_order: 1,
    total_supply: 42069e4,
    hidden: true
  },
  {
    name: "DARK MAGA",
    symbol: "DMAGA",
    decimals: 18,
    l2_token_address: "0x7f4f7e71414cf5891ef9abb913d8140d91630991fd67489dc01ee7647e0aa8e",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "CAVIAR",
    symbol: "KAV",
    decimals: 18,
    l2_token_address: "0x66e306a45d49fc668b2fd002088581deba600e09088cca60898f233e0b4160b",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Stark Panther",
    symbol: "SPANTHER",
    decimals: 18,
    l2_token_address: "0xe163ef7bacef72f2a09d086b9f5bf334789a35cf0135166518c8e660c1e19c",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Mani",
    symbol: "ANI",
    decimals: 18,
    l2_token_address: "0x344d0bebad5d7cf5ca6ea83bd945ed6fe0c291c4cbd813aaf87c1e49511b62d",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "Animi",
    symbol: "ANM",
    decimals: 18,
    l2_token_address: "0x34c53bdc0ebc88161abe62ef827b832fedb084ceac5109958b560dfce39e243",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "BANGKOK",
    symbol: "BKK",
    decimals: 18,
    l2_token_address: "0x47234af8a7abd1fe718d548fc956d7dfb15ea5f1225638bb8f82ebff1cfd12f",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "President of the United States",
    symbol: "POTUS",
    decimals: 18,
    l2_token_address: "0x2e78351f8bdc12276ce128e7e4f1844f2932782b32ce3c0e4e635c2dff8ed3c",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "StarkRoll",
    symbol: "STRL",
    decimals: 18,
    l2_token_address: "0x50d580514b5dc87d25df98b7d9aca12a8050f3d1b8763284f34d047d56922b7",
    sort_order: 1,
    total_supply: 9999999999999,
    hidden: true
  },
  {
    name: "Greatest Of All Time",
    symbol: "GOAT",
    decimals: 18,
    l2_token_address: "0x5ba65c8cbcfd2e6a5a67e88fb9dc2ed1811ed5b4d7463b0e3d88711a03824ed",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Don't buy",
    symbol: "NOBUY",
    decimals: 18,
    l2_token_address: "0x662c6cb1c18882ab24fec5f7abfd8a08b5fe6ab9a39cc4857f50650e6aeb916",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "StarkPunks",
    symbol: "PUNKS",
    decimals: 18,
    l2_token_address: "0xed399ad3cb9393be1798312d587316261497fa661ad6780fdc381079ea67c7",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "wizardwifhat",
    symbol: "WIZ",
    decimals: 18,
    l2_token_address: "0x6e987f1e703c549991e1c13b8f1b8e1e6f8bba068a0715b9aacb51dba2800f",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true,
    logo_url: "https://imagedelivery.net/0xPAQaDtnQhBs8IzYRIlNg/4d060c49-3d94-43d8-5f98-085357fe0800/logo"
  },
  {
    name: "pythia",
    symbol: "PYTH",
    decimals: 18,
    l2_token_address: "0x729bbe6cb2935936aa73dc1fc506811ef992f956f6b9255b1513fb81d747dab",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "PULSAR",
    symbol: "PULSAR",
    decimals: 18,
    l2_token_address: "0x1234f3f6b2149d55e1dfedfb6d09f98fbc632725e63ba9c5aefc800091d660a",
    sort_order: 1,
    total_supply: 420420420420,
    hidden: true
  },
  {
    name: "Starknet BULL",
    symbol: "SBULL",
    decimals: 18,
    l2_token_address: "0x6d4bef2082c4290f68f001ea29109801ede3d718a5c81db836df7fdfc842d2",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "eBeggars",
    symbol: "eba",
    decimals: 18,
    l2_token_address: "0x6a44ffed4c5cc60dfc9cbead0614c985614f7ad03ddf49c81c9c31b49eca1f1",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Sigmoid",
    symbol: "SIG",
    decimals: 18,
    l2_token_address: "0x7f688d155147196b81348e0be66472b9013043450cd0dddad3d6f9a12778558",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "ZKPepe",
    symbol: "ZKP",
    decimals: 18,
    l2_token_address: "0xb3bb04c6aefd8750ccb3b78a913afff4cb61c588d1bbf7ece626d0791333dc",
    sort_order: 1,
    total_supply: 1e13,
    hidden: true
  },
  {
    name: "zkWorld",
    symbol: "ZKW",
    decimals: 18,
    l2_token_address: "0x3fa44ec49c11ec9099f2deb3c2c186d8bf9d5394d051231e78b2b5fe5bc0c6b",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "",
    symbol: "one",
    decimals: 18,
    l2_token_address: "0x159c536cfaa28d4ed0845a5fda8a58a8abaa8c9c09ccdcbd96121d97443e25c",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "Starkraft",
    symbol: "STKF",
    decimals: 18,
    l2_token_address: "0x7d7275c1ed80ecb8d830b9f7f4d711df3401bc05815c9c71e4034cdd522c45",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "TONY STARK",
    symbol: "IRONMAN",
    decimals: 18,
    l2_token_address: "0x1ab9308ba9012665f136e00c2fe197e9137ac384b4bfe828eef3b1712bb40e2",
    sort_order: 1,
    total_supply: 196219631968,
    hidden: true
  },
  {
    name: "Game Of Thrones",
    symbol: "GOT",
    decimals: 18,
    l2_token_address: "0x464a7b709331642046503687d3852b78a68d73574c1de016310f52b99bed371",
    sort_order: 1,
    total_supply: 1337228,
    hidden: true
  },
  {
    name: "Ape Before Doing Logic",
    symbol: "APEDEL",
    decimals: 18,
    l2_token_address: "0x85313bccdb2642c9439ebf606c7d426ef5824a8660351f680ff16080fe73e8",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Even Dead Im The Hero",
    symbol: "EDITH",
    decimals: 18,
    l2_token_address: "0x56ad444d9cd09f957037618dbc10048c653ba7083484478810ac70d4806ff81",
    sort_order: 1,
    total_supply: 6942e5,
    hidden: true
  },
  {
    name: "Baby Brother",
    symbol: "BBROTHER",
    decimals: 18,
    l2_token_address: "0x5b90c278d5f7f019759765d1ec7184082c55933f837724fa3c6febdadd8e6da",
    sort_order: 1,
    total_supply: 1e13,
    hidden: true
  },
  {
    name: "EL-Y BIG BRAIN",
    symbol: "EL-Y",
    decimals: 18,
    l2_token_address: "0x9003851457aef586efecfff71c7e926bbe97b7a28e2ca8e70bfd5c86af6fd0",
    sort_order: 1,
    total_supply: 8888888888,
    hidden: true
  },
  {
    name: "Nevermore",
    symbol: "Never",
    decimals: 18,
    l2_token_address: "0x34f64964ffce40811bedc3a3078e2476b283af85e7317453c7ce7f30aae21c3",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "One For All",
    symbol: "ALMIGHTY",
    decimals: 18,
    l2_token_address: "0x77f6b7ad041fc848c9dced0fd844b933aad046b7302d6e2898cbe376da8ef1d",
    sort_order: 1,
    total_supply: 1,
    hidden: true
  },
  {
    name: "Durex",
    symbol: "DUREX",
    decimals: 18,
    l2_token_address: "0x98f752e4b4ca2f84c55772703db7baf09265a781d2b27f6fd601514bd66dad",
    sort_order: 1,
    total_supply: 1,
    hidden: true
  },
  {
    name: "Flash",
    symbol: "FLASH",
    decimals: 18,
    l2_token_address: "0x82ffda1940a21f43fc8209b6a8a2e86f95a40a6b808b7e9757726a82c9df01",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "Starknet Bee",
    symbol: "BEE",
    decimals: 18,
    l2_token_address: "0x799e9deb361ea5e7bccc3e4940a759252564603d6bc7c7688007c4fb22a5f69",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "StarKnight",
    symbol: "KNIGHT",
    decimals: 18,
    l2_token_address: "0x2520cde28e83faae665a34cb18caa285937bb30a028fd894978664f4454f96",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "sister",
    symbol: "sis",
    decimals: 18,
    l2_token_address: "0xae996ce312c9fcf2ad9be61b7907f47d053c28d24e5c8198a0d71c2f02d9a7",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "FATHER",
    symbol: "FAT",
    decimals: 18,
    l2_token_address: "0x13b40688ff77903ea10db94628653ac715011dbbaa7323a6cf60bbf6691c794",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "ELONCOPTER",
    symbol: "TSLA",
    decimals: 18,
    l2_token_address: "0x1491e45f45f5381cac7100607812bd9fc069bcb3c7824f1cc4eb96ea4c160c3",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Keria Lover",
    symbol: "KERIA",
    decimals: 18,
    l2_token_address: "0x26c1d12ead8d53ac117c925a96f71ab693ba618b031ea660326773f646e0086",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Contex",
    symbol: "CONTEX",
    decimals: 18,
    l2_token_address: "0x66ec0506c52be379be9ddc68122e38022e890832cc911f782aa8b93ea3d6257",
    sort_order: 1,
    total_supply: 1,
    hidden: true
  },
  {
    name: "TEU",
    symbol: "TEU",
    decimals: 18,
    l2_token_address: "0x1d7aa9823a24b3603e0f7202ea3464ea791a14b9621ffc43346797eb28cd8b7",
    sort_order: 1,
    total_supply: 1e14,
    hidden: true
  },
  {
    name: "PumpPotion",
    symbol: "STARKIUM",
    decimals: 18,
    l2_token_address: "0x1f19e1602e41ea7a918cf26ac6b8fbbfcebec5132ef9cdfbdab27eee67350a5",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Starknet AI",
    symbol: "AI",
    decimals: 18,
    l2_token_address: "0x1121a01f412e98899d2128b62638be62ec70a5a75dc40cd95ea23de68a2e74b",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "STRK Up",
    symbol: "UP",
    decimals: 18,
    l2_token_address: "0x55a088d8a34fab8b56dbc48bc79f48c25777f48122a98eba4a7b3a919028b26",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Dt Forget 2 B Awesome",
    symbol: "DFTBA",
    decimals: 18,
    l2_token_address: "0x57467c5e0fb02196bed941b6f049857cfb9b70e308b814bd167488759652789",
    sort_order: 1,
    total_supply: 196219631968,
    hidden: true
  },
  {
    name: "PIKA",
    symbol: "Pikachu",
    decimals: 18,
    l2_token_address: "0x4cf4057b2e6aee461d7e9b98195aaa80eb4b36f81435ccda730728581591d09",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "IRON STARKNET",
    symbol: "IRON",
    decimals: 18,
    l2_token_address: "0x286385985403269eb90bf9122e9bce69eef96aeb1b12e3a15acaf492779df16",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "CantRug",
    symbol: "CRUG",
    decimals: 18,
    l2_token_address: "0x311c13bba85a46ecf887e2ad3442540f5395efee6c5ee09b5e1e604c1ff9782",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "TRINK",
    symbol: "TRINK",
    decimals: 18,
    l2_token_address: "0x46452400f416fecb57832a923a7532caf963c3f75e03f900b972f3af1012430",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "AVNU",
    symbol: "AVN",
    decimals: 18,
    l2_token_address: "0x5d7854e879a96f851a11ae454844827849c2c1fd9d4c768075c76db466f2813",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "SWAGGY the Wolfdog",
    symbol: "SWAGGY",
    decimals: 18,
    l2_token_address: "0x56f216a2f3e3da47e6ccb58f2c75c453107053c9e668d3eb03fe731c0e48fe6",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "TOGA",
    symbol: "TOGA",
    decimals: 18,
    l2_token_address: "0x53cfe3fe6d449e0d1a7db55aea82b685b0dca4d90b7ab3f3cf84a3066fede06",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "BUY",
    symbol: "DONT",
    decimals: 18,
    l2_token_address: "0x74031e465c0aa9d4202bb225898a2d9ef276d27fcf1cfa70a76219021fcab6d",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "Scooby-Doo",
    symbol: "Scooby",
    decimals: 18,
    l2_token_address: "0x671e59def5bbaf8a401102651a16e1c7a2891a0896bfa3514ec5b472802a0c8",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "BENDER_RODRIGEZ",
    symbol: "BNDR",
    decimals: 18,
    l2_token_address: "0x6dd544a5fd6067b9c511c5e89b1e1e7c497211ce2c9c160159fb432d318516",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "SpongeBob",
    symbol: "SBOB",
    decimals: 18,
    l2_token_address: "0x307d998d8e39c6b8a2d81739a6fb45a91624a2cbdb6c4ae33002e418ade808f",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Sonic The Hedgehog",
    symbol: "Sonic",
    decimals: 18,
    l2_token_address: "0x2dfc17ff0fd63b8e7cf00174ec0a1d60e993cc25e7daa8422395f5847686644",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Happy Birthday le V",
    symbol: "HBV",
    decimals: 18,
    l2_token_address: "0x31d17547c976100d20a649a7850703de67832183ac7237497a3c696a706d653",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "F.C.F.S.",
    symbol: "GAME",
    decimals: 18,
    l2_token_address: "0x3a548ce667111ad7b9b1ae2908752b292ea9748c9a21bf321d3ad39eb87118e",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Stark Space",
    symbol: "S2",
    decimals: 18,
    l2_token_address: "0x44fdda35777f54901b14773c32b606356e76cad7817a841f54388617d42dd1c",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "Poelifinario",
    symbol: "TOON",
    decimals: 18,
    l2_token_address: "0xeab5f45791fa99afd75e5f4ad09c545a22b80c2fb30e99684baaa0fcfafb6b",
    sort_order: 1,
    total_supply: 3141592653,
    hidden: true
  },
  {
    name: "Cult of  Brother",
    symbol: "COBRO",
    decimals: 18,
    l2_token_address: "0xc2e3b37f2f88f3129f5fcd792f064a68cafe9037e2522e9efc8ca257bb0f38",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Sikh Coin",
    symbol: "SKH",
    decimals: 18,
    l2_token_address: "0x38ca2e84312eea83f0e1bf08d4a650e174f7fa092b23b51b9ca3ec7b66662f7",
    sort_order: 1,
    total_supply: 26e6,
    hidden: true
  },
  {
    name: "ai stark",
    symbol: "AISTK",
    decimals: 18,
    l2_token_address: "0x488c8977febc8c65803cc18877a73e268c90e5c71dd069d4397bb05bd6fdf0d",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "DOGE COIN",
    symbol: "DOGE COIN",
    decimals: 18,
    l2_token_address: "0x2f840852ab23007fe7341fc81aa51195ec27ec5bd0f9e1abcf1ddcd8a59395d",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "StarkSeason",
    symbol: "SSS",
    decimals: 18,
    l2_token_address: "0x9810e024509e0e1fa08a9a7e4c5e09a063b5289f74a9688f48166cb6f60062",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "StarkWukong",
    symbol: "SWK",
    decimals: 18,
    l2_token_address: "0x72b245cc5bafe3fa6b2aa94aa47601f348498fddea9d2f903994804315d006b",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Starknet Gangz",
    symbol: "SGANGZ",
    decimals: 18,
    l2_token_address: "0x49181f4f268429107fa1701e6f1118f4d52c7d24842e5fff88365f645a418f0",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "Odin's Dog",
    symbol: "ODOG",
    decimals: 18,
    l2_token_address: "0x461712519c576c061483590e097282a500529f9fd7844946fc8a0efc29edaa2",
    sort_order: 1,
    total_supply: 6942e7,
    hidden: true
  },
  {
    name: "SUGAR DADDY",
    symbol: "DADDY",
    decimals: 18,
    l2_token_address: "0x6b2014363e31c6f925bc7a215fbc13791c52d443aaf8192bf48f68880cefab",
    sort_order: 1,
    total_supply: 1e14,
    hidden: true
  },
  {
    name: "DGSV",
    symbol: "DGSV",
    decimals: 18,
    l2_token_address: "0x26a9be916b7af7caff4e6972935931bb8322b92ac331f4a4e205b2aebcd433f",
    sort_order: 1,
    total_supply: 2e10,
    hidden: true
  },
  {
    name: "stark panda",
    symbol: "panda",
    decimals: 18,
    l2_token_address: "0xb01087b73607661014e7c343cf5303f22afa781579c3d2f7b407db4ccb1f13",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "Where is Bob?",
    symbol: "ALICE",
    decimals: 18,
    l2_token_address: "0x39c8748b52517d1e0a716a773791301440b14fac7432486f8ed1e3ac9d0c89e",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "FOR EVERYBODY",
    symbol: "PARTY",
    decimals: 18,
    l2_token_address: "0x7ea98820329b39970fb54242d4fce993e20bfc3745ffb3f029b8e5eeacff764",
    sort_order: 1,
    total_supply: 666666666666,
    hidden: true
  },
  {
    name: "Tehran",
    symbol: "TEHRAN",
    decimals: 18,
    l2_token_address: "0x1214df013be9559a32d9e30bcdff3e370f88c72668e5910fb4f6eece2e6f9df",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Senator Thune",
    symbol: "THUNE",
    decimals: 18,
    l2_token_address: "0x4a0662c3a6a8538d382480e8785daf435e2b6e1a6a6dcb1329bac041dec4fe",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "Grok AI MEME",
    symbol: "GROK",
    decimals: 18,
    l2_token_address: "0x6f4e00fdf84918effc29280b56c564af179dfb9383942cafd4fb7f8e3104e5a",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Lifeless Layer",
    symbol: "DEAD",
    decimals: 18,
    l2_token_address: "0x9d1cba7a4ae6f44c0b7a859c412bcf462b872c99acdca63626dafa1d6013b3",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "BullStark",
    symbol: "BULLSTARK",
    decimals: 18,
    l2_token_address: "0x712077a6c0264fed5e749ed2b69496c031be748e7d4efd9a0b2bf776696a72f",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "SPACE",
    symbol: "SPACE",
    decimals: 18,
    l2_token_address: "0x5227522b86b06062c7f44cf60baee100bd788e9ca6bb9fc75b29599cae1a3f9",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "STARKNET APE",
    symbol: "APE",
    decimals: 18,
    l2_token_address: "0x5c29ba892ca4518cfcfa0fcb66aee8ac45a897b7fe70b2b97c95a007bccd501",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Stark Man",
    symbol: "STARKMAN",
    decimals: 18,
    l2_token_address: "0x2703152631bb82dbaff042f988dc5bf0642ee30adbd4f9791d25936d498a6d",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Stark AI",
    symbol: "SAI",
    decimals: 18,
    l2_token_address: "0x35b78e175e64ad557096e31e461b35751198bb3402cd2044eca1a6b1d38a064",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Drink Water",
    symbol: "LIQUID",
    decimals: 18,
    l2_token_address: "0x584c49ad3fd294dd13121f60cc6faed03ac0b06141bb1b1ab512120e9b36caa",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Stark World",
    symbol: "WORLD",
    decimals: 18,
    l2_token_address: "0x1e23d4119876ff57159990e8563886335003000bed67947da4cd32f84430dbb",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Always Bullish",
    symbol: "BTC90K",
    decimals: 18,
    l2_token_address: "0x73ddc4533c88d9083d859b7f16617258a2d17fccda33ede60035a41c02694e7",
    sort_order: 1,
    total_supply: 21e6,
    hidden: true
  },
  {
    name: "Not APTOS but APT Dance",
    symbol: "APT",
    decimals: 18,
    l2_token_address: "0x69638c0f05012e2bc3f1e4d5d1a370271408c8acf2fc0ccdafe49c0bb2b0dc8",
    sort_order: 1,
    total_supply: 23e6,
    hidden: true
  },
  {
    name: "Brother Wif Hat",
    symbol: "BROWIF",
    decimals: 18,
    l2_token_address: "0x3b47996c89430938ba1ae11b911ddf460cced9eb629f0716b03c7492762190e",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Super Mario",
    symbol: "MARIO",
    decimals: 18,
    l2_token_address: "0x22b0066e043a55e8ef6d1f90d8903d435b16694dac5d8f80992d699dd27c29f",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Stark War",
    symbol: "WAR",
    decimals: 18,
    l2_token_address: "0x7cb336d104a58f9a3a9086ebe0d68af8e4ea5b02165022827b3c0069828ea90",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Peace",
    symbol: "PEACE",
    decimals: 18,
    l2_token_address: "0x24b5866447745760ad4001ab6161525c8d9567353e3d57756be36149ac582bd",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "2 WEEKS",
    symbol: "2WEEKS",
    decimals: 18,
    l2_token_address: "0x66becf0274df0ae48b6cbcf669c23a1c29dd9bcb4ba7c7d11eb0a1500771839",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Eclip",
    symbol: "Eclip",
    decimals: 18,
    l2_token_address: "0x5e84a7c084fd91673443e6e6c48f2cbb975911567fb3960b436df76f1234ed7",
    sort_order: 1,
    total_supply: 5e9,
    hidden: true
  },
  {
    name: "Dept of Gov Efficiency STARK",
    symbol: "DOGES",
    decimals: 18,
    l2_token_address: "0x5c2f536e0d8fc935a2d8e38baea3897d977a329b7d74012250b3dd07ea1c0c3",
    sort_order: 1,
    total_supply: 6942069420,
    hidden: true
  },
  {
    name: "Dwarf Community",
    symbol: "DWARF",
    decimals: 18,
    l2_token_address: "0xbaa00ab61c32278d180469dad7cdcaf170259f47136b2c66ab4dc825d9102c",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Community",
    symbol: "SRAK",
    decimals: 18,
    l2_token_address: "0x61ab993667cd50aafc08ecf33d58ef673736eaee918302973898c0ba961900b",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "OBOT",
    symbol: "OBOT",
    decimals: 18,
    l2_token_address: "0x27908cd756da1e5faf5d47a5d648a64be728ee6c255d87bd15aaecf80e41f81",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "memetark",
    symbol: "MTRK",
    decimals: 18,
    l2_token_address: "0x3b8baaa09d41d71d5898a5fd27acdbed0970c2faa43f6a3070d5b8fb48a5912",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "Starknet SISTA",
    symbol: "SISTA",
    decimals: 18,
    l2_token_address: "0x1e32cf9df3fc03f3e4a6c2afece2340e2920ef536f3e5e7cd6893f8d8fc4882",
    sort_order: 1,
    total_supply: 1e15,
    hidden: true
  },
  {
    name: "CULT OF BROTHER",
    symbol: "COB",
    decimals: 18,
    l2_token_address: "0x1f8e315b79077d246299768f464596cce8532261495268eccf445cac66d3f5c",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Yahoo",
    symbol: "YAHOO",
    decimals: 18,
    l2_token_address: "0x209c3b621a25a7febf3543609f7acd5d28753f1396abc384d6e0b7eb71a1a29",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "ChatGPT",
    symbol: "CHATTY",
    decimals: 18,
    l2_token_address: "0x4421ef4eff4012063ad53eac7eaa5a6ce6b5a7de30f0488efc2f82499affec3",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "stop COIN",
    symbol: "stop",
    decimals: 18,
    l2_token_address: "0x4d57ed39f52254319e3febb0fb959302dc18c5a2f13226b0d4a46932c2c87c5",
    sort_order: 1,
    total_supply: 5e9,
    hidden: true
  },
  {
    name: "TYSON ON STARK",
    symbol: "TYSON",
    decimals: 18,
    l2_token_address: "0x1beacf9d3be04ef66c5b0376c13900cfaead763a4cf82926a8043aaa1f68c33",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "DOCTI",
    symbol: "DOCTI",
    decimals: 18,
    l2_token_address: "0x59db411db129b9181361a620a8b55ae57a3f98a4b7ff18fddcc0edf29b89eff",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "@Starknetmascot",
    symbol: "1. SMC",
    decimals: 18,
    l2_token_address: "0x79a1dde4d8a7f77708db2fcc927669e05de19182d412293489b06d694455dd2",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "Aurum",
    symbol: "AUR",
    decimals: 18,
    l2_token_address: "0x8f7435acc75e6117c4c56e66b3c5ecb926d1b2554a9f8f139ebf9ab9f31ce5",
    sort_order: 1,
    total_supply: 999999999,
    hidden: true
  },
  {
    name: "BLOBERT COIN",
    symbol: "BLOBE",
    decimals: 18,
    l2_token_address: "0x157bde3c332c36e2f84786c4b46fc98e6e1502771e38b8e59873aaddd03a1bb",
    sort_order: 1,
    total_supply: 45e9,
    hidden: true
  },
  {
    name: "brat",
    symbol: "brat",
    decimals: 18,
    l2_token_address: "0x46dd17b05fbc1bb0525765208ff838f23c0623cbf050f065a66c6d5977710c7",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "Vitalik Buterin",
    symbol: "VITALIK",
    decimals: 18,
    l2_token_address: "0x436282665f885508c5da1e54fc36f81f0d3c9d54c07902b40cc5377d30dc024",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Blob Capacity",
    symbol: "BLOBS",
    decimals: 18,
    l2_token_address: "0xbe82f838c25b853aa1dff8b5f7c3eacd2be75294164332007cfbe9c2706e37",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "from solana to starknet",
    symbol: "FSTS",
    decimals: 18,
    l2_token_address: "0x1f439c84e163c84bc57a7b7c6daafcb8ee74d5bab3cade85a3b1c864f867b86",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "DOG/GO/TO/THE/MOON",
    symbol: "DOG",
    decimals: 18,
    l2_token_address: "0x1fa604e9fb5f710a7da1707b5da4dd53503300b4fd04f9f170347e053043efe",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "HoneyPot",
    symbol: "HP",
    decimals: 18,
    l2_token_address: "0x783c576328db42cae79a4498db25c8684a0e45d596dc90c730b3e93c3dd331e",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "HoneyPotV2",
    symbol: "HP2",
    decimals: 18,
    l2_token_address: "0x33d2fd2743e1ece4d81fdaaa58e29e97ca5bf050a49db23c541210d185601a6",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "DNA",
    symbol: "DNA",
    decimals: 18,
    l2_token_address: "0x4303b4dcb2c7d222de22e7051aa70239fb2adacdadbfbc900182fdb6bb49454",
    sort_order: 1,
    total_supply: 6e8,
    hidden: true
  },
  {
    name: "Eliza",
    symbol: "ELIZA",
    decimals: 18,
    l2_token_address: "0x588591ac59a2682611c89b4c1dfd9236ecc8b15007205b18b39e347ece5acee",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Eliza Ai",
    symbol: "AIELIZA",
    decimals: 18,
    l2_token_address: "0x44cd20548365827af7f309fa4da95f7ebb0f71f4d63b8e5ea057cdd0bd157e1",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "DESCI ERECTUSDAO",
    symbol: "ERECTDAO",
    decimals: 18,
    l2_token_address: "0x6b0789c52bde629ab8784d21cf545df9f61f774067d28af86e16e09ec07a254",
    sort_order: 1,
    total_supply: 6969696969,
    hidden: true
  },
  {
    name: "AZT",
    symbol: "AZT",
    decimals: 18,
    l2_token_address: "0x7a1198e28b3111014f6270fbd0814556462a46e32bb8a27a91b3f06a3ed5100",
    sort_order: 1,
    total_supply: 1,
    hidden: true
  },
  {
    name: "R4MI",
    symbol: "R4MI",
    decimals: 18,
    l2_token_address: "0x39b8b68e20da56137782b6a92b44f416494d2d238b4c691b3866d5ca38566a",
    sort_order: 1,
    total_supply: 1,
    hidden: true
  },
  {
    name: "R5MI",
    symbol: "R5MI",
    decimals: 18,
    l2_token_address: "0x721f3b35a5f0c6057a937387ce709776074cd83d3580923a8e9859dc37bb32e",
    sort_order: 1,
    total_supply: 1,
    hidden: true
  },
  {
    name: "Starknet Mascot",
    symbol: "STARKMASK",
    decimals: 18,
    l2_token_address: "0x1b7c2cd6c4fe74883100e5214c412f5f901e4de0a84f4188d5170d76bb46fc",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Make Starknet Great",
    symbol: "MSG",
    decimals: 18,
    l2_token_address: "0x1328028215e1f329a3f8581bcd423427369a86500a1402f5e09e85765c9b83b",
    sort_order: 1,
    total_supply: 420696969,
    hidden: true
  },
  {
    name: "RODBUZ",
    symbol: "BUZ",
    decimals: 18,
    l2_token_address: "0x60222a76d706b3dd901ac211add7c383792956f52d4bccbefcc13368a862128",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Chacloa",
    symbol: "Cloa",
    decimals: 18,
    l2_token_address: "0x171c13871f84f2cc152f4621f228c95453575755d46bff19723a52bfe69581e",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "Balan",
    symbol: "BAL",
    decimals: 18,
    l2_token_address: "0x4f4550d641858babd13ccd7fcd8ce2678e3700c3a625d7c629d2f4bdeef7d2c",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Rede",
    symbol: "Rede",
    decimals: 18,
    l2_token_address: "0x74822ec91d4fbfd9e8190236762a30e9bfcaa59b5e630429595e5ae48c92fa1",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "Gen Z Quant",
    symbol: "Quant",
    decimals: 18,
    l2_token_address: "0x5fa014e8120473d2bb24850ff45c3ce29d6666590f7e40e1ba561f5abd006a1",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "downtime",
    symbol: "down",
    decimals: 18,
    l2_token_address: "0x3309775c485b720014e29b555b5acd8d6efca992c9729c0cb333935700aba42",
    sort_order: 1,
    total_supply: 8e7,
    hidden: true
  },
  {
    name: "Union",
    symbol: "UNION",
    decimals: 18,
    l2_token_address: "0x6fffd3a715da895d01739373a548d05becc979083b6fbd92ab615dc75cc5471",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "AverageFrenchMen",
    symbol: "AFM",
    decimals: 18,
    l2_token_address: "0x6c7634564e7f15e96e7fa062f55dcc2b65077e914c7693912eb1c884abd60e6",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Manlets",
    symbol: "MNLTS",
    decimals: 18,
    l2_token_address: "0x4bd541b08b7e5e2e34b49ac0c1f81dd3997bd66a5a0f5c75abcfbf57b331987",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "Move To Starknet",
    symbol: "Move",
    decimals: 18,
    l2_token_address: "0x4c98e58b7327858d78e40beaf6186f6a0df234213aa8a030655f9679baa6f7b",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "BROTHER VITALIK",
    symbol: "VITABRO",
    decimals: 18,
    l2_token_address: "0x416f8657e8db6ff5b26493a230e510b3d268e4ba1dac8126be3b8954aaf3af7",
    sort_order: 1,
    total_supply: 6942e4,
    hidden: true
  },
  {
    name: "Average French Men",
    symbol: "FRENCH",
    decimals: 18,
    l2_token_address: "0x53104209be3a8795d424b4370d4d111db55ce3a8896cfb1f1973680a7ec44cb",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "REAL",
    symbol: "REAL",
    decimals: 18,
    l2_token_address: "0x5458de22a774c15d094f3b55966c354522e4a78d8dd533ff6eefc04a8fc39a6",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Sad ETH",
    symbol: "SAD",
    decimals: 18,
    l2_token_address: "0x6879b16073f66341f7c853e1bc7706d3a64cccd79815da3f0b303a58adcac9e",
    sort_order: 1,
    total_supply: 1e15,
    hidden: true
  },
  {
    name: "Starknet Pump",
    symbol: "STARKPUMP",
    decimals: 18,
    l2_token_address: "0x430fd26f25235293d7c55a42cb285489f0706f67b2d891d36b671fbe1a36276",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Cult of the Pump",
    symbol: "CULT",
    decimals: 18,
    l2_token_address: "0x506cd82db64a84fc805a36ee12bd87957f1701d7b7cf2ab06e0068ab7b1a5fd",
    sort_order: 1,
    total_supply: 666666666666,
    hidden: true
  },
  {
    name: "ELI BEN-SHITCOIN",
    symbol: "ELIG",
    decimals: 18,
    l2_token_address: "0x51cf0e830329f9f93fb094f5fe7c28bd5671d0c1633a4c200c8857ed5ebdcae",
    sort_order: 1,
    total_supply: 42e10,
    hidden: true
  },
  {
    name: "ELI BEN-SHITCOIN",
    symbol: "BEN",
    decimals: 18,
    l2_token_address: "0x55ae94eabf88d5c5c8f1ed83083a251e8ece9c58f9aa71c947a205dd11336bd",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Other (Not Brother)",
    symbol: "OTHER",
    decimals: 18,
    l2_token_address: "0x64b852ad8247f7c774fd07700d4021138a1a422d247c5fd442a664dc22fb4aa",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Name a cuter dog",
    symbol: "CHIHUAHUA",
    decimals: 18,
    l2_token_address: "0x4d2c6c7b9659cb8e4c636101110f75a365b4b530d80862db5cb833777d70281",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "StarKCats",
    symbol: "SKC",
    decimals: 18,
    l2_token_address: "0x3da6062808aabf5d90bcda697f55e87fca507fa9cfed5381ac62f1f4cb5690c",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "just a chill guy",
    symbol: "CHILL",
    decimals: 18,
    l2_token_address: "0xfe6602ac6e931b6620e8255a994e58f1998d4687eb8f7d599534da521c3a01",
    sort_order: 1,
    total_supply: 42e10,
    hidden: true
  },
  {
    name: "PIPOCA",
    symbol: "POKA",
    decimals: 18,
    l2_token_address: "0x8adb05296d4489faecb5e2e29e25c5c6115b58c9c0b99a1fff4d23d34aff11",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "BTC100K",
    symbol: "BTC100K",
    decimals: 18,
    l2_token_address: "0x2c78ec0858c20556d897cf0da3a22d60554987cf0a407c6c885913c18e82bd0",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "TeT",
    symbol: "TET",
    decimals: 18,
    l2_token_address: "0xd1fe1eb2ea8665717c3430bfc30d040815e1647f5305f03f0f6b0c5520b3ba",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Elizabert",
    symbol: "LIBERT",
    decimals: 18,
    l2_token_address: "0x7488e8941f6d9c3fd1cd2d04e147360dc5d85f159f03a16e85b8449089af43d",
    sort_order: 1,
    total_supply: 4844e7,
    hidden: true
  },
  {
    name: "RallyOnStark",
    symbol: "RALLY",
    decimals: 18,
    l2_token_address: "0x56bab524eac8dd789f6bbcf2967ccbdba1b55e055c925a236d9331c95a0d4fe",
    sort_order: 1,
    total_supply: 88888888888,
    hidden: true
  },
  {
    name: "StarkCaTs",
    symbol: "SCT",
    decimals: 18,
    l2_token_address: "0x521324b728612c1465038c2efee6f485ab64b769b82100ac4d8b52ea5d1bfe7",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "GoingDeep",
    symbol: "GGG",
    decimals: 18,
    l2_token_address: "0x3213a184917c0acfa637e725a58c4b53467caafba29e6ee9cdac6ccc5555b24",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Twony Stark",
    symbol: "2NY",
    decimals: 18,
    l2_token_address: "0x71d6df8de9fc2c115cc007559bc4fe72a9925a38e688fddb1903b626702282",
    sort_order: 1,
    total_supply: 1e17,
    hidden: true
  },
  {
    name: "BUU",
    symbol: "BUU",
    decimals: 18,
    l2_token_address: "0x65d8fbc83914209e3e563bef83418a669903dbd81f892ff0cc6ece384733c71",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Snoop Starke",
    symbol: "STARKD",
    decimals: 18,
    l2_token_address: "0x4164f16bf7939438b08054ca0dffecef4420ba91f9e682b265af1a5af89b6a6",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "KRILLIN",
    symbol: "KIRL",
    decimals: 18,
    l2_token_address: "0x671538d43a370dc39e76ec3645a7e549111fbc7c1d0cddb8e3414dc48b5c176",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Starknet Eye",
    symbol: "EYE",
    decimals: 18,
    l2_token_address: "0x519d14c0a99a3b50577ce7d047e15343a5be6d0d91c709bb8009cda7adbef90",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "Vitalik Vibez",
    symbol: "VIBE",
    decimals: 18,
    l2_token_address: "0x65606549b64dd50763be728f4411714183116fa4900f343dd415345465b263e",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "McDuck",
    symbol: "DUCK",
    decimals: 18,
    l2_token_address: "0x32bfb2adc4896c09932b04595563516bbcce0e0f85c6b478f820c176989a018",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "UMAN",
    symbol: "UM",
    decimals: 18,
    l2_token_address: "0xdb739db9337345cce6e830d0b4932da6764a858711adceea4944c4b39e8549",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "RACA",
    symbol: "RACA",
    decimals: 18,
    l2_token_address: "0x30c00bbc964dcbd54d36ca95814b57e031c47528aad0c10813b7c42a13a7110",
    sort_order: 1,
    total_supply: 1e14,
    hidden: true
  },
  {
    name: "GoingDee",
    symbol: "GDG",
    decimals: 18,
    l2_token_address: "0x219a9f8dc4078f16046ee96fa8b4cbc85f415d291fdf551c451a2d1b72503ea",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Hair",
    symbol: "HAIR",
    decimals: 18,
    l2_token_address: "0x1fb4ad131c0a4678a3385076b48489edda3191f7ac2eb068f3fef9a0fe4e920",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "MOON",
    symbol: "MOON",
    decimals: 18,
    l2_token_address: "0x787436bf242e8230183d1e042f4d0f5e5b57ac7d25d07c0e5a1852a66c2edc1",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "ANONai",
    symbol: "ANON",
    decimals: 18,
    l2_token_address: "0x579c8f756e59d9a39b19bf4d1f918a72247bf74af67f8fc99d921528c10510f",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "Cheap",
    symbol: "Cheap",
    decimals: 18,
    l2_token_address: "0x4785923665019ba36161aaf9f842edac8eb17e475ab91d948fdc6affe48047f",
    sort_order: 1,
    total_supply: 1,
    hidden: true
  },
  {
    name: "notREAL",
    symbol: "notreal.",
    decimals: 18,
    l2_token_address: "0x3444005b8871bb7d3ac2a87df978724bd5de52284e88e0ddbdb86738ba34f82",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "GoingLee",
    symbol: "GLG",
    decimals: 18,
    l2_token_address: "0x7eb377670445d4d1f0ecc69049708acbd60e3d422a3f22214d221f9e6c0b2f0",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "KEK (ansem tweet)",
    symbol: "kek",
    decimals: 18,
    l2_token_address: "0x67290e9329229b701128daa29c7d8463ae5dba867ef67e17199f5d840596e8",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "MUSTARD ON STARK",
    symbol: "MUSTARD",
    decimals: 18,
    l2_token_address: "0x69c9114d57851438f5dc526c29343eff0ce1ff989bdbe01729358e64a4aae6d",
    sort_order: 1,
    total_supply: 6969696969,
    hidden: true
  },
  {
    name: "Starkware Tech is Legit",
    symbol: "SLGT",
    decimals: 18,
    l2_token_address: "0x7190ce3525b277c395cb4b34e0eccee1673617725c0146235d9addfbc6d8ce1",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "LEAP",
    symbol: "LEAP",
    decimals: 18,
    l2_token_address: "0x65e8eb7aeefc58a704e36b3da917a67a7330b9ea847b06d1a9461beb9ebe9b9",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "GoingWee",
    symbol: "GWG",
    decimals: 18,
    l2_token_address: "0x1cf48a34e87396512077980a0e0f393c9dc31a4bed6b947273b46d6e0004b7a",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "HI BESTIE",
    symbol: "BESTIE",
    decimals: 18,
    l2_token_address: "0x6c9a144ac2d8d0749bd4fff28f51d5de512b0d6b81c94e8522c2a8884ded164",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "StarkBot",
    symbol: "STARKBOT",
    decimals: 18,
    l2_token_address: "0x322383b3f9d1af55e660b4e9ad82d16f92f4fc92b0553371dac3e36b7f6b9aa",
    sort_order: 1,
    total_supply: 5e9,
    hidden: true
  },
  {
    name: "HODL Meme",
    symbol: "HODL",
    decimals: 18,
    l2_token_address: "0x1e751081f3585b9c5970b8088102ddb62ff60909d0cdffa5c5c4b65e8ccd4d3",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "Starkware Tech Legit",
    symbol: "STL",
    decimals: 18,
    l2_token_address: "0x42fd2d1521ceaf8b365d43c49c10b17768c70e914bd378852b802724947eb8",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Sing",
    symbol: "SING",
    decimals: 18,
    l2_token_address: "0x33b19fb927f466e7b6c5983d04cfbafd9a4ec5c61d7f722a56b9585d232a7f8",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Starknet Steak",
    symbol: "STEAK",
    decimals: 18,
    l2_token_address: "0x1dd1462a7e09f9f1951af96eba8fd66f96ecba65bff55019c5f3d53962b3704",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "MascotAI",
    symbol: "MAI",
    decimals: 18,
    l2_token_address: "0x42266eeccb435e7a8c1792d66f8e78169bd232f8d60c079e6187b0bf5b743e6",
    sort_order: 1,
    total_supply: 98989899898,
    hidden: true
  },
  {
    name: "X Numero Uno",
    symbol: "XNU",
    decimals: 18,
    l2_token_address: "0x1fb9de3b04dc648cf5ad1e0289f96e1fee799d6347ca34526f6f6b1f6499003",
    sort_order: 1,
    total_supply: 1e13,
    hidden: true
  },
  {
    name: "harihari on Starknet",
    symbol: "HAST",
    decimals: 18,
    l2_token_address: "0x38aee488d72c66c799a39fc3b6521376fd34ae6b79e7eeb9ee590233861b2f0",
    sort_order: 1,
    total_supply: 28e6,
    hidden: true
  },
  {
    name: "snake stark",
    symbol: "snake",
    decimals: 18,
    l2_token_address: "0x6700b484810397e8900c06d775f213714f6ddb9befedf98995fec23331ca994",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "Game Bot",
    symbol: "GB",
    decimals: 18,
    l2_token_address: "0x23b8cd79850b8286fafca8df0a30d1648bb16d74a4e179d184a050184f6c128",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "BlueFalcon",
    symbol: "FALCON",
    decimals: 18,
    l2_token_address: "0x67a02a34e87de765d471b245ec8ae5806429a59d1f897b7c7c5f31859c7dddc",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Billion",
    symbol: "BILLION",
    decimals: 18,
    l2_token_address: "0xaddebf5f0d8b7e81e38d7f181b281b10f820036718a55360fff2a51ec1614a",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "SuuuCoin",
    symbol: "SUUU",
    decimals: 18,
    l2_token_address: "0x2cc3a6fc3f7ac78b19a18dcf73605cbeef19433af6456c0ad97544282ac4265",
    sort_order: 1,
    total_supply: 1e14,
    hidden: true
  },
  {
    name: "Kraven The Hunter",
    symbol: "KRAVEN",
    decimals: 18,
    l2_token_address: "0x3ddc51ad5ce946ea031e1b39804db03764a824cc8b3952f783a14395c4bf1fa",
    sort_order: 1,
    total_supply: 999999999,
    hidden: true
  },
  {
    name: "StakNinja on Staknet",
    symbol: "StakNinja",
    decimals: 18,
    l2_token_address: "0x50f366c3b99272c86c10bafa18ae142c8eae83851733d9abcac6673c177f7d2",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Waky Waky",
    symbol: "WAKY",
    decimals: 18,
    l2_token_address: "0x8ed052f30d7eb3af3b2a5723b8f312bf55025c363e8691583a02860286c88b",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "asdf",
    symbol: "ASDF",
    decimals: 18,
    l2_token_address: "0xccbe0eaac7de9f271d02ccbd6627d833f552a7440d7774ff29450228b93f61",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Nikagold",
    symbol: "Nika",
    decimals: 18,
    l2_token_address: "0x5f5f19ce4bc77523d3ed048d199b462a4c7591689e53c7d039d02ade39a2d7c",
    sort_order: 1,
    total_supply: 1e16,
    hidden: true
  },
  {
    name: "Fuck Pump.Fun",
    symbol: "FKSOL",
    decimals: 18,
    l2_token_address: "0x15019b595a70f864a998c1384afc2bacb55fcd68e60ea1ff87b25c9bc4b03d0",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "BALLS",
    symbol: "BALLZ",
    decimals: 18,
    l2_token_address: "0x3012e3dd536df963385b87f2aad923f79ea5fc28a7803a09680e247a67df905",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Eustass Kid",
    symbol: "KID",
    decimals: 18,
    l2_token_address: "0x5d8d223a4c5dc2849c12e5a38c389e11291c3dca019c352cd68454be9139c6c",
    sort_order: 1,
    total_supply: 3e9,
    hidden: true
  },
  {
    name: "TetaFans",
    symbol: "TEDA",
    decimals: 18,
    l2_token_address: "0x38d8ddc79f78dfff010b1c3952ed48d4cb505d98be727084b6d8068ef84574a",
    sort_order: 1,
    total_supply: 101011111011,
    hidden: true
  },
  {
    name: "Starknet Chicken",
    symbol: "CHICKEN",
    decimals: 18,
    l2_token_address: "0x6a24725b18efdfbd1b1bb04cb441d7983938cb260baf5e023275d37f03a7a89",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Death",
    symbol: "DEATH",
    decimals: 18,
    l2_token_address: "0x2ad4886ff4121581ab786eba9d596790052b7fdeacda9a9df3048982f14d565",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Eliben Hub",
    symbol: "ELIHUB",
    decimals: 18,
    l2_token_address: "0x3c53966872f58a47435d6556ae1734beed6bbb980fe70a716bf117408637f2c",
    sort_order: 1,
    total_supply: 1e13,
    hidden: true
  },
  {
    name: "Saboby Protocol",
    symbol: "SABODY",
    decimals: 18,
    l2_token_address: "0xcb83ccd8e01a89be436f87c0500bcd9a842de8d556d8c84703c96ddaf923f8",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Smithii Tool",
    symbol: "SMIT",
    decimals: 18,
    l2_token_address: "0xb8467a2ec3122965822510e7f070b8a21c16b06167870d75df06b71f5a2b10",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "WinterWags",
    symbol: "WAGS",
    decimals: 18,
    l2_token_address: "0x2a2dd66469098df42d460927e05db6aabbd87f623f6f0e8f0f3072cca63bc8c",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "JavierMilei",
    symbol: "AFUERA",
    decimals: 18,
    l2_token_address: "0x33b2414991ded7f43605b0913d05ca821acdddb44cf224b85e57e4366e6cfa0",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "right",
    symbol: "rght",
    decimals: 18,
    l2_token_address: "0x1b5e910d548ede86a63ee16c826a69509669d2f27ebabd60aff1561b6b52a1a",
    sort_order: 1,
    total_supply: 1e13,
    hidden: true
  },
  {
    name: "Monkey Fighter",
    symbol: "MONFI",
    decimals: 18,
    l2_token_address: "0x71e5c6461a1da65d3c5b436214223283a7203eb0ae54225e4bbf48358cbdffb",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Chubby Hamster",
    symbol: "CHUBBY",
    decimals: 18,
    l2_token_address: "0x5fe2077a9d3fe663fd50be8a3ae0840ac2f5f8899d0cb07ea34425c91c5a2d3",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "We're All Gonna Make It",
    symbol: "WAGMI",
    decimals: 18,
    l2_token_address: "0x661772bdfdcfe809d803c16c1d210eab8758e76233878339589057ae5eaa377",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "Woofy",
    symbol: "WOOF",
    decimals: 18,
    l2_token_address: "0x55e3d75ddeb3d1c77e9439335a77a21be83e2d46ac999f26a3551997502c215",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "TurtlEx",
    symbol: "SHELL",
    decimals: 18,
    l2_token_address: "0x3b24bcfea377aa8d794ca61221b14df1fc25325bea2767e865c582d988d8156",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "Realms World Coin",
    symbol: "RWC",
    decimals: 18,
    l2_token_address: "0x2407e0efdd5ebd03f380f792e1aeceb38427975459a2ab04bef9d7d39c95207",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Fack You Meme Coin",
    symbol: "FYMC",
    decimals: 18,
    l2_token_address: "0x547fc4876be116d6fdb8000883a55e8b110a563f65d59b6672f12824313f01b",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "Siri Bot",
    symbol: "SIRI",
    decimals: 18,
    l2_token_address: "0x7792863af2b9f6d15ff09ddd4c5e808c056e43e5ca7a7ae1e82776544ec6962",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "MadDog",
    symbol: "MDOG",
    decimals: 18,
    l2_token_address: "0x7d0b535f01f27b79ec68eba2268698e5671226b7dbc425b42caa46254eea078",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "WhaleFUD",
    symbol: "WHALE",
    decimals: 18,
    l2_token_address: "0x34fc3c18e5efb96115136d721c8a9e320a3a0a93be890c09cd7bde4ca8781f0",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "ReStark",
    symbol: "RES",
    decimals: 18,
    l2_token_address: "0x29cc3205aaf563656b9d4e5455ebc7c6e62f85da518e6d250b456a6b4424d3e",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Snake Wif Hat",
    symbol: "SKWH",
    decimals: 18,
    l2_token_address: "0x13bab8ae6702e7e0b50615a8e157216b7660e4036238485c54958262addd255",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "THE",
    symbol: "THE",
    decimals: 18,
    l2_token_address: "0x63f813f6e243ba4b1f3cf24f0d2a3ef5f65e96fa8074d801a7244c53e4d5708",
    sort_order: 1,
    total_supply: 1e5,
    hidden: true
  },
  {
    name: "Stark Cooking",
    symbol: "COOK",
    decimals: 18,
    l2_token_address: "0x5e602efc13d8bb720286681e1e3673e54b53767b900c5db46e68d271d441e5a",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "Silo",
    symbol: "SILO",
    decimals: 18,
    l2_token_address: "0x16954c95dac6b49686785b5e2aa56ff9d8f3eb3aac9f534deeb8e75588457a2",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "CatNado",
    symbol: "CATO",
    decimals: 18,
    l2_token_address: "0x176553942cbbe4d6e44e651a4412cb4608996a4b00725f7ba1a219d770bd563",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Astro Hamster",
    symbol: "ASTRO",
    decimals: 18,
    l2_token_address: "0x341467a6af4a7e80eabc0102af3d551060ed0b5a88b75b93c28c6b9dc277bd5",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Moon Pug",
    symbol: "PUG",
    decimals: 18,
    l2_token_address: "0x41da24b3a7db2389b1cb4000584f0a220648e60849bd61e0d326c2098bd733e",
    sort_order: 1,
    total_supply: 222222222,
    hidden: true
  },
  {
    name: "BROTHERCYCLE",
    symbol: "BROCY",
    decimals: 18,
    l2_token_address: "0x1a4dabff3ec45f830fb766d4a82a6e0b54c1dd917f2e77d463e22a03e88d815",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "Shrek Coin",
    symbol: "OGRE",
    decimals: 18,
    l2_token_address: "0x3e38d0b68e3938a781c66b8b8a90a2bd125bc73d591c439f3b10e405367f14a",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "TOAD",
    symbol: "TOAD",
    decimals: 18,
    l2_token_address: "0x638119baac8cb130fc0ef8b61ccb81c57f7eb9691c53b7e5f191d0c91217445",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "SHILL",
    symbol: "SHILL",
    decimals: 18,
    l2_token_address: "0x34ce77b5280566db16dc9daf176372b6307f1045b4bc8c8ceb65a3d81be22f2",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Neuro Block",
    symbol: "NEURO",
    decimals: 18,
    l2_token_address: "0x3f518ea83183f549d6f96bbe16241070276860afe74672a71df49e579d61c5e",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "AI Link",
    symbol: "AIL",
    decimals: 18,
    l2_token_address: "0x646e9346b27afbd49ddb4966dd3b59c8d280b40e423032248299e176906c128",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "MOAR",
    symbol: "MOAR",
    decimals: 18,
    l2_token_address: "0x6cc51293c2d31f23b19cc1611fb5d6b1fb1d815a1009df1e63d16af6e99758a",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Crypto Mind",
    symbol: "MIND",
    decimals: 18,
    l2_token_address: "0x1c1ae51f9b2d52afb7ace0fae66a42c9ffb9c638bd78735ed7ab421c3b170cd",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "StarkGreen",
    symbol: "SGREEN",
    decimals: 18,
    l2_token_address: "0x5b414934170dd7b58737d8e78d7220d42acb6e34601fae71c39dff029d7df75",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "VERDANT",
    symbol: "VERD",
    decimals: 18,
    l2_token_address: "0x72cc48322fc00be317e06caea000df2ec36680198de480b8c3d199579b1ed1d",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "Everai",
    symbol: "EVERAI",
    decimals: 18,
    l2_token_address: "0x71d037fc93e0681af6608fcc93d5acad9dd7c06f13ac965221e178fa6ab2754",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "BIO NETWORK",
    symbol: "BIO",
    decimals: 18,
    l2_token_address: "0x73221cbd54c8308a7d62288e47d17280739e08f32a2e1d573892e1124541050",
    sort_order: 1,
    total_supply: 3e9,
    hidden: true
  },
  {
    name: "Sustain",
    symbol: "SUST",
    decimals: 18,
    l2_token_address: "0x648f84bd12e6145ef9895c446d51438fdf665f50ef301df3016e4737753fb04",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Earth Core Team",
    symbol: "CORE",
    decimals: 18,
    l2_token_address: "0x73b9b84e755b459383513e665398ce280b8a7cc5872d70e1690c2365f609fc9",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Grateful Coin",
    symbol: "GRATEFUL",
    decimals: 18,
    l2_token_address: "0x742aad0cb8ef0f7f9f7b1eb93e063512cbcf8aaf93f3db2ed3418927cb9da5d",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "TableFi",
    symbol: "TABLE",
    decimals: 18,
    l2_token_address: "0x2457e45ff72514784fe56b3d91167f6549657b3aa53f28940963da876f1b887",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "Tiny Paws",
    symbol: "PAWS",
    decimals: 18,
    l2_token_address: "0x14f05c874baed361ce5fe4957cc3cc4c7a2c2c065b907c57c9b2c5d7e3f2da4",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Zero Energy",
    symbol: "ZERO",
    decimals: 18,
    l2_token_address: "0x59e9d6b43dd95bb6eb3e7e71096aa29ef4c7b97acdcbf07ed067251e5348ef8",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "BRANCHES",
    symbol: "BRANCH",
    decimals: 18,
    l2_token_address: "0x7fc82de071c5c5c7ffff230e36a9e21fdedcdf91fa68572f709449d6b59161",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Baby Sister",
    symbol: "SBSTR",
    decimals: 18,
    l2_token_address: "0x4f11c885ea54130367018c0ef9b94f94c1bb94f397d8e6bfb09f60adb72d0d7",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Quack Quack Quack",
    symbol: "QUACK",
    decimals: 18,
    l2_token_address: "0x5eb8073f2e2d128e89ebdf30e00b229df8931372690423115a2ec7fac918f13",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Eureka!",
    symbol: "EKA",
    decimals: 18,
    l2_token_address: "0x2a6da4abce1f8c75f2d3d861e0979675d534b8f2e8d6fd1f105411568073ffe",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Unity",
    symbol: "UNITY",
    decimals: 18,
    l2_token_address: "0x4b65e31d6a61e04252d2c90d77133c7d9576027cd9a37204734b958a7052a13",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "CycleX",
    symbol: "CYCLEX",
    decimals: 18,
    l2_token_address: "0x20daef0fb6a11d13935162e8cc40fc526ea4f0f91f2e9ae071a628d776ac2d4",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "Stark dot com",
    symbol: "STARK",
    decimals: 18,
    l2_token_address: "0x51e9b26e8c6ac0e1e199d140b0fd107a9a9a6ec82ff03cbdee045b34328d093",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Hold My Beer",
    symbol: "BEER",
    decimals: 18,
    l2_token_address: "0xa6fb19553321d8c34a46d641002dc17b287242946728f54f68704baa263258",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "Vault Storage",
    symbol: "VAULT",
    decimals: 18,
    l2_token_address: "0x1428d80df927b5adcc9c7b84d145f579120699e634c4b19c965f2cd76a7553a",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "Starknet Mommy",
    symbol: "SMOM",
    decimals: 18,
    l2_token_address: "0x31bcb2f62f9339dcf181ca54f9c85601a0b411af02cc58c19c96f16e5ab35ad",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Battle of The Wolves",
    symbol: "BTWS",
    decimals: 18,
    l2_token_address: "0x4f09ad3b23ce9c536eafa9341ecfd91669f0f4db9eabcbd5b8e21e872464b28",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Kinship",
    symbol: "KIN",
    decimals: 18,
    l2_token_address: "0x285546d1c3cbc245ae1754cbdf9a103235aac7767dd7d9dc34f01a35baa5a09",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "MEOW",
    symbol: "MEOW",
    decimals: 18,
    l2_token_address: "0x770ff8aee37a26a1c00d35c5536aaf562fa649143a4ee51a392cb963a4423f8",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Serenity",
    symbol: "SERENE",
    decimals: 18,
    l2_token_address: "0x51205192dc9f982561a7a77f3ad01572ecfc09f3c3441ce3211149ab580a689",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "mtnToken",
    symbol: "MTN",
    decimals: 18,
    l2_token_address: "0x2a9caa0ec2d02a9621bffb12ae90f65f120c74149e4a71e2601b8a39976c8e2",
    sort_order: 1,
    total_supply: 100,
    hidden: true
  },
  {
    name: "zkSynk Brother",
    symbol: "ZKBROTHER",
    decimals: 18,
    l2_token_address: "0x2835f7999c1375d1ddd4186f1152adcbb26db9c0367577e72a0e49257fb12ff",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "Evergreen",
    symbol: "EVER",
    decimals: 18,
    l2_token_address: "0x5f0b6e7c0cf8ea59331395280ba0cad75eb4b01b46932e4aae0b8fad55c177b",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "DEGENBER",
    symbol: "DEGENBER",
    decimals: 18,
    l2_token_address: "0x5f29d3f2ab0282cbf644aee72cfd4731e1dab197fd376cda2abfbd9efb5d5d7",
    sort_order: 1,
    total_supply: 1e14,
    hidden: true
  },
  {
    name: "Cambodia",
    symbol: "CAM",
    decimals: 18,
    l2_token_address: "0x64029c82efd22f583bdf23b75dcea77673dd16458cec045d7a549ee245a420f",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "SOLACE",
    symbol: "SOLACE",
    decimals: 18,
    l2_token_address: "0x33441dcab91e4426c27f1ee2b36b1a103eec15cae88c6bbd1602c2866f0b69b",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Brother Man",
    symbol: "BROMAN",
    decimals: 18,
    l2_token_address: "0x74dcf958ea37311e4d5f029374fddf549078c70c6cbdcd62a449fde849254b",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "API Bara",
    symbol: "BARA",
    decimals: 18,
    l2_token_address: "0x143fd5508e81493afa078b23f01a2197f016a4dff9a9a5782a974cbf7348861",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "The Integrity Web",
    symbol: "IW",
    decimals: 18,
    l2_token_address: "0x56ef2ae8dd94ac3fe027481a67981a1ff1e0968f53ac24a646ad80fb8fcf5a9",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Starknet India",
    symbol: "INDIA",
    decimals: 18,
    l2_token_address: "0x257f428afdaf9ce19ebb7628e0d8fbd08c103d4b52893d6976c0937a0fa92d5",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Bitcoin ZK Starknet",
    symbol: "BZKS",
    decimals: 18,
    l2_token_address: "0x7fdf93c350553d4947f42472d6877b3fd8b2bf33718128778f3971743da749b",
    sort_order: 1,
    total_supply: 1e13,
    hidden: true
  },
  {
    name: "BROTHER FRIDAY",
    symbol: "BFRIDAY",
    decimals: 18,
    l2_token_address: "0x1778e966092db793be393c279789e68df760c1f6e1deb8da0e724e751286ccd",
    sort_order: 1,
    total_supply: 88e10,
    hidden: true
  },
  {
    name: "Nova Land",
    symbol: "NOVA",
    decimals: 18,
    l2_token_address: "0x5e0987a59232cd7afed8b55408d8fe5894d2fe6bc0c595f9d5668bfa9f10c3a",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Odyssey",
    symbol: "ODYSSEY",
    decimals: 18,
    l2_token_address: "0xc3b849e153534372187ff1a566c6df976a475d5bffa53a6c50e2bb4f56443",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Happy Thanksgiving",
    symbol: "HTG",
    decimals: 18,
    l2_token_address: "0x4fd140bddce24e73469c9781d628bc7391087fbb13dab6302ef3ba7f9a9dbd8",
    sort_order: 1,
    total_supply: 1e13,
    hidden: true
  },
  {
    name: "Qoo",
    symbol: "QOO",
    decimals: 18,
    l2_token_address: "0x7e9ae809318482d43e07e2761b48abde2c389407dbc7a5fcd5945b36bd9bc86",
    sort_order: 1,
    total_supply: 99999999999,
    hidden: true
  },
  {
    name: "Zenith",
    symbol: "ZENITH",
    decimals: 18,
    l2_token_address: "0x369439b0a871e4721fbdcb908315cd17b2d0746d38f3b393e64b3891d6a0859",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "SPOOKY",
    symbol: "SPOOKY",
    decimals: 18,
    l2_token_address: "0x1a587dc6d1c231ee4e83f5e67c314781f496e29015be6e6ac55ca03fc122ae9",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "TESTTEST",
    symbol: "TESTTEST",
    decimals: 18,
    l2_token_address: "0x4a9cf09bf7512dbcc92f9a8a9d3f673715b9fef221306f1ab094c0641cb2915",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "StarkShine",
    symbol: "SHINE",
    decimals: 18,
    l2_token_address: "0x1fd4b9ea3ebbdb9608dcc3a6b8c912195d05dd1b79dc7a52a7a5af3b0a924e5",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "Switch Protocol",
    symbol: "SWITCH",
    decimals: 18,
    l2_token_address: "0xa2be5a310e9b8606361f60632abce100e5c70a6902bb321d5382449089980f",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "PawCoin",
    symbol: "PAW",
    decimals: 18,
    l2_token_address: "0x181235ed5ef4c3f08ceefc24f7342fdde523ea7808f24e3e24b3a8f900aa16c",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Goldie Meme",
    symbol: "GOLDIE",
    decimals: 18,
    l2_token_address: "0x641653d6706493a9cb792d3de0cb781799dfff15e33ec91edd9d424ca65ab37",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "Moo Deng",
    symbol: "MOODENG",
    decimals: 18,
    l2_token_address: "0x72162ffc3a25bcea4ef58a4389a1581cdb8549cd30ff8e43af5f2209b402ff2",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "Pussy",
    symbol: "PSY",
    decimals: 18,
    l2_token_address: "0xb776458ae7de143d7ff0dd814a4f07fa8c00abc13f71e3b5a9417e680e8f17",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "MERRY PAWS",
    symbol: "MERRY",
    decimals: 18,
    l2_token_address: "0x280bba409dad0889d7fcf0605f5b6aa5a295b7d55216aae49b8d2f9ded890ae",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "CHILL SANTA",
    symbol: "CHILLSNT",
    decimals: 18,
    l2_token_address: "0x49bc2b34d0e0e484c041ec9dddd47657de66cc840b53bed8e09a62a617127d5",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Pips",
    symbol: "PIPS",
    decimals: 18,
    l2_token_address: "0x1479e68dd7d98028fb72fb4fd221ccd6426050c21edd116822570aba497c2a2",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "Stellar Gate",
    symbol: "STELLAR",
    decimals: 18,
    l2_token_address: "0x7cca4e03cc2c96a533f58ef1c42888571073008eb18c91bfa130567ff9fab30",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Pupsik",
    symbol: "PSP",
    decimals: 18,
    l2_token_address: "0x11f574e821fbf0c1e0c42cae9637808436659c3584f90f9316c029c51d5deef",
    sort_order: 1,
    total_supply: 77e12,
    hidden: true
  },
  {
    name: "Omakase",
    symbol: "OMAKASE",
    decimals: 18,
    l2_token_address: "0x50351d7f87862689065eaf066e738bad3e66267784e1d18fc0989df0185f074",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Zen Wealth",
    symbol: "ZWH",
    decimals: 18,
    l2_token_address: "0x48592a1297b7c6ae2fb29268240cbb3c6adb3f93df824e2d4fd42600df7299c",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "KARROT",
    symbol: "KARROT",
    decimals: 18,
    l2_token_address: "0x1651e61ed6448725d0313e56900baa8642a13aa5634b6007d6b6849947f38de",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "BROMANCE",
    symbol: "BROMANCE",
    decimals: 18,
    l2_token_address: "0x7e21157a75766dc34e1318291f400ddaeca20f81d973feaa06ef0d90716fe95",
    sort_order: 1,
    total_supply: 1e11,
    hidden: true
  },
  {
    name: "Art Peace",
    symbol: "ARTP",
    decimals: 18,
    l2_token_address: "0x4192fd8ac6fd58fa32ad4f9999bb08c1c8d7c3c49baef8dbcd2fcb190d0563e",
    sort_order: 1,
    total_supply: 1e15,
    hidden: true
  },
  {
    name: "StarkGrow",
    symbol: "SGR",
    decimals: 18,
    l2_token_address: "0x5020cf36c859edff865831303272cea117009b2e4af26f01180d5b25dbea1e9",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "AquaWave",
    symbol: "AWAVE",
    decimals: 18,
    l2_token_address: "0x47068d9b4a7b4f42dad88c999d4f6e9b01fa3ef5bc342b59f711f50a719ab94",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "StarkGrow",
    symbol: "STG",
    decimals: 18,
    l2_token_address: "0x31f6b39d7ad6d699d5dfaa352ba9ca33819cbad6a0b244892cde259aaa63f77",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "NITRO STARK",
    symbol: "NITRO",
    decimals: 18,
    l2_token_address: "0x5fc5cba0be9f48be5bc75887d587b8c354f10618db251dbff84576093bfd6ac",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "SS",
    symbol: "SS",
    decimals: 18,
    l2_token_address: "0x321d84867236278113021597783b5b6827d8b9d049453efd87ca9dbf2b501ef",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Gremlins",
    symbol: "GREM",
    decimals: 18,
    l2_token_address: "0x39cb78b822286a528fe2a22747605ebab151be7c6e1351d54fdd3577baf17fd",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "BITCOIN",
    symbol: "BTC",
    decimals: 18,
    l2_token_address: "0x3f819b95a46a0dbe9ce8e4bdd57148cad87c40250fa3ccd4c259959d54db687",
    sort_order: 1,
    total_supply: 21e9,
    hidden: true
  },
  {
    name: "JameSayla",
    symbol: "JAMES",
    decimals: 18,
    l2_token_address: "0x2d811ff2c10f6a2202c27614ae156e33fc4c3da17463f0cd91d064275a28fe1",
    sort_order: 1,
    total_supply: 1e8,
    hidden: true
  },
  {
    name: "JingleBell",
    symbol: "JINGLE",
    decimals: 18,
    l2_token_address: "0x485b3079584f327ef6db44432eaecdfd8e03d3f3550784b5c0eabc0d75b37a4",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Rudolph",
    symbol: "RUDOLPH",
    decimals: 18,
    l2_token_address: "0x7f6c9f91928609fa3ce3833e47cd183cba4bca398b0001fae0ba5502d37993b",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Chatbot AI Degen",
    symbol: "CAID",
    decimals: 18,
    l2_token_address: "0x76c55f7845e7e80bbb52c37c6f16045319e940d8969bceafef7e7b15d5bd7eb",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "BROTHER INU",
    symbol: "BROINU",
    decimals: 18,
    l2_token_address: "0x3114e502deddde2b258bafe00775af95ba25734e0eff06f17109da128b5003a",
    sort_order: 1,
    total_supply: 1e12,
    hidden: true
  },
  {
    name: "Hey Starknet",
    symbol: "Soon",
    decimals: 18,
    l2_token_address: "0xf93f5264e5a88f3f019ff4397db2f7ce03de78e83950de46ac284c401d2852",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "You Own Your Life",
    symbol: "YOYL",
    decimals: 18,
    l2_token_address: "0x7abcb26245ecc5a0cfe039b9f8df8a45ae160123f1ce67249fb6f193d2a777f",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Starknet AI Companion",
    symbol: "$STA",
    decimals: 18,
    l2_token_address: "0x20407bfaa1b21ec14901b1e763e7187fcb290595269c0a62fcb45f575bfa25d",
    sort_order: 1,
    total_supply: 1e13,
    hidden: true
  },
  {
    name: "TESTO",
    symbol: "TESTO",
    decimals: 18,
    l2_token_address: "0x117761602b4e7c34a0aaef99b8acfb07be9ce4f1044633148ef27078c17a1ef",
    sort_order: 1,
    total_supply: 1e10,
    hidden: true
  },
  {
    name: "Brother and Sister",
    symbol: "BROSIS",
    decimals: 18,
    l2_token_address: "0xd9bd20bcface330d5d1914c222a2f4173e3efc9da5cecfe080384a8c4bee14",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "TPTEST",
    symbol: "TPTEST",
    decimals: 18,
    l2_token_address: "0x3dde581763eae9fd2f2680c578bf1c9768a6b75b4d4d8ce2235f9ae011d2a24",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "TPTEST2",
    symbol: "TPTEST2",
    decimals: 18,
    l2_token_address: "0x1ba5d163c90528fb8ac268f5d2992fb7b1e5ebed5855e4e5eef2c1fe99aa35e",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "TPTEST3",
    symbol: "TPTEST3",
    decimals: 18,
    l2_token_address: "0x5c484251f16d8902217401e11a2329d4bdfe6e6c9ee89bfc1de915412a29b1a",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "GG",
    symbol: "GG",
    decimals: 18,
    l2_token_address: "0x3cb4518bfda6c05147c6d356c16a6b2de12bebe03733a7981201c19c0314a62",
    sort_order: 1,
    total_supply: 1e6,
    hidden: true
  },
  {
    name: "President Yoon",
    symbol: "YOON",
    decimals: 18,
    l2_token_address: "0x7fc77776c3a36bd8424a06775ecec53c546182136dc075f6afe03972e4d0dac",
    sort_order: 1,
    total_supply: 1e7,
    hidden: true
  },
  {
    name: "Brother Eli",
    symbol: "SLAY",
    decimals: 18,
    l2_token_address: "0x2ab526354a39e7f5d272f327fa94e757df3688188d4a92c6dc3623ab79894e2",
    sort_order: 1,
    total_supply: 88888888,
    hidden: true
  },
  {
    name: "Sister and Brother",
    symbol: "SISBRO",
    decimals: 18,
    l2_token_address: "0x2f5c2233c3704815bed135ea5e8410c6ce5113050196b7fefefc6f3a4930c85",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "FOMO",
    symbol: "FOMO",
    decimals: 18,
    l2_token_address: "0x58de27d29f7a831a221abbe47944c0f2c440f5b629b089cad96898c9a44f49",
    sort_order: 1,
    total_supply: 666666666666,
    hidden: true
  },
  {
    name: "BELLA",
    symbol: "BELLA",
    decimals: 18,
    l2_token_address: "0x768e2662a2ad079bd16dc86492bc70fa081e891708deef1d4ca5dee57b25a5a",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Stark Guy",
    symbol: "SGUY",
    decimals: 18,
    l2_token_address: "0x7159847725765f61b23a78b4ca0a42005cac115178c6ac35b3ae8508ca3fc",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "Wen4?",
    symbol: "W4",
    decimals: 18,
    l2_token_address: "0x2a4926924b58de26a337cddebd00e4f40a3e8eaced3cab046ea8700a71bc8fd",
    sort_order: 1,
    total_supply: 1e9,
    hidden: true
  },
  {
    name: "DUCK SANTA",
    symbol: "DUCKS",
    decimals: 18,
    l2_token_address: "0x567efb3fb3521e56f203e5829d62fa289cc235964eb29868698d0d23f6e0327",
    sort_order: 1,
    total_supply: 15e8,
    hidden: true
  },
  {
    name: "Lords",
    symbol: "LORDS",
    decimals: 18,
    l2_token_address: "0x0124aeb495b947201f5fac96fd1138e326ad86195b98df6dec9009158a533b49",
    sort_order: 1,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/coin.png"
  },
  {
    name: "Ancien Fragments",
    symbol: "ANCIENT_FRAGMENT",
    decimals: 18,
    l2_token_address: "0x0695b08ecdfdd828c2e6267da62f59e6d7543e690ef56a484df25c8566b332a5",
    sort_order: 2,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/29.png"
  },
  {
    name: "Knight",
    symbol: "KNIGHT",
    decimals: 18,
    l2_token_address: "0x00ac965f9e67164723c16735a9da8dbc9eb8e43b1bd0323591e87c056badf606",
    sort_order: 3,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/icons/250.png"
  },
  {
    name: "Crossbowman",
    symbol: "CROSSBOWMAN",
    decimals: 18,
    l2_token_address: "0x067e4ac00a241be06ba6afc11fa2715ec7da0c42c05a67ef6ecfcfeda725aaa8",
    sort_order: 4,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/icons/251.png"
  },
  {
    name: "Paladin",
    symbol: "PALADIN",
    decimals: 18,
    l2_token_address: "0x03bc86299bee061c7c8d7546ccb62b9daf9bffc653b1508facb722c6593874bc",
    sort_order: 5,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/icons/252.png"
  },
  {
    name: "Donkey",
    symbol: "DONKEY",
    decimals: 18,
    l2_token_address: "0x0264be95a4a2ace20add68cb321acdccd2f9f8440ee1c7abd85da44ddab01085",
    sort_order: 6,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/buildings/thumb/trade.png"
  },
  {
    name: "Wheat",
    symbol: "WHEAT",
    decimals: 18,
    l2_token_address: "0x057a3f1ee475e072ce3be41785c0e889b7295d7a0dcc22b992c5b9408dbeb280",
    sort_order: 7,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/254.png"
  },
  {
    name: "Fish",
    symbol: "FISH",
    decimals: 18,
    l2_token_address: "0x027719173cfe10f1aa38d2aaed0a075b6077290f1e817aa3485d2b828394f4d9",
    sort_order: 8,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/255.png"
  },
  {
    name: "Wood",
    symbol: "WOOD",
    decimals: 18,
    l2_token_address: "0x040d8907cec0f7ae9c364dfb12485a1314d84c129bf1898d2f3d4b7fcc7d44f4",
    sort_order: 9,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/1.png"
  },
  {
    name: "Stone",
    symbol: "STONE",
    decimals: 18,
    l2_token_address: "0x0439a1c010e3e1bb2d43d43411000893c0042bd88f6c701611a0ea914d426da4",
    sort_order: 10,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/2.png"
  },
  {
    name: "Coal",
    symbol: "COAL",
    decimals: 18,
    l2_token_address: "0x00ce635e3f241b0ae78c46a929d84a9101910188f9c4024eaa7559556503c31a",
    sort_order: 11,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/3.png"
  },
  {
    name: "Copper",
    symbol: "COPPER",
    decimals: 18,
    l2_token_address: "0x066ed5c928ee027a9419ace1cbea8389885161db5572a7c5c4fef2310e9bf494",
    sort_order: 12,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/4.png"
  },
  {
    name: "Obsidian",
    symbol: "OBSIDIAN",
    decimals: 18,
    l2_token_address: "0x03b6448d09dcd023507376402686261f5d6739455fa02f804907b066e488da66",
    sort_order: 13,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/5.png"
  },
  {
    name: "Silver",
    symbol: "SILVER",
    decimals: 18,
    l2_token_address: "0x06fe21d2d4a8a05bdb70f09c9250af9870020d5dcc35f410b4a39d6605c3e353",
    sort_order: 14,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/6.png"
  },
  {
    name: "Ironwood",
    symbol: "IRONWOOD",
    decimals: 18,
    l2_token_address: "0x01720cf6318bff45e62acc588680ae3cd4d5f8465b1d52cb710533c9299b031a",
    sort_order: 15,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/7.png"
  },
  {
    name: "Cold Iron",
    symbol: "COLDIRON",
    decimals: 18,
    l2_token_address: "0x0555d713e59d4ff96b7960447e9bc9e79bfdeab5b0eea74e3df81bce61cfbc77",
    sort_order: 16,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/8.png"
  },
  {
    name: "Gold",
    symbol: "GOLD",
    decimals: 18,
    l2_token_address: "0x000dff9dca192609c4e86ab3be22c7ec1e968876c992d21986f3c542be97fa2f",
    sort_order: 17,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/9.png"
  },
  {
    name: "Hartwood",
    symbol: "HARTWOOD",
    decimals: 18,
    l2_token_address: "0x05620aa7170cd66dbcbc37d03087bfe4633ffef91d3e4d97b501de906004f79b",
    sort_order: 18,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/10.png"
  },
  {
    name: "Diamonds",
    symbol: "DIAMONDS",
    decimals: 18,
    l2_token_address: "0x00e03ea8ae385f64754820af5c01c36abf1b8130dd6797d3fd9d430e4114e876",
    sort_order: 19,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/11.png"
  },
  {
    name: "Sapphire",
    symbol: "SAPPHIRE",
    decimals: 18,
    l2_token_address: "0x02f8dd022568af8f9f718aa37707a9b858529db56910633a160456838b6cbcbc",
    sort_order: 20,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/12.png"
  },
  {
    name: "Ruby",
    symbol: "RUBY",
    decimals: 18,
    l2_token_address: "0x03d9b66720959d0e7687b898292c10e62e78626f2dba5e1909961a2ce3f86612",
    sort_order: 21,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/13.png"
  },
  {
    name: "Deep Crystal",
    symbol: "DEEPCRYSTAL",
    decimals: 18,
    l2_token_address: "0x01d655ac834d38df7921074fc1588411e202b1af83307cbd996983aff52db3a8",
    sort_order: 22,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/14.png"
  },
  {
    name: "Ignium",
    symbol: "IGNIUM",
    decimals: 18,
    l2_token_address: "0x0625c1f789b03ebebc7a9322366f38ebad1f693b84b2abd8cb8f5b2748b0cdd5",
    sort_order: 23,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/15.png"
  },
  {
    name: "Ethereal Silica",
    symbol: "ETHEREALSILICA",
    decimals: 18,
    l2_token_address: "0x068b6e23cbbd58a644700f55e96c83580921e9f521b6e5175396b53ba7910e7d",
    sort_order: 24,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/16.png"
  },
  {
    name: "True Ice",
    symbol: "TRUEICE",
    decimals: 18,
    l2_token_address: "0x04485f5a6e16562e1c761cd348e63256d00389e3ddf4f5d98afe7ab44c57c481",
    sort_order: 25,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/17.png"
  },
  {
    name: "Twilight Quartz",
    symbol: "TWILIGHTQUARTZ",
    decimals: 18,
    l2_token_address: "0x035e24c02409c3cfe8d5646399a62c4d102bb782938d5f5180e92c9c62d3faf7",
    sort_order: 26,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/18.png"
  },
  {
    name: "Alchemical Silver",
    symbol: "ALCHEMICALSILVER",
    decimals: 18,
    l2_token_address: "0x03956a5301e99522038a2e7dcb9c2a89bf087ffa79310ee0a508b5538efd8ddd",
    sort_order: 27,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/19.png"
  },
  {
    name: "Adamantine",
    symbol: "ADAMANTINE",
    decimals: 18,
    l2_token_address: "0x0367f838f85a2f5e1580d6f011e4476f581083314cff8721ba3dda9706076eed",
    sort_order: 28,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/20.png"
  },
  {
    name: "Mithral",
    symbol: "MITHRAL",
    decimals: 18,
    l2_token_address: "0x0067ba235c569c23877064b2ac6ebd4d79f32d3c00f5fab8e28a3b5700b957f6",
    sort_order: 29,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/21.png"
  },
  {
    name: "Dragon Hide",
    symbol: "DRAGONHIDE",
    decimals: 18,
    l2_token_address: "0x03bf856515bece3c93f5061b7941b8645f817a0acab93c758b8c7b4bc0afa3c6",
    sort_order: 30,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/22.png"
  },
  {
    name: "Demon Hide",
    symbol: "DEMONHIDE",
    decimals: 18,
    l2_token_address: "0x03b847fbef9d344ba968edc945100e6ab0258a4b1b91f8665f85a3d814c3e4d4",
    sort_order: 31,
    total_supply: null,
    hidden: true,
    logo_url: "https://empire.realms.world/images/resources/22.png"
  }
];
var controllerConfigs = configs;
var erc20Metadata = metadata;
var defaultTheme = configs["cartridge"].theme;
export {
  NotReadyToConnect,
  ResponseCodes,
  controllerConfigs,
  ControllerProvider as default,
  defaultTheme,
  erc20Metadata,
  lookupAddresses,
  lookupUsernames,
  toArray,
  toSessionPolicies,
  toWasmPolicies
};
//# sourceMappingURL=index.js.map